/* eslint-disable unicode-bom */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useSessionTimeout from '../hooks/useSessionTimeout';
import { useLanguage } from '../context/LanguageContext';
import TransactionLedger from '../components/admin/TransactionLedger';
import BankTopupApprovals from '../components/admin/BankTopupApprovals';
import MachineRegistrationApprovals from '../components/admin/MachineRegistrationApprovals';
import QuotationExceptionsApprovals from '../components/admin/QuotationExceptionsApprovals';
import ContractTerminationApprovals from '../components/admin/ContractTerminationApprovals';
import OwnerDefaultApprovals from '../components/admin/OwnerDefaultApprovals';
import WithdrawApprovals from '../components/admin/WithdrawApprovals';
import PhoneChangeApprovalPanel from '../components/PhoneChangeApprovalPanel';
import MachineCompliancePanel from '../components/MachineCompliancePanel';
import AdminClientKycPanel from '../components/AdminClientKycPanel';
import AdminOwnerKycPanel from '../components/AdminOwnerKycPanel';
import AdminOperatorKycPanel from '../components/AdminOperatorKycPanel';
import MachineServicePanel from '../components/MachineServicePanel';
import AdminSessionPanel from '../components/AdminSessionPanel';
import AdminTotpSetupPanel from '../components/AdminTotpSetupPanel';
import DualApprovals from '../components/admin/DualApprovals';
import TreasuryWalletPanel from '../components/admin/TreasuryWalletPanel';
import PendingSettlements from '../components/admin/PendingSettlements';
import ClientRefundForm from '../components/admin/ClientRefundForm';
import OwnerPayoutForm from '../components/admin/OwnerPayoutForm';
import { fetchAdminBankTopups } from '../services/walletApi';
import { buildClientLookup, buildBookingRefLookup, inferWalletTxnChannel, inferWalletTxnStatus } from '../utils/ledger/mapWalletTransactions';
import { generateServiceAgreement } from '../utils/ledger/generateServiceAgreement';
import { useWindowSize } from '../hooks/useWindowSize';
import { useDeviceLayout } from '../hooks/useDeviceLayout';
import { logoutAndRedirect } from '../utils/authLogout';
import { getMachinesPage, getAllBookingsPage, getAllUsersPage, getAllTransactionsPage, getAllIssuesPage, getPendingUsers, approveUser, rejectUser, allowUserResubmission, deactivateUser, reactivateUser, blacklistClient, unblacklistClient, getUserKycDocumentUrl, getDlqItems, retryDlqItem, getDlqStats, getAuditLogs, getRateLimitTelemetry, getApiHealthTelemetry, acknowledgeSecuritySignal, getSurveySurveyors, getSurveyResponses, secureFetch } from '../supabaseService';
import { appendUniqueById } from '../utils/pagination';
import { formatClientType, clientTypeTier, formatOwnerEntityType } from '../utils/clientTypeUtils';
import { buildMachineLookup, getBookingMachineCode } from '../utils/bookingDisplay';
import { formatHoursHm } from '../utils/durationHours';
import BookingFeedbackSummary from '../components/BookingFeedbackSummary';
import { AdminDisputePanel, AdminForceSettlePanel, AdminForceCancelPanel, AdminStartMachineButton } from '../components/booking/AdminBookingV2Tools';
import BookingFlowV2Card from '../components/booking/BookingFlowV2Card';
import ContractBookingStatus from '../components/booking/ContractBookingStatus';
import PushNotificationToggle from '../components/notifications/PushNotificationToggle';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import SurveyResponsesTab from '../components/SurveyResponsesTab';
import SurveyReviewTab from '../components/SurveyReviewTab';
import SurveyPayoutTab from '../components/SurveyPayoutTab';
import SurveyQrGeneratorTab from '../components/SurveyQrGeneratorTab';
import AdminCommandShell from '../components/admin/AdminCommandShell';
import AdminOverviewCharts from '../components/admin/AdminOverviewCharts';
import AdminFleetAnalytics from '../components/AdminFleetAnalytics';
import AdminMachineFinancialPanel from '../components/AdminMachineFinancialPanel';
import AdminMarketRatePanel from '../components/AdminMarketRatePanel';
import AdminPayrollOverview from '../components/AdminPayrollOverview';
import AdminNotificationLog from '../components/AdminNotificationLog';
import BentoOverview from '../components/admin/BentoOverview';
import OverviewRollPanel from '../components/admin/OverviewRollPanel';
import OverviewUnifiedFilters from '../components/admin/OverviewUnifiedFilters';
import AuditDatetimePicker from '../components/admin/AuditDatetimePicker';
import { NAV_ICONS } from '../components/admin/adminNavIcons';
import { exportAuditLogsCsv } from '../utils/csvExport';
import { exportSecuritySignalsReport, getSecuritySignalSeverity } from '../utils/securitySignalsExport';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const inferTxnStatus = inferWalletTxnStatus;

const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** datetime-local input: local YYYY-MM-DDTHH:mm (never UTC slice) */
const toDatetimeLocalValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
};

const datetimeLocalToIso = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
};

const formatAuditDatetimeLabel = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toDateInputValue(start), to: toDateInputValue(now) };
};

const getLastDaysRange = (days) => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - Math.max(0, days - 1));
  return { from: toDateInputValue(start), to: toDateInputValue(now) };
};

const friendlySurveyError = (err, translate) => {
  if (!err) return null;
  if (err.includes('auth_required') || err.includes('auth')) {
    return translate('sessionExpiredRefresh');
  }
  return err;
};

const parseBookingDate = (b) => {
  const raw = b?.created_at || b?.start_date;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const getSecuritySignalKey = (row) => String(row?.id ?? row?.metadata?.signalId ?? '');

const buildAcknowledgedSignalMap = (ackItems = []) => {
  const ackMap = {};
  ackItems.forEach((x) => {
    const sid = x?.metadata?.signalId ?? x?.entity_id;
    if (sid != null && sid !== '') {
      ackMap[String(sid)] = {
        acknowledged: true,
        actorId: x.actor_id || '-',
        acknowledgedAt: x.created_at || null,
      };
    }
  });
  return ackMap;
};

const isTxnInDateRange = (tx, from, to) => {
  if (!from && !to) return true;
  const raw = tx?.created_at || tx?.date;
  if (!raw) return false;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  const day = toDateInputValue(d);
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

const formatRsCompact = (amount) => {
  const n = Number(amount) || 0;
  if (n <= 0) return '₹0';
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${Math.round(n / 1000)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const NAV = [
  { id: 'overview', icon: String.fromCodePoint(0x1F4CA), label: 'Overview', i18nKey: 'overview' },
  { id: 'machines', icon: String.fromCodePoint(0x1F69C), label: 'Machines', i18nKey: 'machines' },
  { id: 'bookings', icon: String.fromCodePoint(0x1F4CB), label: 'Bookings', i18nKey: 'bookingsTab' },
  { id: 'clients', icon: String.fromCodePoint(0x1F477), label: 'Clients', i18nKey: 'clients' },
  { id: 'owners', icon: String.fromCodePoint(0x1F3D7), label: 'Owners', i18nKey: 'owners' },
  { id: 'operators', icon: String.fromCodePoint(0x1F527), label: 'Operators', i18nKey: 'operators' },
  { id: 'wallet', icon: String.fromCodePoint(0x1F4B3), label: 'Wallet & Billing', i18nKey: 'billingWallet' },
  { id: 'approvals', icon: String.fromCodePoint(0x23F3), label: 'Approvals', i18nKey: 'approvals' },
  { id: 'surveyQr', icon: String.fromCodePoint(0x1F465), label: 'Freelancers', i18nKey: 'surveyQr' },
  { id: 'dlq', icon: String.fromCodePoint(0x1F9EF), label: 'DLQ Monitor', i18nKey: 'dlqMonitor' },
  { id: 'audit', icon: String.fromCodePoint(0x1F4DC), label: 'Audit Logs', i18nKey: 'auditLogs' },
  { id: 'reports', icon: String.fromCodePoint(0x1F4C8), label: 'Reports', i18nKey: 'reports' },
  { id: 'analytics', icon: String.fromCodePoint(0x1F4CA), label: 'Analytics', i18nKey: 'adminAnalytics' },
  { id: 'surveys', icon: String.fromCodePoint(0x1F4DD), label: 'Survey Responses', i18nKey: 'surveyResponses' },
  { id: 'surveyReview', icon: String.fromCodePoint(0x2705), label: 'Survey Review', i18nKey: 'surveyReview' },
  { id: 'surveyPayout', icon: String.fromCodePoint(0x1F4B0), label: 'Payout Summary', i18nKey: 'surveyPayout' },
  { id: 'iot', icon: String.fromCodePoint(0x1F4E1), label: 'IoT & GPS', i18nKey: 'iot' },
  { id: 'grievances', icon: String.fromCodePoint(0x1F4AC), label: 'Grievances' },
];

const MANAGER_HIDDEN_NAV_IDS = new Set(['wallet', 'reports', 'surveyPayout', 'analytics']);

const ADMIN_TAB_ORDER = NAV.map((n) => n.id);

function rawUserPhone(user) {
  if (!user) return '';
  let v = String(user.phone ?? user.mobile ?? user.mobile_number ?? '').trim();
  // Legacy duplicate rows: phone stored as {phone}-dup-{userIdSuffix}
  v = v.replace(/-dup-[a-f0-9]+$/i, '');
  const tenDigit = v.match(/^[6-9]\d{9}/);
  if (tenDigit) return tenDigit[0];
  return v;
}

function displayUserPhone(user) {
  const raw = rawUserPhone(user);
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return raw;
}

function userPhoneHref(user) {
  const digits = rawUserPhone(user).replace(/\D/g, '');
  if (digits.length === 10) return `tel:+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `tel:+${digits}`;
  if (digits.length >= 10) return `tel:+${digits}`;
  return null;
}

const ui = {
  container: 'admin-premium relative flex min-h-screen overflow-x-hidden bg-[#030810] font-sans text-white',
  ambientGold: 'pointer-events-none fixed -left-24 top-0 z-0 h-[28rem] w-[28rem] rounded-full bg-de-gold/12 blur-[120px]',
  ambientBlue: 'pointer-events-none fixed -right-20 top-1/3 z-0 h-80 w-80 rounded-full bg-sky-500/10 blur-[100px]',
  ambientRose: 'pointer-events-none fixed bottom-0 left-1/3 z-0 h-72 w-72 rounded-full bg-de-accent/8 blur-[90px]',
  sidebar: 'relative z-10 hidden w-60 shrink-0 flex-col gap-2 border-r border-de-gold/20 bg-gradient-to-b from-[#0c1a30]/95 to-[#060e1c]/98 p-5 shadow-2xl shadow-black/50 backdrop-blur-xl lg:flex',
  sidebarLogo: 'mb-2 flex items-center gap-2.5',
  logoCircle: 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-de-logo text-xs font-black text-de-panel',
  logoTitle: 'm-0 text-sm font-bold text-[#C9A84C]',
  logoSub: 'm-0 text-[10px] text-de-muted',
  divider: 'my-2 h-px bg-de-gold/15',
  nav: 'flex w-full min-h-11 cursor-pointer items-center gap-2.5 rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5 text-left text-sm text-de-muted transition-all duration-200 hover:border-de-gold/25 hover:bg-de-gold/5 hover:text-slate-200',
  navActive: 'flex w-full min-h-11 cursor-pointer items-center gap-2.5 rounded-xl border border-de-gold/50 bg-gradient-to-r from-de-gold/25 via-de-gold/10 to-transparent px-3 py-2.5 text-left text-sm font-bold text-de-gold de-glow-gold ring-1 ring-de-gold/30',
  navIconWrap: 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-slate-500/30 bg-de-panel/60',
  navIconWrapActive: 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-de-gold/40 bg-de-gold/20 shadow-sm shadow-de-gold/20',
  navLabel: 'flex-1',
  navChevron: 'text-lg leading-none text-slate-500',
  navChevronActive: 'text-lg leading-none text-[#C9A84C]',
  logoutBtn: 'mt-auto flex w-full min-h-10 cursor-pointer items-center gap-2.5 rounded-lg border border-de-accent/30 bg-de-accent/10 px-3 py-2.5 text-sm text-de-accent',
  sidebarFooter: 'mt-2 text-center text-[9px] tracking-wide text-de-gold/40',
  main: 'relative z-10 min-w-0 flex-1 overflow-y-auto overflow-x-hidden',
  hero: 'mb-6 overflow-hidden rounded-2xl border border-de-gold/25 bg-gradient-to-br from-[#122a4a]/90 via-[#0a1628]/95 to-[#060e1c]/98 p-4 de-glow-gold sm:p-6',
  header: 'mb-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
  pageTitle: 'm-0 mb-1 text-xl font-extrabold tracking-tight de-text-gradient sm:text-2xl',
  pageDate: 'm-0 text-xs text-de-muted',
  adminBadge: 'inline-flex min-h-10 items-center gap-2 rounded-full border border-de-gold/30 bg-de-gold/10 px-4 text-sm font-semibold text-[#C9A84C]',
  adminDot: 'inline-block h-2 w-2 rounded-full bg-emerald-500',
  cardRow: 'mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6',
  statPremium: 'group relative overflow-hidden rounded-2xl border border-de-gold/30 bg-gradient-to-br from-[#142d4f]/80 via-[#0c1a30]/90 to-[#060e1c]/95 p-4 shadow-xl transition duration-300 hover:-translate-y-0.5 hover:border-de-gold/50 hover:shadow-de-gold/20 sm:p-5',
  statGlow: 'pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-de-gold/15 blur-2xl transition group-hover:bg-de-gold/25',
  statIconWrap: 'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-de-gold/30 bg-gradient-to-br from-de-gold/25 to-de-gold/5 text-2xl shadow-inner',
  statNumber: 'mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl',
  statLabel: 'mt-1 text-xs font-medium uppercase tracking-wider text-de-muted',
  statChange: 'mt-2 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold',
  card: 'rounded-xl border border-de-gold/25 bg-de-panel p-4 text-center shadow-lg sm:p-5',
  cardIcon: 'mb-2 text-2xl',
  cardNumber: 'm-0 mb-1 text-xl font-bold text-[#C9A84C] sm:text-2xl',
  cardLabel: 'm-0 mb-1.5 text-[11px] text-de-muted',
  cardChange: 'm-0 text-[10px]',
  tableCard: 'mb-6 w-full min-w-0 rounded-2xl border border-de-gold/25 bg-gradient-to-br from-[#122a4a]/70 via-[#0a1628]/90 to-[#060e1c]/95 p-4 de-glow-gold sm:p-5',
  tableTitle: 'mb-4 text-base font-semibold text-[#C9A84C]',
  liveDot: 'inline-block h-2 w-2 rounded-full bg-emerald-500',
  tableWrap: 'hidden w-full min-w-0 overflow-x-auto md:block',
  tableMobile: 'flex w-full min-w-0 flex-col gap-3 md:hidden',
  table: 'w-full min-w-0 border-collapse text-sm',
  th: 'whitespace-nowrap border-b border-de-gold/15 px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-de-gold/70',
  tr: 'border-b border-white/5',
  td: 'px-3 py-3 text-sm text-slate-200 md:whitespace-nowrap',
  machineTag: 'inline-flex rounded-full border border-de-gold/30 bg-de-gold/10 px-2.5 py-0.5 text-xs font-semibold text-[#C9A84C]',
  fuelBar: 'mb-1 h-1 w-full max-w-[5rem] rounded bg-white/10',
  fuelFill: 'h-full rounded',
  statusBadge: 'inline-flex rounded-full px-3 py-1 text-xs font-semibold',
  bottomRow: 'grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3',
  bottomCard: 'w-full min-w-0 rounded-xl border border-de-gold/25 bg-de-panel p-4 shadow-lg sm:p-5',
  bottomTitle: 'mb-4 text-[15px] font-semibold text-[#C9A84C]',
  revenueRow: 'mb-3 flex items-center gap-2.5',
  revenueLabel: 'w-24 shrink-0 text-xs text-de-muted',
  revenueBarBg: 'h-1.5 flex-1 rounded bg-white/5',
  revenueBarFill: 'h-full rounded bg-gradient-to-r from-amber-700 to-de-gold',
  revenueValue: 'w-10 shrink-0 text-right text-xs font-bold text-[#C9A84C]',
  alertRow: 'mb-2 rounded-lg bg-white/[0.03] px-3 py-2.5',
  alertMsg: 'mb-0.5 text-xs text-slate-200',
  alertTime: 'm-0 text-[10px] text-de-muted',
  downloadBtn: 'min-h-10 rounded-lg border border-de-gold/50 bg-gradient-to-br from-amber-300/20 to-de-panel/90 px-4 text-sm font-bold text-[#C9A84C] shadow-md transition hover:brightness-110',
  btnLoadMore: 'min-h-10 w-full border-de-gold/35 bg-de-gold/15 text-de-gold hover:bg-de-gold/20 sm:w-auto',
  hint: 'mt-2 text-center text-[11px] text-de-muted',
  empty: 'py-8 text-center text-sm text-de-muted',
  mobileCard: 'rounded-xl border border-de-gold/20 bg-gradient-to-br from-de-panel to-[#060e1c] p-4 shadow-md',
  mobileGrid2: 'grid grid-cols-2 gap-2',
  mobileField: 'rounded-lg border border-white/5 bg-black/25 px-3 py-2',
  mobileLabel: 'text-[10px] uppercase tracking-wide text-de-muted',
  mobileValue: 'mt-0.5 break-words text-sm font-medium text-slate-200',
  btnApprove: 'min-h-10 border-emerald-500/50 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
  btnReject: 'min-h-10 border-de-accent/50 bg-de-accent/10 text-de-accent hover:bg-de-accent/20',
  filterChip: 'min-h-10 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 text-[10px] font-semibold text-sky-300',
  filterChipOn: 'min-h-10 rounded-full border border-sky-400/50 bg-sky-400/20 px-3 text-[10px] font-semibold text-sky-200',
  accordion: 'mb-3 overflow-hidden rounded-xl border border-de-gold/20',
  accordionHead: 'flex w-full min-h-10 cursor-pointer items-center justify-between gap-2 bg-de-panel px-4 py-3.5',
  accordionBody: 'p-2.5',
  statMini: 'rounded-lg border border-de-gold/15 bg-black/30 px-2.5 py-2.5 text-center',
  statMiniLabel: 'mb-0.5 text-[10px] text-de-muted',
  statMiniValue: 'm-0 text-sm font-bold text-de-gold',
  msgBanner: 'mb-2.5 rounded-lg px-2.5 py-2 text-xs',
  msgBannerOk: 'border border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
  msgBannerErr: 'border border-de-accent/45 bg-de-accent/15 text-de-accent',
  backBtn: 'mb-1.5 flex w-fit min-h-10 items-center gap-1.5 rounded-full border border-de-gold/20 bg-de-gold/10 px-3 py-1.5 text-xs font-semibold text-de-gold',
};

function MobileField({ label, value, className, href }) {
  return (
    <div className={cn(ui.mobileField, className)}>
      <p className={ui.mobileLabel}>{label}</p>
      {href ? (
        <a href={href} className={cn(ui.mobileValue, 'text-de-gold hover:underline')}>
          {value}
        </a>
      ) : (
        <p className={ui.mobileValue}>{value}</p>
      )}
    </div>
  );
}

function MobilePhoneField({ user, className }) {
  return (
    <MobileField
      label="Mobile"
      value={displayUserPhone(user)}
      href={userPhoneHref(user)}
      className={className}
    />
  );
}

function PhoneTableCell({ user }) {
  const display = displayUserPhone(user);
  const href = userPhoneHref(user);
  return (
    <TableCell className="min-w-[6.5rem] whitespace-normal break-words">
      {href ? (
        <a href={href} className="font-medium text-de-gold hover:underline">
          {display}
        </a>
      ) : (
        display
      )}
    </TableCell>
  );
}

function formatLegalNoticeAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN')}`;
}

function formatLegalNoticeDaysOverdue(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return '0';
  return String(Math.floor(n));
}

function LegalNoticeApprovals({ onQueueChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/admin/legal-notices');
      if (data?.success === false) throw new Error(data.message || 'Failed to load legal notices');
      const rows = Array.isArray(data?.items) ? data.items : [];
      setItems(rows);
      onQueueChange?.(rows.length);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setError(err?.message || 'Failed to load legal notices');
      setItems([]);
      onQueueChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSendNotice = async (row) => {
    if (busyId || !row?.id) return;
    setBusyId(row.id);
    setError('');
    try {
      const result = await secureFetch(`/api/admin/legal-notices/${row.id}/send`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      if (result?.success === false) {
        throw new Error(result.message || result.error || 'Failed to send legal notice');
      }
      await load();
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setError(err?.message || 'Failed to send legal notice');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {loading && <p className="m-0 text-[12px] text-slate-400">Loading legal notices…</p>}
      {error && <p className="mb-2 text-[12px] text-red-400">{error}</p>}
      {!loading && items.length === 0 && (
        <p className="m-0 text-[12px] text-slate-500">No pending legal notices</p>
      )}
      {items.length > 0 && (
        <>
          <div className={ui.tableWrap}>
            <Table>
              <TableHeader>
                <TableRow>
                  {['Booking Ref', 'Client Name', 'Phone', 'Amount Due', 'Days Overdue', 'Action'].map((h) => (
                    <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-semibold text-[#C9A84C]">{row.booking_ref || '—'}</TableCell>
                    <TableCell>{row.client_name || '—'}</TableCell>
                    <PhoneTableCell user={{ phone: row.client_phone }} />
                    <TableCell>{formatLegalNoticeAmount(row.amount_due)}</TableCell>
                    <TableCell>{formatLegalNoticeDaysOverdue(row.days_overdue)}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        disabled={busyId === row.id}
                        onClick={() => handleSendNotice(row)}
                        className="rounded border border-[#C9A84C]/45 bg-[#C9A84C]/15 px-3 py-2 text-[11px] font-semibold text-[#C9A84C] disabled:opacity-50"
                      >
                        {busyId === row.id ? 'Sending…' : 'Send Notice'}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className={ui.tableMobile}>
            {items.map((row) => (
              <Card key={row.id} className={ui.mobileCard}>
                <CardHeader className="flex-row items-start justify-between space-y-0 p-4 pb-2">
                  <CardTitle className="text-sm text-[#C9A84C]">{row.booking_ref || '—'}</CardTitle>
                </CardHeader>
                <CardContent className={ui.mobileGrid2}>
                  <MobileField label="Client Name" value={row.client_name || '—'} />
                  <MobilePhoneField user={{ phone: row.client_phone }} />
                  <MobileField label="Amount Due" value={formatLegalNoticeAmount(row.amount_due)} />
                  <MobileField label="Days Overdue" value={formatLegalNoticeDaysOverdue(row.days_overdue)} />
                </CardContent>
                <div className="px-4 pb-4">
                  <button
                    type="button"
                    disabled={busyId === row.id}
                    onClick={() => handleSendNotice(row)}
                    className="w-full rounded border border-[#C9A84C]/45 bg-[#C9A84C]/15 px-3 py-2 text-[11px] font-semibold text-[#C9A84C] disabled:opacity-50"
                  >
                    {busyId === row.id ? 'Sending…' : 'Send Notice'}
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LoadMoreButton({ onClick, disabled, loading, label, loadingLabel }) {
  return (
    <div className="mt-3 flex justify-center">
      <Button type="button" variant="outline" className={ui.btnLoadMore} onClick={onClick} disabled={disabled}>
        {loading ? loadingLabel : label}
      </Button>
    </div>
  );
}

function TablePanel({ title, desktop, mobile, footer, showTitle = false, preferCards = false }) {
  return (
    <Card className="mb-6 w-full min-w-0 rounded-2xl border-de-gold/30 bg-gradient-to-br from-[#122a4a]/60 via-[#0a1628]/95 to-[#060e1c] de-glow-gold shadow-2xl">
      {showTitle && title ? (
        <CardHeader className="p-4 pb-2 sm:p-5">
          <CardTitle className="text-base font-semibold text-[#C9A84C]">{title}</CardTitle>
        </CardHeader>
      ) : null}
      <CardContent className={cn('min-w-0 space-y-3 p-4 sm:p-5', showTitle && title ? 'pt-0 sm:pt-0' : '')}>
        <div className={cn(ui.tableWrap, preferCards && '!hidden')}>{desktop}</div>
        {mobile ? (
          <div className={cn(preferCards ? 'flex w-full min-w-0 flex-col gap-3' : ui.tableMobile)}>
            {mobile}
          </div>
        ) : null}
        {footer ? <p className={ui.hint}>{footer}</p> : null}
      </CardContent>
    </Card>
  );
}

function userStatusBadge(status, translate) {
  if (status === 'active') return <Badge variant="success">{translate('statusActive')}</Badge>;
  if (status === 'pending') return <Badge variant="warning">{translate('statusPending')}</Badge>;
  if (status === 'inactive') return <Badge variant="secondary">{translate('statusInactive')}</Badge>;
  return <Badge variant="danger">{translate('statusRejected')}</Badge>;
}

function getOperatorKycDocs(operator, t) {
  if (!operator) return [];
  const docs = [];
  if (operator.license_document_path) docs.push({ type: 'license', label: t('kycDocLicense') });
  if (operator.aadhaar_front_path) docs.push({ type: 'aadhaar_front', label: t('kycDocAadhaarFront') });
  if (operator.aadhaar_back_path) docs.push({ type: 'aadhaar_back', label: t('kycDocAadhaarBack') });
  if (operator.police_verification_path) docs.push({ type: 'police', label: t('kycDocPoliceVerification') });
  return docs;
}

function formatSyntheticEmailDisplay(email) {
  const value = (email || '').toString().trim();
  const lower = value.toLowerCase();
  if (
    lower.endsWith('@operator.developmentexpress.local')
    || lower.endsWith('@register.developmentexpress.local')
  ) {
    return '—';
  }
  return value || '—';
}

function formatOperatorEmailDisplay(email) {
  return formatSyntheticEmailDisplay(email);
}

function formatPendingUserPanStatus(u) {
  if (u.pan_verified || u.pan_verification_status === 'verified') {
    return {
      label: `✅ PAN Verified${u.pan_name ? ` — ${u.pan_name}` : ''}`,
      color: '#4CAF50',
    };
  }
  if (u.pan_verification_status === 'failed') {
    return { label: '❌ PAN Verification Failed', color: '#e94560' };
  }
  if (u.pan_number || u.pan_document_path || u.pan_verification_status === 'not_verified') {
    return { label: '⏳ PAN Not Yet Verified', color: '#FF9800' };
  }
  return null;
}

function StatMini({ label, value }) {
  return (
    <div className={ui.statMini}>
      <p className={ui.statMiniLabel}>{label}</p>
      <p className={ui.statMiniValue}>{value}</p>
    </div>
  );
}

function PremiumStatCard({ icon, value, label, change, up }) {
  return (
    <div className={ui.statPremium}>
      <span className={ui.statGlow} aria-hidden />
      <div className="relative flex items-start justify-between gap-2">
        <div className={ui.statIconWrap}>{icon}</div>
        {change ? (
          <span className={cn(ui.statChange, up ? 'bg-emerald-500/15 text-emerald-400' : 'bg-de-accent/15 text-de-accent')}>
            {up ? '↑' : '↓'} {change}
          </span>
        ) : null}
      </div>
      <p className={ui.statNumber}>{value}</p>
      <p className={ui.statLabel}>{label}</p>
    </div>
  );
}

function AdminMilestoneBillPanel({ booking }) {
  const [billsCount, setBillsCount] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [successMsg, setSuccessMsg] = React.useState('');
  const [completedUnits, setCompletedUnits] = React.useState(
    Number(booking?.total_units_completed || 0),
  );

  const loadBills = React.useCallback(async () => {
    if (!booking?.id || booking.booking_type !== 'milestone') return;
    try {
      const data = await secureFetch(
        `/api/admin/bookings/${encodeURIComponent(booking.id)}/milestone/bills`,
      );
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load milestone bills');
      }
      setBillsCount(Array.isArray(data?.items) ? data.items.length : 0);
    } catch (_err) {
      setBillsCount(null);
    }
  }, [booking?.id, booking?.booking_type]);

  React.useEffect(() => {
    setCompletedUnits(Number(booking?.total_units_completed || 0));
    loadBills();
  }, [booking?.total_units_completed, loadBills]);

  if (!booking || booking.booking_type !== 'milestone') return null;

  const unit = booking.booking_unit_label || 'units';
  const estimated = Number(booking.total_units_estimated || 0);
  const rate = Number(booking.rate_per_unit || 0);

  const handleGenerate = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setSuccessMsg('');
    try {
      const result = await secureFetch(
        `/api/admin/bookings/${encodeURIComponent(booking.id)}/milestone/generate-bill`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to generate bill');
      }
      const amount = Number(result?.bill?.gross_amount || 0);
      setSuccessMsg(`Bill #${result?.bill?.milestone_number || '—'} — ₹${amount.toLocaleString('en-IN')}`);
      setBillsCount((prev) => (prev == null ? 1 : prev + 1));
    } catch (err) {
      setError(err?.message || 'Failed to generate bill');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-de-gold/25 bg-black/25 px-3 py-3 text-xs text-[#e8e0d0]">
      <p className="mb-2 font-extrabold text-[#C9A84C]">Milestone / unit-rate</p>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-black/30 px-2 py-2">
          <p className="mb-1 text-[9px] text-de-muted">Progress</p>
          <p className="m-0 font-bold">
            {completedUnits.toLocaleString('en-IN')} / {estimated.toLocaleString('en-IN')} {unit}
          </p>
        </div>
        <div className="rounded-lg bg-black/30 px-2 py-2">
          <p className="mb-1 text-[9px] text-de-muted">Rate / unit</p>
          <p className="m-0 font-bold">₹{rate.toLocaleString('en-IN')}</p>
        </div>
        <div className="col-span-2 rounded-lg bg-black/30 px-2 py-2">
          <p className="mb-1 text-[9px] text-de-muted">Milestone bills generated</p>
          <p className="m-0 font-bold">{billsCount == null ? '—' : billsCount}</p>
        </div>
      </div>
      {error && <p className="mb-2 text-[11px] font-semibold text-[#e94560]">{error}</p>}
      <button
        type="button"
        disabled={busy}
        onClick={handleGenerate}
        className="w-full rounded-lg border-0 px-3 py-2.5 text-xs font-extrabold text-[#030810]"
        style={{
          background: 'linear-gradient(135deg, #a07830, #e2c97e)',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? 'Generating…' : 'Generate Milestone Bill'}
      </button>
      {successMsg && (
        <div className="mt-2 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-center text-xs font-bold text-emerald-400">
          {successMsg}
        </div>
      )}
    </div>
  );
}

function AdminSiteWorkCompletePanel({ booking, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [successMsg, setSuccessMsg] = React.useState('');

  if (!booking?.site_work_type) return null;

  const depthDone = Number(booking.total_depth_completed || 0);
  const depthEst = Number(booking.estimated_depth || 0);
  const unit = booking.billing_basis === 'per_meter' ? 'm' : 'ft';
  const amount = Number(
    booking.confirmed_strata_amount
      ?? (Array.isArray(booking.strata_logs)
        ? booking.strata_logs
          .filter((l) => l.client_confirmed || l.auto_confirmed_at)
          .reduce((sum, l) => sum + Number(l.amount_due || 0), 0)
        : 0),
  );

  const handleComplete = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    setSuccessMsg('');
    try {
      const result = await secureFetch(
        `/api/admin/site-work/${encodeURIComponent(booking.id)}/complete`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to complete site work');
      }
      setSuccessMsg(
        `Completed — gross ₹${Number(result.gross || 0).toLocaleString('en-IN')}, owner ₹${Number(result.netOwner || 0).toLocaleString('en-IN')}`,
      );
      if (typeof onDone === 'function') onDone();
    } catch (err) {
      setError(err?.message || 'Failed to complete site work');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-de-gold/25 bg-black/25 px-3 py-3 text-xs text-[#e8e0d0]">
      <p className="mb-2 font-extrabold text-[#C9A84C]">
        Site Work — {String(booking.site_work_type).replace(/_/g, ' ')}
      </p>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-black/30 px-2 py-2">
          <p className="mb-1 text-[9px] text-de-muted">Depth</p>
          <p className="m-0 font-bold">
            {depthDone.toLocaleString('en-IN')} / {depthEst.toLocaleString('en-IN')} {unit}
          </p>
        </div>
        <div className="rounded-lg bg-black/30 px-2 py-2">
          <p className="mb-1 text-[9px] text-de-muted">Confirmed amount</p>
          <p className="m-0 font-bold">₹{amount.toLocaleString('en-IN')}</p>
        </div>
      </div>
      {error && <p className="mb-2 text-[11px] font-semibold text-[#e94560]">{error}</p>}
      <button
        type="button"
        disabled={busy}
        onClick={handleComplete}
        className="w-full rounded-lg border-0 px-3 py-2.5 text-xs font-extrabold text-[#030810]"
        style={{
          background: 'linear-gradient(135deg, #a07830, #e2c97e)',
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? 'Completing…' : 'Complete Site Work'}
      </button>
      {successMsg && (
        <div className="mt-2 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-center text-xs font-bold text-emerald-400">
          {successMsg}
        </div>
      )}
    </div>
  );
}

function AdminSiteWorkSection({ onRefresh }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch('/api/admin/site-work');
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load site work');
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load site work');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mb-4 rounded-xl border border-de-gold/25 bg-[#030810]/80 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-sm font-extrabold text-[#C9A84C]">Site Work — active</h3>
        <button
          type="button"
          onClick={load}
          className="rounded-lg border border-de-gold/35 bg-transparent px-3 py-1 text-[11px] font-bold text-[#C9A84C]"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {error && <p className="mb-2 text-xs font-semibold text-[#e94560]">{error}</p>}
      {items.length === 0 && !loading ? (
        <p className="m-0 text-xs text-de-muted">No active site work bookings.</p>
      ) : (
        <div className="space-y-3">
          {items.map((b) => (
            <div key={b.id} className="rounded-lg border border-de-gold/15 bg-black/30 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-bold text-[#C9A84C]">{b.booking_ref || b.id}</span>
                <span className="text-[10px] text-de-muted">
                  {String(b.site_work_type || '').replace(/_/g, ' ')} · {b.status}
                </span>
              </div>
              <AdminSiteWorkCompletePanel
                booking={b}
                onDone={() => {
                  load();
                  if (typeof onRefresh === 'function') onRefresh();
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MonthAccordion = ({
  month, days, userData, isMobile, machineLookup, onBookingRefresh,
  onApproveLoadingMilestone,
  approveLoadingBusyId = null,
  loadingReleaseFlashById = {},
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [openDay, setOpenDay] = React.useState(null);
  const total = Object.values(days).flat().length;
  return (
    <div className={ui.accordion}>
      <button type="button" className={ui.accordionHead} onClick={() => setOpen(!open)}>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-lg">{String.fromCodePoint(0x1F4C5)}</span>
          <span className="font-bold text-[#C9A84C]">{month}</span>
          <Badge>{t('bookingsCountLabel').replace('{{count}}', total)}</Badge>
        </div>
        <span className="text-[#C9A84C]">{open ? String.fromCodePoint(0x25B2) : String.fromCodePoint(0x25BC)}</span>
      </button>
      {open && (
        <div className={ui.accordionBody}>
          {Object.entries(days).map(([day, bookings]) => (
            <div key={day} className="mb-2 overflow-hidden rounded-lg border border-de-gold/10">
              <button type="button" className="flex w-full min-h-10 items-center justify-between gap-2 bg-de-gold/5 px-3.5 py-2.5" onClick={() => setOpenDay(openDay === day ? null : day)}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-de-muted">{String.fromCodePoint(0x1F4C6)} {day}</span>
                  <Badge variant="success">{t('bookingsCountLabel').replace('{{count}}', bookings.length)}</Badge>
                </div>
                <span className="text-sm text-de-gold">{openDay === day ? String.fromCodePoint(0x25B2) : String.fromCodePoint(0x25BC)}</span>
              </button>
              {openDay === day && (
                <div className="space-y-2 p-2">
                  {bookings.map((b, i) => {
                    const client = userData.find((u) => u.id === b.client_id);
                    const bookingAt = parseBookingDate(b) || new Date();
                    return (
                      <Card key={b.id || i} className={ui.mobileCard}>
                        <CardHeader className="flex-row items-start justify-between space-y-0 p-4 pb-2">
                          <div className="min-w-0">
                            <Badge className="mb-1">{b.booking_ref}</Badge>
                            <CardDescription className="text-xs">{bookingAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</CardDescription>
                          </div>
                          <Badge variant={b.status === 'Active' ? 'success' : 'warning'}>{b.status}</Badge>
                        </CardHeader>
                        <CardContent className={cn('gap-2 p-4 pt-0', isMobile ? ui.mobileGrid2 : 'grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4')}>
                          <MobileField label={t('client')} value={client ? client.name : t('unknownLabel')} />
                          <MobileField label={t('machine')} value={getBookingMachineCode(b, machineLookup)} />
                          <MobileField
                            label={t('type')}
                            value={
                              b.booking_type === 'contract'
                                ? 'Contract'
                                : b.booking_type === 'milestone'
                                  ? 'Milestone'
                                  : (b.booking_unit || b.booking_type)
                            }
                          />
                          <MobileField label={t('location')} value={b.location || t('naLabel')} />
                          <MobileField label={t('baseLabel')} value={`₹${(b.base_amount || 0).toLocaleString('en-IN')}`} />
                          <MobileField label={t('advance')} value={`₹${(b.advance_paid || 0).toLocaleString('en-IN')}`} />
                          <MobileField
                            label={t('qtyLabel')}
                            value={(() => {
                              if (b.booking_type === 'contract') {
                                return `${b.quantity} ${Number(b.quantity) === 1 ? 'cycle' : 'cycles'}`;
                              }
                              if (b.booking_type === 'milestone') {
                                const unit = b.booking_unit_label || 'units';
                                return `${Number(b.total_units_completed || 0).toLocaleString('en-IN')} / ${Number(b.total_units_estimated || 0).toLocaleString('en-IN')} ${unit}`;
                              }
                              const unit = b.booking_unit || b.booking_type;
                              if (unit === 'hourly') {
                                const hrs = Number(b.booked_hours ?? b.quantity ?? 0);
                                if (Number.isFinite(hrs) && hrs > 0) return formatHoursHm(hrs);
                              }
                              return `${b.quantity}${unit === 'daily' ? ' days' : unit === 'weekly' ? ' weeks' : unit === 'hourly' ? ' hrs' : ' mo'}`;
                            })()}
                          />
                          <MobileField
                            label={t('actualHrsLabel')}
                            value={b.operator_actual_hours
                              ? formatHoursHm(b.operator_actual_hours)
                              : '-'}
                          />
                          <MobileField label={t('tableStart')} value={b.start_date ? new Date(b.start_date).toLocaleDateString('en-IN') : t('naLabel')} />
                        </CardContent>
                        {(b.selected_attachment || b.site_location || b.work_description) && (
                          <div className="mx-4 mb-3 rounded-lg border border-de-gold/15 bg-black/25 px-3 py-2 text-xs text-[#e8e0d0]">
                            {b.selected_attachment && (
                              <p className="mb-1.5 break-words">
                                {String.fromCodePoint(0x1F527)} {t('bookingAttachmentLabel')}: {b.selected_attachment}
                              </p>
                            )}
                            {b.site_location && (
                              <p className="mb-1.5 break-words">
                                {String.fromCodePoint(0x1F4CD)} {t('bookingSiteLabel')}: {b.site_location}
                              </p>
                            )}
                            {b.work_description && (
                              <p className="mb-0 break-words">
                                {String.fromCodePoint(0x1F4CB)} {t('bookingWorkLabel')}: {b.work_description}
                              </p>
                            )}
                          </div>
                        )}
                        <div className={cn('mx-4 mb-3 rounded-lg border px-3 py-2 text-xs font-semibold', b.owner_approved ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-amber-500/30 bg-amber-500/10 text-amber-400')}>
                          {b.owner_approved ? `${String.fromCodePoint(0x2705)} ${t('ownerApprovedStatus')}` : `${String.fromCodePoint(0x23F3)} ${t('awaitingOwnerApprovalStatus')}`}
                        </div>
                        <div className="mx-4 mb-4 flex flex-wrap items-center gap-2">
                          <AdminStartMachineButton booking={b} onDone={onBookingRefresh} />
                          {b.machine_started_at && (
                            <span className="text-xs font-semibold text-emerald-400">
                              {String.fromCodePoint(0x1F7E2)} Started {new Date(b.machine_started_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <div className="mx-4 mb-4">
                          {b.site_work_type ? (
                            <AdminSiteWorkCompletePanel booking={b} onDone={onBookingRefresh} />
                          ) : b.booking_type === 'contract' ? (
                            <>
                              <ContractBookingStatus booking={b} role="admin" />
                              {b.mobilization_loading_submitted_at && !b.mobilization_loading_approved_at && (
                                <button
                                  type="button"
                                  disabled={approveLoadingBusyId === b.id}
                                  onClick={() => onApproveLoadingMilestone?.(b)}
                                  className="mt-2 w-full rounded-lg border-0 px-3 py-2.5 text-xs font-extrabold text-[#030810]"
                                  style={{
                                    background: 'linear-gradient(135deg, #a07830, #e2c97e)',
                                    cursor: approveLoadingBusyId === b.id ? 'wait' : 'pointer',
                                    opacity: approveLoadingBusyId === b.id ? 0.7 : 1,
                                  }}
                                >
                                  {approveLoadingBusyId === b.id
                                    ? t('loading')
                                    : 'Approve Loading — Release 50%'}
                                </button>
                              )}
                              {(loadingReleaseFlashById[b.id] != null
                                || (b.mobilization_loading_approved_at
                                  && Number(b.mobilization_loading_released || 0) > 0)) && (
                                <div className="mt-2 rounded-lg border border-emerald-500/35 bg-emerald-500/10 px-3 py-2 text-center text-xs font-bold text-emerald-400">
                                  ₹{Number(
                                    loadingReleaseFlashById[b.id]
                                      ?? b.mobilization_loading_released
                                      ?? 0,
                                  ).toLocaleString('en-IN')}
                                  {' '}
                                  released to owner
                                </div>
                              )}
                            </>
                          ) : b.booking_type === 'milestone' ? (
                            <AdminMilestoneBillPanel booking={b} />
                          ) : (
                            <BookingFlowV2Card booking={b} viewOnly onRefresh={onBookingRefresh} />
                          )}
                        </div>
                        <div className="mx-4 mb-4">
                          <BookingFeedbackSummary booking={b} />
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


function fillRateBadgeVariant(status) {
  if (status === 'green') return 'success';
  if (status === 'yellow') return 'warning';
  if (status === 'red') return 'danger';
  return 'muted';
}

function fillRateBadgeLabel(status) {
  if (status === 'green') return 'Green';
  if (status === 'yellow') return 'Yellow';
  if (status === 'red') return 'Red';
  return 'N/A';
}

function formatFillRatePct(fillRate) {
  if (fillRate == null || !Number.isFinite(Number(fillRate))) return 'N/A';
  return `${Number(fillRate)}%`;
}

function FillRateDashboard({ authToken }) {
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadFillRate = useCallback(async () => {
    if (!authToken) {
      setError('Session expired. Refresh and sign in again.');
      setDistricts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/fill-rate`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      setDistricts(Array.isArray(json.districts) ? json.districts : []);
    } catch {
      setDistricts([]);
      setError('Failed to load fill-rate');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadFillRate();
  }, [loadFillRate]);

  return (
    <div className="space-y-5">
      <h2 className="m-0 text-lg font-bold text-de-gold">District Fill Rate</h2>
      {loading ? (
        <div className="de-ultra-card p-5">
          <p className="m-0 text-sm text-de-muted">Loading</p>
        </div>
      ) : error ? (
        <div className="de-ultra-card p-5">
          <p className="m-0 mb-3 text-sm text-de-accent">{error}</p>
          <button
            type="button"
            onClick={loadFillRate}
            className="rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold"
          >
            Retry
          </button>
        </div>
      ) : (
        <TablePanel
          desktop={(
            <Table>
              <TableHeader>
                <TableRow>
                  {['District', 'Total Bookings', 'Completed', 'Fill Rate', 'Status'].map((h) => (
                    <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {districts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-de-muted">No bookings</TableCell>
                  </TableRow>
                ) : districts.map((row) => (
                  <TableRow key={row.district}>
                    <TableCell className="font-semibold text-slate-100">{row.district}</TableCell>
                    <TableCell>{row.total}</TableCell>
                    <TableCell>{row.completed}</TableCell>
                    <TableCell className="text-de-gold">{formatFillRatePct(row.fillRate)}</TableCell>
                    <TableCell>
                      <Badge variant={fillRateBadgeVariant(row.status)}>{fillRateBadgeLabel(row.status)}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          mobile={districts.length === 0 ? (
            <p className={ui.empty}>No bookings</p>
          ) : districts.map((row) => (
            <Card key={row.district} className={ui.mobileCard}>
              <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                <CardTitle className="text-sm text-de-gold">{row.district}</CardTitle>
                <Badge variant={fillRateBadgeVariant(row.status)}>{fillRateBadgeLabel(row.status)}</Badge>
              </CardHeader>
              <CardContent className={ui.mobileGrid2}>
                <MobileField label="Total Bookings" value={String(row.total)} />
                <MobileField label="Completed" value={String(row.completed)} />
                <MobileField label="Fill Rate" value={formatFillRatePct(row.fillRate)} />
              </CardContent>
            </Card>
          ))}
        />
      )}
    </div>
  );
}

const AdminDashboard = () => {
  useSessionTimeout();
  const navigate = useNavigate();
  const { t } = useLanguage(); // eslint-disable-line
  const { isMobile, isTablet, gridCols, pick } = useWindowSize();
  const layoutMode = useDeviceLayout();
  const useMobileNav = layoutMode === 'phone';
  const preferCompactTables = isMobile || isTablet;
  const [activeTab, setActiveTab] = useState('overview');

  const [machineData, setMachineData] = useState([]);
  const [overviewTemporalMode, setOverviewTemporalMode] = useState('day');
  const [openMachineStatusGroups, setOpenMachineStatusGroups] = useState({});
  const [bookingData, setBookingData] = useState([]);
  const [approveLoadingBusyId, setApproveLoadingBusyId] = useState(null);
  const [loadingReleaseFlashById, setLoadingReleaseFlashById] = useState({});
  const [userData, setUserData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [issueData, setIssueData] = useState([]);
  const [machinesOffset, setMachinesOffset] = useState(0);
  const [machinesHasMore, setMachinesHasMore] = useState(false);
  const [machinesLoadingMore, setMachinesLoadingMore] = useState(false);
  const [bookingsOffset, setBookingsOffset] = useState(0);
  const [bookingsHasMore, setBookingsHasMore] = useState(false);
  const [bookingsLoadingMore, setBookingsLoadingMore] = useState(false);
  const [transactionsOffset, setTransactionsOffset] = useState(0);
  const [transactionsHasMore, setTransactionsHasMore] = useState(false);
  const [transactionsLoadingMore, setTransactionsLoadingMore] = useState(false);
  const [reconFrom, setReconFrom] = useState(() => getCurrentMonthRange().from);
  const [reconTo, setReconTo] = useState(() => getCurrentMonthRange().to);
  const [gstReport, setGstReport] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstMonth, setGstMonth] = useState(new Date().toISOString().slice(0, 7));
  const [txnChannelFilter, setTxnChannelFilter] = useState('all');
  const [txnStatusFilter, setTxnStatusFilter] = useState('all');
  const [issuesOffset, setIssuesOffset] = useState(0);
  const [issuesHasMore, setIssuesHasMore] = useState(false);
  const [issuesLoadingMore, setIssuesLoadingMore] = useState(false);
  const [grievances, setGrievances] = useState([]);
  const [grievancesLoading, setGrievancesLoading] = useState(false);
  const [grievanceFilter, setGrievanceFilter] = useState('');
  const [iotDeviceRows, setIotDeviceRows] = useState([]);
  const [iotLoading, setIotLoading] = useState(false);
  const [dlqData, setDlqData] = useState([]);
  const [dlqCounters, setDlqCounters] = useState({});
  const [dlqQueueFilter, setDlqQueueFilter] = useState('');
  const [dlqStatusFilter, setDlqStatusFilter] = useState('');
  const [isDlqQueueOpen, setIsDlqQueueOpen] = useState(false);
  const [isDlqStatusOpen, setIsDlqStatusOpen] = useState(false);
  const [dlqCursor, setDlqCursor] = useState(null);
  const [dlqHasMore, setDlqHasMore] = useState(false);
  const [dlqLoadingMore, setDlqLoadingMore] = useState(false);
  const [dlqStats, setDlqStats] = useState({ totalLast24h: 0, failedLast24h: 0, retriedLast24h: 0, queueCounts: {}, hourly: [] });
  const [dlqMessage, setDlqMessage] = useState('');
  const [auditMessage, setAuditMessage] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditActorFilter, setAuditActorFilter] = useState('');
  const [auditRoleFilter, setAuditRoleFilter] = useState('');
  const [isAuditRoleOpen, setIsAuditRoleOpen] = useState(false);
  const [auditManagerFilter, setAuditManagerFilter] = useState('');
  const [auditEntityFilter, setAuditEntityFilter] = useState('');
  const [auditFrom, setAuditFrom] = useState('');
  const [auditTo, setAuditTo] = useState('');
  const [auditMetaFilter, setAuditMetaFilter] = useState('');
  const [auditCursor, setAuditCursor] = useState(null);
  const [auditHasMore, setAuditHasMore] = useState(false);
  const [auditLoadingMore, setAuditLoadingMore] = useState(false);
  const [auditFromPickerOpen, setAuditFromPickerOpen] = useState(false);
  const [auditToPickerOpen, setAuditToPickerOpen] = useState(false);
  const [auditPresetActive, setAuditPresetActive] = useState('');
  const [selectedDlqItem, setSelectedDlqItem] = useState(null);
  const [operatorDocsTarget, setOperatorDocsTarget] = useState(null);
  const [dlqRetryReason, setDlqRetryReason] = useState('');
  const [expandedAuditRows, setExpandedAuditRows] = useState({});
  const [rateHistoryMachineId, setRateHistoryMachineId] = useState(null);
  const [rateHistoryItems, setRateHistoryItems] = useState([]);
  const [rateHistoryLoading, setRateHistoryLoading] = useState(false);
  const [rateHistoryError, setRateHistoryError] = useState('');
  const [csvColumns, setCsvColumns] = useState({
    created_at: true,
    action: true,
    actor_id: true,
    actor_role: true,
    entity_type: true,
    entity_id: true,
    metadata: false,
  });
  const [auditExportOpen, setAuditExportOpen] = useState(false);
  const [auditActionDebounced, setAuditActionDebounced] = useState('');
  const [auditActorDebounced, setAuditActorDebounced] = useState('');
  const [auditEntityDebounced, setAuditEntityDebounced] = useState('');
  const [auditMetaDebounced, setAuditMetaDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingBankTopups, setPendingBankTopups] = useState(0);
  const [pendingMachineRegistrations, setPendingMachineRegistrations] = useState(0);
  const [pendingWithdraws, setPendingWithdraws] = useState(0);
  const [pendingDualApprovals, setPendingDualApprovals] = useState(0);
  const [pendingPhoneChanges, setPendingPhoneChanges] = useState(0);
  const [pendingClientKyc, setPendingClientKyc] = useState(0);
  const [pendingOwnerKyc, setPendingOwnerKyc] = useState(0);
  const [pendingOperatorKyc, setPendingOperatorKyc] = useState(0);
  const [pendingQuotationExceptions, setPendingQuotationExceptions] = useState(0);
  const [pendingContractTerminations, setPendingContractTerminations] = useState(0);
  const [pendingLegalNotices, setPendingLegalNotices] = useState(0);
  const [pendingOwnerDefaults, setPendingOwnerDefaults] = useState(0);
  const [dashboardKpis, setDashboardKpis] = useState(null);
  const refreshPendingPhoneChangeCount = useCallback(async () => {
    try {
      const data = await secureFetch('/api/phone-change/admin-pending');
      setPendingPhoneChanges((data.items || []).length);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPendingPhoneChanges(0);
    }
  }, []);
  const refreshPendingKycCounts = useCallback(async () => {
    try {
      const fetchPendingCount = async (path) => {
        try {
          const data = await secureFetch(path);
          return Number(data?.count ?? (data?.items?.length ?? 0));
        } catch (err) {
          if ((err?.message || '') === 'auth_required') return 0;
          return 0;
        }
      };
      const [clientCount, ownerCount, operatorCount] = await Promise.all([
        fetchPendingCount('/api/admin/kyc/clients?status=pending'),
        fetchPendingCount('/api/admin/kyc/owners?status=pending'),
        fetchPendingCount('/api/admin/kyc/operators?status=pending'),
      ]);
      setPendingClientKyc(clientCount);
      setPendingOwnerKyc(ownerCount);
      setPendingOperatorKyc(operatorCount);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPendingClientKyc(0);
      setPendingOwnerKyc(0);
      setPendingOperatorKyc(0);
    }
  }, []);
  const refreshPendingLegalNotices = useCallback(async () => {
    try {
      const data = await secureFetch('/api/admin/legal-notices');
      setPendingLegalNotices(Array.isArray(data?.items) ? data.items.length : 0);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setPendingLegalNotices(0);
    }
  }, []);
  useEffect(() => {
    refreshPendingLegalNotices();
  }, [refreshPendingLegalNotices]);
  const [pendingFreelancers, setPendingFreelancers] = useState(0);
  const [pendingSurveyReviews, setPendingSurveyReviews] = useState(0);
  const [surveyTabError, setSurveyTabError] = useState('');
  const [walletPanelKey, setWalletPanelKey] = useState(0);
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
  } catch (_err) {
    storedUser = {};
  }
  const isManager = storedUser?.admin_scope === 'manager';
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managerFormOpen, setManagerFormOpen] = useState(false);
  const [managerCredentials, setManagerCredentials] = useState(null);
  const [managerSaving, setManagerSaving] = useState(false);
  const [managerForm, setManagerForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
  });
  const [managerActivityOpen, setManagerActivityOpen] = useState({});
  const [managerActivityData, setManagerActivityData] = useState({});
  const [managerActivityLoading, setManagerActivityLoading] = useState({});
  const navItems = useMemo(
    () => NAV
      .filter((item) => !(isManager && MANAGER_HIDDEN_NAV_IDS.has(item.id)))
      .map((item) => ({
      ...item,
      label: item.i18nKey ? t(item.i18nKey) : item.label,
      Icon: NAV_ICONS[item.id],
      badge: item.id === 'approvals' && (pendingUsers.length + pendingBankTopups + pendingWithdraws + pendingDualApprovals + pendingMachineRegistrations + pendingPhoneChanges + pendingClientKyc + pendingOwnerKyc + pendingOperatorKyc + pendingQuotationExceptions + pendingContractTerminations + pendingLegalNotices + pendingOwnerDefaults) > 0
        ? pendingUsers.length + pendingBankTopups + pendingWithdraws + pendingDualApprovals + pendingMachineRegistrations + pendingPhoneChanges + pendingClientKyc + pendingOwnerKyc + pendingOperatorKyc + pendingQuotationExceptions + pendingContractTerminations + pendingLegalNotices + pendingOwnerDefaults
        : item.id === 'surveyQr' && pendingFreelancers > 0
          ? pendingFreelancers
          : item.id === 'surveyReview' && pendingSurveyReviews > 0
            ? pendingSurveyReviews
            : undefined,
    })),
    [t, isManager, pendingUsers.length, pendingBankTopups, pendingWithdraws, pendingDualApprovals, pendingMachineRegistrations, pendingPhoneChanges, pendingClientKyc, pendingOwnerKyc, pendingOperatorKyc, pendingQuotationExceptions, pendingContractTerminations, pendingLegalNotices, pendingOwnerDefaults, pendingFreelancers, pendingSurveyReviews],
  );
  const [usersOffset, setUsersOffset] = useState(0);
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [usersLoadingMore, setUsersLoadingMore] = useState(false);
  const [rateTelemetry, setRateTelemetry] = useState({ allowed: 0, blocked: 0, byRoute: [], activeBuckets: 0 });
  const [rateTelemetryHistory, setRateTelemetryHistory] = useState([]);
  const [openTelemetryGroups, setOpenTelemetryGroups] = useState({});
  const [openRevenueGroups, setOpenRevenueGroups] = useState({});
  const [openAlertsGroups, setOpenAlertsGroups] = useState({});
  const [apiHealthTelemetry, setApiHealthTelemetry] = useState({ totalRequests: 0, errors5xx: 0, errorRatePct: 0, p95Ms: 0, p99Ms: 0, slo: { p95MsThreshold: 0, errorRatePctThreshold: 0, p95Healthy: true, errorRateHealthy: true }, byRoute: [] });
  const [securitySignals, setSecuritySignals] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [securityRefreshing, setSecurityRefreshing] = useState(false);
  const [securitySeverityFilter, setSecuritySeverityFilter] = useState('ALL');
  const [openSecurityGroups, setOpenSecurityGroups] = useState({});
  const [acknowledgedSignalIds, setAcknowledgedSignalIds] = useState({});
  const [securityLastRefreshedAt, setSecurityLastRefreshedAt] = useState(null);
  const [securityNow, setSecurityNow] = useState(Date.now());
  const [securityPanelMessage, setSecurityPanelMessage] = useState('');
  const [inviteGenerating, setInviteGenerating] = useState(false);
  const adminHardwareBackFromPopRef = useRef(false);
  const adminActiveTabRef = useRef(activeTab);
  const adminHardwareBackModalsRef = useRef({
    selectedDlqItem: null,
    auditFromPickerOpen: false,
    auditToPickerOpen: false,
    auditExportOpen: false,
    isAuditRoleOpen: false,
    isDlqQueueOpen: false,
    isDlqStatusOpen: false,
  });

  adminActiveTabRef.current = activeTab;
  adminHardwareBackModalsRef.current = {
    selectedDlqItem,
    auditFromPickerOpen,
    auditToPickerOpen,
    auditExportOpen,
    isAuditRoleOpen,
    isDlqQueueOpen,
    isDlqStatusOpen,
  };

  useEffect(() => {
    if (isManager && MANAGER_HIDDEN_NAV_IDS.has(activeTab)) {
      setActiveTab('overview');
    }
  }, [isManager, activeTab]);

  const auditActionInputRef = useRef(null);
  const machineStatusGroupMode = overviewTemporalMode === 'hour' ? 'hour' : 'day';
  const overviewPeriodMode = overviewTemporalMode === 'month' ? 'month' : 'day';
  const auditRoleWrapRef = useRef(null);
  const getCurrentAdminId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('developmentexpress_user') || '{}');
      return user?.id || user?.email || 'current_admin';
    } catch (_err) {
      return 'current_admin';
    }
  };
  const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const pushTelemetrySnapshot = useCallback((telemetry) => {
    if (!telemetry) return;
    setRateTelemetryHistory((prev) => {
      const next = [
        ...prev,
        {
          ts: new Date().toISOString(),
          allowed: Number(telemetry.allowed || 0),
          blocked: Number(telemetry.blocked || 0),
          activeBuckets: Number(telemetry.activeBuckets || 0),
        },
      ];
      return next.slice(-240);
    });
  }, []);
  const apiHealthEnabled = String(process.env.REACT_APP_ENABLE_API_HEALTH_TELEMETRY || 'false').toLowerCase() === 'true';
  const fetchSecurityDataWithRetry = useCallback(async (maxAttempts = 3) => {
    let telemetry = null;
    let secLogs = null;
    let failedLoginLogs = null;
    let ackLogs = null;
    let apiHealth = null;
    let lastError = '';
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      [telemetry, secLogs, failedLoginLogs, ackLogs] = await Promise.all([
        getRateLimitTelemetry(),
        getAuditLogs({ action: 'security.', limit: 20 }),
        getAuditLogs({ action: 'security.auth_login_failed', limit: 20 }),
        getAuditLogs({ action: 'security.signal_acknowledged', limit: 100 }),
      ]);
      apiHealth = apiHealthEnabled
        ? await getApiHealthTelemetry()
        : {
            windowMs: 0,
            totalRequests: 0,
            errors5xx: 0,
            errorRatePct: 0,
            p95Ms: 0,
            p99Ms: 0,
            slo: { p95MsThreshold: 0, errorRatePctThreshold: 0, p95Healthy: true, errorRateHealthy: true },
            byRoute: [],
            error: '',
            unavailableReason: 'disabled',
          };
      const hasError = telemetry?.error || secLogs?.error || failedLoginLogs?.error || ackLogs?.error || apiHealth?.error;
      if (!hasError) {
        return { telemetry, secLogs, failedLoginLogs, ackLogs, apiHealth, error: '' };
      }
      lastError = telemetry?.error || secLogs?.error || failedLoginLogs?.error || ackLogs?.error || apiHealth?.error || 'security panel fetch failed';
      if (lastError === 'auth_required') {
        return { telemetry, secLogs, failedLoginLogs, ackLogs, apiHealth, error: lastError };
      }
      if (attempt < maxAttempts) {
        await waitMs(250 * (2 ** (attempt - 1)));
      }
    }
    return { telemetry, secLogs, failedLoginLogs, ackLogs, apiHealth, error: lastError };
  }, [apiHealthEnabled]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const results = await Promise.allSettled([
          getMachinesPage({ limit: 250, offset: 0 }),
          getAllBookingsPage({ limit: 250, offset: 0 }),
          getAllUsersPage({ limit: 250, offset: 0 }),
          getAllTransactionsPage({ limit: 250, offset: 0 }),
          getAllIssuesPage({ limit: 250, offset: 0 }),
          getPendingUsers(),
          fetchAdminBankTopups('pending').catch(() => ({ items: [] })),
          (async () => {
            try {
              return await secureFetch('/api/phone-change/admin-pending');
            } catch {
              return { items: [] };
            }
          })(),
          (async () => {
            try {
              return await secureFetch('/api/admin/kyc/clients?status=pending');
            } catch {
              return { count: 0 };
            }
          })(),
          (async () => {
            try {
              return await secureFetch('/api/admin/kyc/owners?status=pending');
            } catch {
              return { count: 0 };
            }
          })(),
          (async () => {
            try {
              return await secureFetch('/api/admin/kyc/operators?status=pending');
            } catch {
              return { count: 0 };
            }
          })(),
          (async () => {
            try {
              return await secureFetch('/api/admin/dashboard-kpis');
            } catch {
              return null;
            }
          })(),
        ]);

        const readPage = (index) => {
          const result = results[index];
          if (result?.status !== 'fulfilled') return null;
          return result.value || null;
        };
        const readArray = (index) => {
          const result = results[index];
          if (result?.status !== 'fulfilled') return [];
          return Array.isArray(result.value) ? result.value : [];
        };

        const machinesPage = readPage(0);
        const bookingsPage = readPage(1);
        const usersPage = readPage(2);
        const transactionsPage = readPage(3);
        const issuesPage = readPage(4);
        const pending = readArray(5);
        const bankTopupsResult = results[6];
        const bankTopupItems = bankTopupsResult?.status === 'fulfilled'
          ? (bankTopupsResult.value?.items || [])
          : [];
        const phoneChangesResult = results[7];
        const phoneChangeItems = phoneChangesResult?.status === 'fulfilled'
          ? (phoneChangesResult.value?.items || [])
          : [];
        const clientKycResult = results[8];
        const ownerKycResult = results[9];
        const operatorKycResult = results[10];
        const pendingClientKycCount = clientKycResult?.status === 'fulfilled'
          ? Number(clientKycResult.value?.count ?? (clientKycResult.value?.items?.length ?? 0))
          : 0;
        const pendingOwnerKycCount = ownerKycResult?.status === 'fulfilled'
          ? Number(ownerKycResult.value?.count ?? (ownerKycResult.value?.items?.length ?? 0))
          : 0;
        const pendingOperatorKycCount = operatorKycResult?.status === 'fulfilled'
          ? Number(operatorKycResult.value?.count ?? (operatorKycResult.value?.items?.length ?? 0))
          : 0;
        const dashboardKpisResult = results[11];
        if (dashboardKpisResult?.status === 'fulfilled' && dashboardKpisResult.value) {
          setDashboardKpis(dashboardKpisResult.value);
        }

        setMachineData(machinesPage?.items || []);
        setMachinesHasMore(Boolean(machinesPage?.hasMore));
        setMachinesOffset(machinesPage?.nextOffset || 0);
        setBookingData(bookingsPage?.items || []);
        setBookingsHasMore(Boolean(bookingsPage?.hasMore));
        setBookingsOffset(bookingsPage?.nextOffset || 0);
        setUserData(usersPage?.items || []);
        setUsersHasMore(Boolean(usersPage?.hasMore));
        setUsersOffset(usersPage?.nextOffset || 0);
        setTransactionData(transactionsPage?.items || []);
        setTransactionsHasMore(Boolean(transactionsPage?.hasMore));
        setTransactionsOffset(transactionsPage?.nextOffset || 0);
        setIssueData(issuesPage?.items || []);
        setIssuesHasMore(Boolean(issuesPage?.hasMore));
        setIssuesOffset(issuesPage?.nextOffset || 0);
        setPendingUsers(pending);
        setPendingBankTopups(bankTopupItems.length);
        setPendingPhoneChanges(phoneChangeItems.length);
        setPendingClientKyc(pendingClientKycCount);
        setPendingOwnerKyc(pendingOwnerKycCount);
        setPendingOperatorKyc(pendingOperatorKycCount);

        const failedCount = results.filter((entry) => entry.status === 'rejected').length;
        if (failedCount > 0) {
          setSecurityPanelMessage(`Some admin panels failed to load (${failedCount}). Other report data is shown.`);
        }

        void (async () => {
          try {
            const securityData = await fetchSecurityDataWithRetry(2);
            const telemetrySnapshot = securityData.telemetry || { allowed: 0, blocked: 0, byRoute: [], activeBuckets: 0 };
            setRateTelemetry(telemetrySnapshot);
            pushTelemetrySnapshot(telemetrySnapshot);
            setApiHealthTelemetry(securityData.apiHealth || { totalRequests: 0, errors5xx: 0, errorRatePct: 0, p95Ms: 0, p99Ms: 0, slo: { p95MsThreshold: 0, errorRatePctThreshold: 0, p95Healthy: true, errorRateHealthy: true }, byRoute: [] });
            setSecuritySignals(securityData.secLogs?.items || []);
            setFailedLogins(securityData.failedLoginLogs?.items || []);
            setAcknowledgedSignalIds(buildAcknowledgedSignalMap(securityData.ackLogs?.items || []));
            setSecurityLastRefreshedAt(new Date().toISOString());
            setSecurityPanelMessage(
              securityData.error
                ? (friendlySurveyError(securityData.error, t) || `Security panel retry exhausted: ${securityData.error.slice(0, 120)}`)
                : '',
            );
          } catch (_err) {
            setSecurityPanelMessage('Security panel load failed');
          }
        })();
      } catch (_err) {
        setSecurityPanelMessage('Dashboard data load failed — refresh the page or check your connection.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchSecurityDataWithRetry, pushTelemetrySnapshot]);
  const loadMoreUsers = async () => {
    if (!usersHasMore || usersLoadingMore) return;
    setUsersLoadingMore(true);
    const result = await getAllUsersPage({ limit: 250, offset: usersOffset });
    if ((result.items || []).length > 0) {
      setUserData((prev) => appendUniqueById(prev, result.items));
      setUsersOffset(result.nextOffset || usersOffset);
      setUsersHasMore(Boolean(result.hasMore));
    } else {
      setUsersHasMore(false);
    }
    setUsersLoadingMore(false);
  };
  const loadMoreMachines = async () => {
    if (!machinesHasMore || machinesLoadingMore) return;
    setMachinesLoadingMore(true);
    const result = await getMachinesPage({ limit: 250, offset: machinesOffset });
    if ((result.items || []).length > 0) {
      setMachineData((prev) => appendUniqueById(prev, result.items));
      setMachinesOffset(result.nextOffset || machinesOffset);
      setMachinesHasMore(Boolean(result.hasMore));
    } else {
      setMachinesHasMore(false);
    }
    setMachinesLoadingMore(false);
  };
  const loadMachines = async () => {
    const result = await getMachinesPage({ limit: 250, offset: 0 });
    if (result?.items) {
      setMachineData(result.items);
      setMachinesOffset(result.nextOffset || 0);
      setMachinesHasMore(Boolean(result.hasMore));
    }
  };

  const toggleMachineRateHistory = async (machineUuid) => {
    if (rateHistoryMachineId === machineUuid) {
      setRateHistoryMachineId(null);
      setRateHistoryItems([]);
      setRateHistoryError('');
      return;
    }
    setRateHistoryMachineId(machineUuid);
    setRateHistoryLoading(true);
    setRateHistoryError('');
    setRateHistoryItems([]);
    try {
      const data = await secureFetch(
        `/api/admin/machines/${machineUuid}/rate-history?limit=50`,
      );
      setRateHistoryItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setRateHistoryError(err?.message || 'Failed to load rate history');
    } finally {
      setRateHistoryLoading(false);
    }
  };

  const formatRateHistoryValue = (value) => {
    if (value == null || value === '') return '—';
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object') return JSON.stringify(parsed);
    } catch (_err) { /* plain text */ }
    return String(value);
  };

  const handleMachineRegistrationApproved = async (machine) => {
    if (machine?.id) {
      setMachineData((prev) => appendUniqueById(prev, [machine]));
    } else {
      const result = await getMachinesPage({ limit: 250, offset: 0 });
      if (result?.items) {
        setMachineData(result.items);
        setMachinesOffset(result.nextOffset || 0);
        setMachinesHasMore(Boolean(result.hasMore));
      }
    }
  };
  const loadMoreBookings = async () => {
    if (!bookingsHasMore || bookingsLoadingMore) return;
    setBookingsLoadingMore(true);
    const result = await getAllBookingsPage({ limit: 250, offset: bookingsOffset });
    if ((result.items || []).length > 0) {
      setBookingData((prev) => appendUniqueById(prev, result.items));
      setBookingsOffset(result.nextOffset || bookingsOffset);
      setBookingsHasMore(Boolean(result.hasMore));
    } else {
      setBookingsHasMore(false);
    }
    setBookingsLoadingMore(false);
  };
  const refreshWalletPanels = useCallback(async () => {
    try {
      const [bookingsPage, usersPage, transactionsPage] = await Promise.all([
        getAllBookingsPage({ limit: 250, offset: 0 }),
        getAllUsersPage({ limit: 250, offset: 0 }),
        getAllTransactionsPage({ limit: 250, offset: 0 }),
      ]);
      setBookingData(bookingsPage?.items || []);
      setUserData(usersPage?.items || []);
      setTransactionData(transactionsPage?.items || []);
      setWalletPanelKey((k) => k + 1);
    } catch (_err) {
      /* keep existing data */
    }
  }, []);
  const loadMoreTransactions = async () => {
    if (!transactionsHasMore || transactionsLoadingMore) return;
    setTransactionsLoadingMore(true);
    const result = await getAllTransactionsPage({ limit: 250, offset: transactionsOffset });
    if ((result.items || []).length > 0) {
      setTransactionData((prev) => appendUniqueById(prev, result.items));
      setTransactionsOffset(result.nextOffset || transactionsOffset);
      setTransactionsHasMore(Boolean(result.hasMore));
    } else {
      setTransactionsHasMore(false);
    }
    setTransactionsLoadingMore(false);
  };
  const loadMoreIssues = async () => {
    if (!issuesHasMore || issuesLoadingMore) return;
    setIssuesLoadingMore(true);
    const result = await getAllIssuesPage({ limit: 250, offset: issuesOffset });
    if ((result.items || []).length > 0) {
      setIssueData((prev) => appendUniqueById(prev, result.items));
      setIssuesOffset(result.nextOffset || issuesOffset);
      setIssuesHasMore(Boolean(result.hasMore));
    } else {
      setIssuesHasMore(false);
    }
    setIssuesLoadingMore(false);
  };
  const refreshSecurityPanel = useCallback(async () => {
    if (!localStorage.getItem('developmentexpress_token')) return;
    setSecurityRefreshing(true);
    const securityData = await fetchSecurityDataWithRetry(3);
    const telemetrySnapshot = securityData.telemetry || { allowed: 0, blocked: 0, byRoute: [], activeBuckets: 0 };
    setRateTelemetry(telemetrySnapshot);
    pushTelemetrySnapshot(telemetrySnapshot);
    setApiHealthTelemetry(securityData.apiHealth || { totalRequests: 0, errors5xx: 0, errorRatePct: 0, p95Ms: 0, p99Ms: 0, slo: { p95MsThreshold: 0, errorRatePctThreshold: 0, p95Healthy: true, errorRateHealthy: true }, byRoute: [] });
    setSecuritySignals(securityData.secLogs?.items || []);
    setFailedLogins(securityData.failedLoginLogs?.items || []);
    setAcknowledgedSignalIds(buildAcknowledgedSignalMap(securityData.ackLogs?.items || []));
    setSecurityLastRefreshedAt(new Date().toISOString());
    setSecurityPanelMessage(
      securityData.error
        ? (friendlySurveyError(securityData.error, t) || `Security panel retry exhausted: ${securityData.error.slice(0, 120)}`)
        : '',
    );
    setSecurityRefreshing(false);
  }, [fetchSecurityDataWithRetry, pushTelemetrySnapshot]);
  useEffect(() => {
    if (loading) return;
    const timer = setInterval(() => {
      refreshSecurityPanel();
    }, 30000);
    return () => clearInterval(timer);
  }, [loading, refreshSecurityPanel]);
  useEffect(() => {
    if (loading) return;
    const refreshPendingFreelancers = () => {
      getSurveySurveyors().then(({ items }) => {
        setPendingFreelancers((items || []).filter((row) => row.status === 'pending').length);
      });
    };
    refreshPendingFreelancers();
    const timer = setInterval(refreshPendingFreelancers, 60000);
    return () => clearInterval(timer);
  }, [loading, activeTab]);
  useEffect(() => {
    if (loading) return;
    const refreshPendingReviews = () => {
      getSurveyResponses().then(({ items, error }) => {
        setSurveyTabError(error ? (friendlySurveyError(error, t) || error) : '');
        const pending = (items || []).filter(
          (row) => !row.is_legacy && row.payment_status === 'pending' && !row.payout_locked,
        ).length;
        setPendingSurveyReviews(pending);
      });
    };
    refreshPendingReviews();
    const timer = setInterval(refreshPendingReviews, 60000);
    return () => clearInterval(timer);
  }, [loading, activeTab]);
  const fetchGstReport = async (month) => {
    setGstLoading(true);
    try {
      const [year, mon] = month.split('-');
      const from = `${year}-${mon}-01`;
      const lastDay = new Date(year, mon, 0).getDate();
      const to = `${year}-${mon}-${lastDay}`;
      const data = await secureFetch(
        `/api/admin/reports/gst-summary?from=${from}&to=${to}`,
      );
      if (data.success) setGstReport(data);
    } catch (e) {
      if ((e?.message || '') === 'auth_required') return;
      console.error(e);
    } finally {
      setGstLoading(false);
    }
  };
  useEffect(() => {
    if (activeTab === 'reports') {
      fetchGstReport(gstMonth);
    }
  }, [activeTab, gstMonth]);
  async function loadGrievances(status = '') {
    setGrievancesLoading(true);
    try {
      const url = status ? `/api/admin/grievances?status=${status}` : '/api/admin/grievances';
      const data = await secureFetch(url);
      setGrievances(data?.items || []);
    } catch { setGrievances([]); }
    finally { setGrievancesLoading(false); }
  }
  useEffect(() => {
    if (activeTab !== 'grievances') return;
    loadGrievances(grievanceFilter);
  }, [activeTab, grievanceFilter]);

  async function loadIotDevices() {
    setIotLoading(true);
    try {
      const data = await secureFetch('/api/iot/devices');
      const devices = Array.isArray(data?.devices) ? data.devices : [];
      const enriched = await Promise.all(devices.map(async (device) => {
        let status = device.status || 'not_connected';
        let fuelLevelPct = null;
        try {
          const st = await secureFetch(`/api/iot/status/${encodeURIComponent(device.machine_id)}`);
          if (st?.status) status = st.status;
          const pct = st?.latest_telemetry?.fuel_level_pct;
          if (pct != null && Number.isFinite(Number(pct))) {
            fuelLevelPct = Number(pct);
          }
        } catch {
          // Keep device row status when status endpoint is unavailable
        }
        return {
          ...device,
          status,
          fuel_level_pct: fuelLevelPct,
        };
      }));
      setIotDeviceRows(enriched);
    } catch {
      setIotDeviceRows([]);
    } finally {
      setIotLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab !== 'iot') return;
    loadIotDevices();
  }, [activeTab]);

  async function handleAckGrievance(id) {
    try {
      await secureFetch(`/api/admin/grievances/${id}/acknowledge`, { method: 'PATCH' });
    } catch { /* keep list; reload below */ }
    loadGrievances(grievanceFilter);
  }
  async function handleResolveGrievance(id) {
    try {
      await secureFetch(`/api/admin/grievances/${id}/resolve`, { method: 'PATCH' });
    } catch { /* keep list; reload below */ }
    loadGrievances(grievanceFilter);
  }
  async function handleEscalateGrievance(id) {
    try {
      const data = await secureFetch(`/api/admin/grievances/${id}/escalate`, { method: 'PATCH' });
      if (data?.success === false) {
        const msg = String(data.message || '');
        if (data.duplicate || msg === 'Already escalated') {
          alert('Already escalated');
        } else {
          alert(msg || 'Failed to escalate');
        }
      } else {
        alert('Grievance escalated');
      }
    } catch (err) {
      const msg = String(err?.message || '');
      if (msg.includes('Already escalated')) {
        alert('Already escalated');
      } else {
        alert(msg || 'Failed to escalate');
      }
    }
    loadGrievances(grievanceFilter);
  }
  function getSLABadge(timestamp, isActive) {
    if (!isActive) return null;
    if (!timestamp) return '—';
    const dueMs = new Date(timestamp).getTime();
    if (Number.isNaN(dueMs)) return '—';
    const diffMs = dueMs - Date.now();
    const absMs = Math.abs(diffMs);
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));
    const overdue = diffMs < 0;
    const withinSixHours = !overdue && diffMs <= 6 * 60 * 60 * 1000;
    const color = overdue ? '#C0392B' : withinSixHours ? '#F39C12' : '#27AE60';
    const label = overdue ? `${hours}h ${minutes}m overdue` : `${hours}h ${minutes}m left`;
    return (
      <span
        style={{
          display: 'inline-block',
          borderRadius: '12px',
          padding: '2px 8px',
          fontSize: '11px',
          fontWeight: '600',
          color,
          border: `1px solid ${color}`,
          background: overdue
            ? 'rgba(192,57,43,0.15)'
            : withinSixHours
              ? 'rgba(243,156,18,0.15)'
              : 'rgba(39,174,96,0.15)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  }
  useEffect(() => {
    const timer = setInterval(() => setSecurityNow(Date.now()), 10000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (loading || activeTab !== 'dlq') return;
    Promise.all([
      getDlqItems({ queue: dlqQueueFilter, status: dlqStatusFilter, limit: 100 }),
      getDlqStats(),
    ]).then(([data, stats]) => {
      setDlqData(data.items || []);
      setDlqCounters(data.counters || {});
      setDlqCursor(data.nextCursor || null);
      setDlqHasMore(Boolean(data.hasMore));
      setDlqStats(stats);
      if (data.error) {
        setDlqMessage(`DLQ fetch error: ${data.error.slice(0, 120)}`);
      } else {
        setDlqMessage('');
      }
    }).catch((err) => {
      setDlqMessage(`DLQ fetch error: ${err?.message || 'Unknown error'}`);
    });
  }, [activeTab, dlqQueueFilter, dlqStatusFilter, loading]);
  const loadMoreDlq = async () => {
    if (!dlqHasMore || !dlqCursor || dlqLoadingMore) return;
    setDlqLoadingMore(true);
    const result = await getDlqItems({
      queue: dlqQueueFilter,
      status: dlqStatusFilter,
      cursor: dlqCursor,
      limit: 100,
    });
    setDlqData((prev) => appendUniqueById(prev, result.items || []));
    setDlqCursor(result.nextCursor || null);
    setDlqHasMore(Boolean(result.hasMore));
    setDlqLoadingMore(false);
  };

  useEffect(() => {
    const id = setTimeout(() => setAuditActionDebounced(auditActionFilter), 400);
    return () => clearTimeout(id);
  }, [auditActionFilter]);
  useEffect(() => {
    const id = setTimeout(() => setAuditActorDebounced(auditActorFilter), 400);
    return () => clearTimeout(id);
  }, [auditActorFilter]);
  useEffect(() => {
    const id = setTimeout(() => setAuditEntityDebounced(auditEntityFilter), 400);
    return () => clearTimeout(id);
  }, [auditEntityFilter]);
  useEffect(() => {
    const id = setTimeout(() => setAuditMetaDebounced(auditMetaFilter), 400);
    return () => clearTimeout(id);
  }, [auditMetaFilter]);

  useEffect(() => {
    if (loading || activeTab !== 'audit') return;
    const fromIso = datetimeLocalToIso(auditFrom);
    const toIso = datetimeLocalToIso(auditTo);
    setExpandedAuditRows({});
    getAuditLogs({
      action: auditActionDebounced,
      actorId: auditActorDebounced,
      actorRole: auditRoleFilter === 'manager' ? 'admin' : auditRoleFilter,
      entityType: auditEntityDebounced,
      metadata: auditRoleFilter === 'manager' ? 'admin_scope":"manager' : auditMetaDebounced,
      from: fromIso,
      to: toIso,
      limit: 120,
    }).then((result) => {
      setAuditLogs(result.items || []);
      setAuditCursor(result.nextCursor || null);
      setAuditHasMore(Boolean(result.hasMore));
      if (result.error) {
        setAuditMessage(`Audit fetch error: ${result.error.slice(0, 120)}`);
      } else {
        setAuditMessage('');
      }
    });
  }, [activeTab, auditActionDebounced, auditActorDebounced, auditRoleFilter, auditEntityDebounced, auditMetaDebounced, auditFrom, auditTo, loading]);

  const setAuditPreset = (preset) => {
    setAuditPresetActive(preset || '');
    const now = new Date();
    if (preset === 'today') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      setAuditFrom(toDatetimeLocalValue(start));
      setAuditTo(toDatetimeLocalValue(now));
    } else if (preset === '24h') {
      setAuditFrom(toDatetimeLocalValue(new Date(Date.now() - (24 * 60 * 60 * 1000))));
      setAuditTo(toDatetimeLocalValue(now));
    } else if (preset === '7d') {
      setAuditFrom(toDatetimeLocalValue(new Date(Date.now() - (7 * 24 * 60 * 60 * 1000))));
      setAuditTo(toDatetimeLocalValue(now));
    } else {
      setAuditFrom('');
      setAuditTo('');
    }
  };

  const exportAuditCsv = async () => {
    if (visibleAuditLogs.length === 0) return;
    const filename = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    try {
      const ok = await exportAuditLogsCsv(visibleAuditLogs, csvColumns, filename);
      if (ok) {
        setAuditMessage(`CSV exported · ${visibleAuditLogs.length} rows`);
        setTimeout(() => setAuditMessage(''), 2200);
      }
    } catch (_err) {
      setAuditMessage('CSV export failed');
      setTimeout(() => setAuditMessage(''), 2200);
    }
  };

  const handleLogout = () => {
    logoutAndRedirect(navigate);
  };

  const handleGenerateInviteCode = async () => {
    setInviteGenerating(true);
    try {
      const result = await secureFetch('/api/auth/admin/generate-invite', {
        method: 'POST',
        body: JSON.stringify({
          role: 'client',
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        }),
      });
      const code = result?.code || result?.invite?.code;
      if (!code) {
        alert(t('inviteGenerateFailed'));
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        window.prompt('Copy invite code:', code);
      }
      alert(t('inviteCodeCopied'));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') {
        alert(t('sessionExpiredSignIn'));
        return;
      }
      alert(err?.message || t('inviteGenerateFailed'));
    } finally {
      setInviteGenerating(false);
    }
  };

  const clearAuditField = (field) => {
    if (field === 'action') setAuditActionFilter('');
    if (field === 'actor') setAuditActorFilter('');
    if (field === 'role') {
      setAuditRoleFilter('');
      setAuditManagerFilter('');
      setAuditActorFilter('');
      setIsAuditRoleOpen(false);
    }
    if (field === 'entity') setAuditEntityFilter('');
    if (field === 'meta') setAuditMetaFilter('');
    if (field === 'from') setAuditFrom('');
    if (field === 'to') setAuditTo('');
  };
  const clearAllAuditFilters = () => {
    setAuditActionFilter('');
    setAuditActorFilter('');
    setAuditRoleFilter('');
    setAuditManagerFilter('');
    setAuditEntityFilter('');
    setAuditMetaFilter('');
    setAuditFrom('');
    setAuditTo('');
    setIsAuditRoleOpen(false);
    setAuditPresetActive('');
  };

  // Derived data from Supabase
  const clientUsers = userData.filter(u => u.role === 'client');
  const ledgerClientLookup = useMemo(() => buildClientLookup(userData), [userData]);
  const ledgerBookingByRef = useMemo(() => buildBookingRefLookup(bookingData), [bookingData]);
  const inferTxnChannel = useCallback(
    (tx) => inferWalletTxnChannel(tx, { bookingByRef: ledgerBookingByRef }),
    [ledgerBookingByRef],
  );
  const ownerUsers = userData.filter(u => u.role === 'owner');
  const operatorUsers = userData.filter(u => u.role === 'operator');
  const rejectedUsers = useMemo(
    () => userData.filter((u) => String(u.status || '').toLowerCase() === 'rejected'),
    [userData],
  );
  const activeBookings = bookingData.filter(b => b.status === 'Active');
  const refundableBookings = useMemo(() => {
    const clientNameById = Object.fromEntries(
      clientUsers.map((c) => [c.id, c.name || c.email || c.id]),
    );
    return bookingData
      .filter((b) => String(b.status || '').toLowerCase() === 'active' && Number(b.advance_paid || 0) > 0)
      .map((b) => ({
        ...b,
        client_name: clientNameById[b.client_id] || b.client_id,
      }));
  }, [bookingData, clientUsers]);
  const totalRevenue = bookingData.reduce((a, b) => a + (b.base_amount || 0), 0);
  const commission = dashboardKpis?.monthCommission != null
    ? Math.round(dashboardKpis.monthCommission)
    : Math.round(totalRevenue * 0.15); // fallback if API not loaded
  const machineLookup = useMemo(
    () => buildMachineLookup(machineData.map((m) => ({ id: m.id, machine_id: m.machine_id, name: m.name }))),
    [machineData],
  );
  const todayBookingsCount = useMemo(() => {
    const todayKey = toDateInputValue(new Date());
    return bookingData.filter((b) => {
      const d = parseBookingDate(b);
      return d && toDateInputValue(d) === todayKey;
    }).length;
  }, [bookingData]);
  const handleApproveLoadingMilestone = async (booking) => {
    if (!booking?.id || approveLoadingBusyId) return;
    setApproveLoadingBusyId(booking.id);
    try {
      const result = await secureFetch(
        `/api/admin/bookings/${encodeURIComponent(booking.id)}/mobilization/approve-loading`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      if (!result?.success) {
        alert(result?.message || t('actionFailed') || 'Approve failed');
        return;
      }
      const releaseAmount = Number(result.releaseAmount || 0);
      const approvedAt = new Date().toISOString();
      setBookingData((prev) => prev.map((b) => (
        b.id === booking.id
          ? {
            ...b,
            mobilization_loading_approved_at: approvedAt,
            mobilization_loading_released: releaseAmount,
          }
          : b
      )));
      setLoadingReleaseFlashById((prev) => ({ ...prev, [booking.id]: releaseAmount }));
      alert(`₹${releaseAmount.toLocaleString('en-IN')} released to owner`);
    } catch (err) {
      alert(err?.message || 'Approve failed');
    } finally {
      setApproveLoadingBusyId(null);
    }
  };

  const handleApproveUser = async (userId) => {
    const ok = await approveUser(userId);
    if (ok) {
      setPendingUsers((prev) => prev.filter((p) => p.id !== userId));

      const approvedUser = pendingUsers.find(
        (p) => p.id === userId,
      );
      if (approvedUser) {
        try {
          await generateServiceAgreement(approvedUser);
        } catch (e) {
          console.error('Agreement PDF failed:', e);
        }
      }

      alert(t('userApprovedPdfDownloaded'));
    } else {
      alert(t('userApprovalFailed'));
    }
  };
  const handleRejectUser = async (userId) => {
    const reason = window.prompt('Rejection reason (required — sent to the applicant):');
    if (reason == null) return;
    const trimmed = String(reason).trim();
    if (!trimmed) {
      alert('Rejection reason is required.');
      return;
    }
    const ok = await rejectUser(userId, trimmed);
    if (ok) {
      setPendingUsers((prev) => prev.filter((p) => p.id !== userId));
      alert(t('userRejectedSuccess'));
      // Move into local userData as rejected so Allow resubmission list updates
      const rejected = pendingUsers.find((p) => p.id === userId);
      if (rejected) {
        setUserData((prev) => {
          const without = (prev || []).filter((u) => u.id !== userId);
          return [...without, { ...rejected, status: 'rejected', registration_rejection_reason: trimmed }];
        });
      }
    } else {
      alert(t('userRejectionFailed'));
    }
  };
  const handleAllowResubmission = async (user) => {
    const name = user?.name || 'this user';
    const phone = user?.phone || '—';
    const confirmed = window.confirm(
      `Allow resubmission for ${name} (${phone})?\n\n`
      + 'This permanently deletes the rejected account so they can register again with the same phone/email.\n'
      + 'They will need a new invite code.\n\n'
      + 'Uploaded KYC files will be removed from storage when possible.',
    );
    if (!confirmed) return;
    const result = await allowUserResubmission(user.id);
    if (result === true) {
      setUserData((prev) => (prev || []).filter((u) => u.id !== user.id));
      alert('Account cleared. Applicant can register again with a new invite code.');
    } else {
      alert(result?.error || 'Failed to allow resubmission');
    }
  };
  const handleDeactivateUser = async (userId) => {
    if (!window.confirm(
      t('confirmDeactivateOperator'),
    )) return;
    const ok = await deactivateUser(userId);
    if (ok) {
      setUserData((prev) => prev.map((u) => (
        u.id === userId ? { ...u, status: 'inactive' } : u
      )));
    } else {
      alert(t('deactivateFailed'));
    }
  };
  const handleReactivateUser = async (userId) => {
    const ok = await reactivateUser(userId);
    if (ok) {
      setUserData((prev) => prev.map((u) => (
        u.id === userId ? { ...u, status: 'active' } : u
      )));
    } else {
      alert(t('reactivateFailed'));
    }
  };

  const loadManagers = useCallback(async () => {
    if (isManager) {
      setManagers([]);
      return;
    }
    setManagersLoading(true);
    try {
      const data = await secureFetch('/api/admin/managers');
      setManagers(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      if ((err?.message || '') === 'auth_required') return;
      setManagers([]);
    } finally {
      setManagersLoading(false);
    }
  }, [isManager]);

  useEffect(() => {
    if (loading || isManager) return undefined;
    if (activeTab !== 'operators' && activeTab !== 'audit') return undefined;
    loadManagers();
    return undefined;
  }, [loading, activeTab, isManager, loadManagers]);

  useEffect(() => {
    if (auditRoleFilter !== 'manager' || isManager) return undefined;
    if (!managers.length && !managersLoading) loadManagers();
    return undefined;
  }, [auditRoleFilter, isManager, managers.length, managersLoading, loadManagers]);

  const handleCreateManager = async (event) => {
    event.preventDefault();
    if (managerSaving) return;
    const name = String(managerForm.name || '').trim();
    const email = String(managerForm.email || '').trim();
    const password = String(managerForm.password || '');
    const phone = String(managerForm.phone || '').trim();
    if (!name || !email || password.length < 8) {
      alert(t('managerPassword'));
      return;
    }
    setManagerSaving(true);
    try {
      await secureFetch('/api/admin/managers', {
        method: 'POST',
        body: JSON.stringify({
          name,
          email,
          password,
          phone: phone || undefined,
        }),
      });
      setManagerCredentials({
        name: managerForm.name,
        email: managerForm.email,
        password: managerForm.password,
        loginUrl: 'https://app.developmentexpress.in/login/admin',
      });
      await loadManagers();
    } catch (err) {
      if ((err?.message || '') === 'auth_required') {
        alert(t('sessionExpiredSignIn'));
        return;
      }
      alert(err?.message || t('inviteGenerateFailed'));
    } finally {
      setManagerSaving(false);
    }
  };

  const copyManagerCredential = async (value) => {
    const text = String(value || '');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
    } catch {
      /* fallback below */
    }
    window.prompt('Copy:', text);
  };

  const closeManagerCredentials = () => {
    setManagerCredentials(null);
    setManagerForm({ name: '', email: '', password: '', phone: '' });
    setManagerFormOpen(false);
  };

  const handleDeactivateManager = async (managerId) => {
    if (!window.confirm(t('confirmDeactivateOperator'))) return;
    try {
      await secureFetch(`/api/admin/managers/${managerId}/deactivate`, { method: 'PATCH' });
      setManagers((prev) => prev.map((m) => (
        m.id === managerId ? { ...m, status: 'inactive' } : m
      )));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') {
        alert(t('sessionExpiredSignIn'));
        return;
      }
      alert(err?.message || t('deactivateFailed'));
    }
  };

  const handleReactivateManager = async (managerId) => {
    try {
      await secureFetch(`/api/admin/managers/${managerId}/reactivate`, { method: 'PATCH' });
      setManagers((prev) => prev.map((m) => (
        m.id === managerId ? { ...m, status: 'active' } : m
      )));
    } catch (err) {
      if ((err?.message || '') === 'auth_required') {
        alert(t('sessionExpiredSignIn'));
        return;
      }
      alert(err?.message || t('reactivateFailed'));
    }
  };

  const loadManagerActivity = async (managerId) => {
    if (managerActivityData[managerId]) return;
    setManagerActivityLoading((p) => ({ ...p, [managerId]: true }));
    const result = await getAuditLogs({ actorId: managerId, limit: 20 });
    setManagerActivityData((p) => ({ ...p, [managerId]: result.items || [] }));
    setManagerActivityLoading((p) => ({ ...p, [managerId]: false }));
  };

  const handleBlacklistClient = async (client) => {
    if (!client?.id) return;
    if (!window.confirm(t('clientBlacklistConfirm'))) return;
    const reason = window.prompt(t('clientBlacklistReason'), '');
    if (reason == null) return;
    if (String(reason).trim().length < 10) {
      alert(t('clientBlacklistReason'));
      return;
    }
    const result = await blacklistClient(client.id, String(reason).trim());
    if (result === true) {
      setUserData((prev) => prev.map((u) => (
        u.id === client.id
          ? {
            ...u,
            is_blacklisted: true,
            blacklisted_reason: String(reason).trim(),
            blacklisted_at: new Date().toISOString(),
          }
          : u
      )));
    } else {
      alert(result?.error || 'Failed to blacklist client');
    }
  };
  const handleUnblacklistClient = async (clientId) => {
    if (!clientId) return;
    if (!window.confirm(t('clientUnblacklist'))) return;
    const result = await unblacklistClient(clientId);
    if (result === true) {
      setUserData((prev) => prev.map((u) => (
        u.id === clientId
          ? {
            ...u,
            is_blacklisted: false,
            blacklisted_reason: null,
            blacklisted_at: null,
          }
          : u
      )));
    } else {
      alert(result?.error || 'Failed to remove blacklist');
    }
  };
  const handleViewKyc = async (userId, docType) => {
    const url = await getUserKycDocumentUrl(userId, docType);
    if (!url) {
      alert(t('panDocumentUnavailable'));
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  function fuelDisplay(fuelPct, capacityLitres) {
    if (capacityLitres > 0) {
      const litres = Math.round(
        (fuelPct / 100) * capacityLitres,
      );
      return `${litres}L / ${capacityLitres}L`;
    }
    return `${fuelPct}%`;
  }

  const iotByMachineKey = useMemo(() => {
    const map = new Map();
    (iotDeviceRows || []).forEach((row) => {
      if (row.machine_id != null) map.set(String(row.machine_id), row);
      const humanId = row.machines?.machine_id;
      if (humanId != null) map.set(String(humanId), row);
    });
    return map;
  }, [iotDeviceRows]);

  function getIotRowForMachine(m) {
    if (!m) return null;
    return iotByMachineKey.get(String(m.id))
      || iotByMachineKey.get(String(m.machine_id))
      || null;
  }

  function iotGpsStatusLabel(status) {
    const normalized = String(status || 'not_connected');
    if (normalized === 'active') {
      return {
        className: 'text-emerald-400',
        text: `${String.fromCodePoint(0x1F7E2)} ${t('iotStatusLive')}`,
      };
    }
    if (normalized === 'inactive') {
      return {
        className: 'text-amber-400',
        text: `${String.fromCodePoint(0x26AB)} ${t('statusInactive')}`,
      };
    }
    return {
      className: 'text-amber-400',
      text: `${String.fromCodePoint(0x26AB)} ${t('iotStatusNotConnected')}`,
    };
  }

  function iotFuelPctForMachine(m) {
    const row = getIotRowForMachine(m);
    if (row && row.fuel_level_pct != null && Number.isFinite(Number(row.fuel_level_pct))) {
      return Number(row.fuel_level_pct);
    }
    return Number(m?.fuel_level || 0);
  }

  const iotGpsDevicesActiveCount = (iotDeviceRows || []).filter((d) => d.status === 'active').length;
  const iotFuelSensorsActiveCount = (iotDeviceRows || []).filter(
    (d) => d.fuel_level_pct != null && Number(d.fuel_level_pct) > 0,
  ).length;

  const lowFuelMachines = machineData.filter(m => (m.fuel_level || 0) < 30);
  const workStatusCounts = useMemo(() => ({
    completed: (bookingData || []).filter((b) => b.status === 'Completed').length,
    pending: (bookingData || []).filter((b) => b.status === 'Dispatched' || b.status === 'Completing').length,
    active: (bookingData || []).filter((b) => b.status === 'Active').length,
    disputed: (bookingData || []).filter((b) => b.status === 'Disputed').length,
  }), [bookingData]);
  const machineWorkHours = useMemo(() => {
    const now = new Date();
    const map = {};
    const addHours = (key, todayHrs, monthHrs) => {
      if (!key) return;
      const id = String(key);
      if (!map[id]) map[id] = { today: 0, month: 0 };
      map[id].today += todayHrs;
      map[id].month += monthHrs;
    };
    (bookingData || []).forEach((b) => {
      const hours = Number(b.operator_actual_hours || b.booked_hours || 0);
      if (!Number.isFinite(hours) || hours <= 0) return;
      const d = parseBookingDate(b);
      const inMonth = d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      const inToday = inMonth && d.getDate() === now.getDate();
      const todayHrs = inToday ? hours : 0;
      const monthHrs = inMonth ? hours : 0;
      if (!todayHrs && !monthHrs) return;
      addHours(b.machine_id, todayHrs, monthHrs);
      addHours(b.machines?.id, todayHrs, monthHrs);
      addHours(b.machines?.machine_id, todayHrs, monthHrs);
    });
    return map;
  }, [bookingData]);
  const lookupMachineHours = (m) => {
    const byUuid = machineWorkHours[String(m?.id || '')];
    const byCode = machineWorkHours[String(m?.machine_id || '')];
    return {
      today: Number(byUuid?.today || byCode?.today || 0),
      month: Number(byUuid?.month || byCode?.month || 0),
    };
  };
  const machineStatusGroups = useMemo(() => {
    const toDate = (m) => {
      const raw = m?.updated_at || m?.last_seen_at || m?.created_at || null;
      if (!raw) return null;
      const parsed = new Date(raw);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };
    const bucket = {};
    (machineData || []).forEach((m) => {
      const dt = toDate(m);
      const key = dt
        ? (machineStatusGroupMode === 'day'
          ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
          : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')} ${String(dt.getHours()).padStart(2, '0')}:00`)
        : 'unknown';
      if (!bucket[key]) bucket[key] = [];
      bucket[key].push(m);
    });
    return Object.entries(bucket)
      .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
      .map(([key, rows]) => {
        let label = 'Unknown Time';
        if (key !== 'unknown') {
          if (machineStatusGroupMode === 'day') {
            label = new Date(`${key}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
          } else {
            const normalized = key.replace(' ', 'T');
            label = new Date(`${normalized}:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${key.slice(-5)}`;
          }
        }
        const activeCount = rows.filter((x) => String(x?.status || '').toLowerCase() === 'active').length;
        return { key, label, rows, activeCount };
      });
  }, [machineData, machineStatusGroupMode]);
  const visibleAuditLogs = auditLogs;
  const auditRoleCounts = useMemo(() => {
    const counts = { admin: 0, owner: 0, client: 0, operator: 0, unknown: 0 };
    (visibleAuditLogs || []).forEach((row) => {
      const role = (row?.actor_role || '').toString().toLowerCase();
      if (Object.prototype.hasOwnProperty.call(counts, role)) counts[role] += 1;
      else counts.unknown += 1;
    });
    return counts;
  }, [visibleAuditLogs]);
  const securityAuditCount = useMemo(
    () => (visibleAuditLogs || []).filter((row) => String(row?.action || '').startsWith('security.')).length,
    [visibleAuditLogs],
  );
  const uniqueAuditActors = useMemo(
    () => new Set((visibleAuditLogs || []).map((row) => row?.actor_id).filter(Boolean)).size,
    [visibleAuditLogs],
  );
  const getRoleBadgeStyle = (role) => {
    const normalized = String(role || '').toLowerCase();
    if (normalized === 'admin') return { color: '#f7df9b', border: '1px solid rgba(201,168,76,0.45)', background: 'rgba(201,168,76,0.16)' };
    if (normalized === 'owner') return { color: '#8dd3ff', border: '1px solid rgba(105,184,255,0.4)', background: 'rgba(69,138,220,0.18)' };
    if (normalized === 'client') return { color: '#9fe3be', border: '1px solid rgba(76,175,80,0.4)', background: 'rgba(76,175,80,0.18)' };
    if (normalized === 'operator') return { color: '#ffb98f', border: '1px solid rgba(255,152,0,0.4)', background: 'rgba(255,152,0,0.18)' };
    return { color: '#c5cad3', border: '1px solid rgba(148,163,184,0.35)', background: 'rgba(148,163,184,0.12)' };
  };
  const getActionBadgeStyle = (action) => {
    const text = String(action || '').toLowerCase();
    if (text.startsWith('security.')) return { color: '#ff9aa8', border: '1px solid rgba(233,69,96,0.45)', background: 'rgba(233,69,96,0.15)' };
    if (text.includes('approve')) return { color: '#9fe3be', border: '1px solid rgba(76,175,80,0.45)', background: 'rgba(76,175,80,0.15)' };
    if (text.includes('retry')) return { color: '#ffd798', border: '1px solid rgba(255,176,64,0.45)', background: 'rgba(255,176,64,0.15)' };
    return { color: '#e8e0d0', border: '1px solid rgba(201,168,76,0.32)', background: 'rgba(201,168,76,0.1)' };
  };
  const getActionSeverityIcon = (action) => {
    const text = String(action || '').toLowerCase();
    if (text.startsWith('security.')) return String.fromCodePoint(0x1F6E1);
    if (text.includes('retry')) return String.fromCodePoint(0x1F504);
    if (text.includes('approve')) return String.fromCodePoint(0x2705);
    if (text.includes('reject')) return String.fromCodePoint(0x274C);
    return String.fromCodePoint(0x1F4CB);
  };
  const getAuditSeverityMeta = (action) => {
    const text = String(action || '').toLowerCase();
    if (text.startsWith('security.')) return { level: 'Critical', rail: '#e94560', glow: 'rgba(233,69,96,0.35)' };
    if (text.includes('retry') || text.includes('failed')) return { level: 'High', rail: '#ff9800', glow: 'rgba(255,152,0,0.35)' };
    if (text.includes('reject') || text.includes('delete')) return { level: 'Medium', rail: '#f2c94c', glow: 'rgba(242,201,76,0.35)' };
    return { level: 'Normal', rail: '#4CAF50', glow: 'rgba(76,175,80,0.35)' };
  };
  const auditCsvColLabel = (key) => ({
    created_at: t('auditCsvColCreatedAt'),
    action: t('auditCsvColAction'),
    actor_id: t('auditCsvColActorId'),
    actor_role: t('auditCsvColActorRole'),
    entity_type: t('auditCsvColEntityType'),
    entity_id: t('auditCsvColEntityId'),
    metadata: t('auditCsvColMetadata'),
  }[key] || key);
  const auditRoleLabel = (role) => {
    if (!role) return t('auditAllRoles');
    if (role === 'owner') return t('complianceColOwner');
    if (role === 'client') return t('client');
    if (role === 'operator') return t('operator');
    if (role === 'manager') return t('managerSection') || 'Manager';
    return role;
  };
  const auditSeverityCounts = useMemo(() => {
    const bucket = { Critical: 0, High: 0, Medium: 0, Normal: 0 };
    (visibleAuditLogs || []).forEach((row) => {
      const level = getAuditSeverityMeta(row?.action).level;
      bucket[level] = (bucket[level] || 0) + 1;
    });
    return bucket;
  }, [visibleAuditLogs]);
  const copyAuditEvent = async (row) => {
    const payload = JSON.stringify(row || {}, null, 2);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const el = document.createElement('textarea');
        el.value = payload;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setAuditMessage(`Full audit event copied #${row?.id || '-'}`);
      setTimeout(() => setAuditMessage(''), 1800);
    } catch (_err) {
      setAuditMessage('Failed to copy full event');
      setTimeout(() => setAuditMessage(''), 2200);
    }
  };
  const copyAuditMetadata = async (row) => {
    const payload = JSON.stringify(row?.metadata || {}, null, 2);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const el = document.createElement('textarea');
        el.value = payload;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setAuditMessage(`Metadata copied for audit #${row?.id || '-'}`);
      setTimeout(() => setAuditMessage(''), 1800);
    } catch (_err) {
      setAuditMessage('Failed to copy metadata');
      setTimeout(() => setAuditMessage(''), 2200);
    }
  };
  const auditCmdInputStyle = {
    background: 'linear-gradient(180deg, rgba(10,20,35,0.98), rgba(4,10,19,0.98))',
    color: '#f4e7c0',
    border: '1px solid rgba(201,168,76,0.48)',
    borderRadius: '12px',
    padding: '10px 12px',
    minHeight: '42px',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.09), inset 0 -8px 18px rgba(0,0,0,0.32), 0 0 0 1px rgba(0,0,0,0.28), 0 8px 20px rgba(0,0,0,0.24)',
    backdropFilter: 'blur(6px)',
    boxSizing: 'border-box',
    flex: isMobile ? '1 1 100%' : '1 1 200px',
    minWidth: isMobile ? 0 : 140,
    width: isMobile ? '100%' : undefined,
  };
  const auditCmdButtonStyle = {
    background: 'linear-gradient(112deg, rgba(255,228,156,0.24) 10%, rgba(255,255,255,0.5) 18%, rgba(194,146,54,0.35) 32%, rgba(11,20,34,0.86) 56%, rgba(255,224,145,0.22) 84%)',
    backgroundSize: '220% 100%',
    border: '1px solid rgba(245,216,138,0.78)',
    color: '#ffebad',
    borderRadius: '12px',
    padding: '9px 13px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '700',
    boxShadow: '0 10px 22px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.24), inset 0 -10px 16px rgba(0,0,0,0.3), 0 0 16px rgba(201,168,76,0.22)',
    transition: 'all 0.2s ease',
    animation: 'auditShimmerSweep 4.2s linear infinite',
    flex: isMobile ? '1 1 auto' : undefined,
  };
  const auditVirtualWindow = useMemo(() => ({
    useVirtual: false,
    topPad: 0,
    bottomPad: 0,
    items: visibleAuditLogs,
  }), [visibleAuditLogs]);
  const auditActiveFilterCount = useMemo(() => (
    [auditActionFilter, auditActorFilter, auditRoleFilter, auditEntityFilter, auditMetaFilter, auditFrom, auditTo]
      .filter(Boolean).length
  ), [auditActionFilter, auditActorFilter, auditRoleFilter, auditEntityFilter, auditMetaFilter, auditFrom, auditTo]);
  const loadMoreAuditLogs = async () => {
    if (!auditHasMore || !auditCursor || auditLoadingMore) return;
    const fromIso = datetimeLocalToIso(auditFrom);
    const toIso = datetimeLocalToIso(auditTo);
    setAuditLoadingMore(true);
    const result = await getAuditLogs({
      action: auditActionDebounced,
      actorId: auditActorDebounced,
      actorRole: auditRoleFilter === 'manager' ? 'admin' : auditRoleFilter,
      entityType: auditEntityDebounced,
      metadata: auditRoleFilter === 'manager' ? 'admin_scope":"manager' : auditMetaDebounced,
      from: fromIso,
      to: toIso,
      cursor: auditCursor,
      limit: 120,
    });
    setAuditLogs((prev) => appendUniqueById(prev, result.items || []));
    setAuditCursor(result.nextCursor || null);
    setAuditHasMore(Boolean(result.hasMore));
    if (result.error) {
      setAuditMessage(`Audit fetch error: ${result.error.slice(0, 120)}`);
    } else if (!result.items || result.items.length === 0) {
      setAuditMessage(t('noMoreAuditLogs'));
      setTimeout(() => setAuditMessage(''), 2500);
    } else {
      setAuditMessage('');
    }
    setAuditLoadingMore(false);
  };

  const stats = [
    { icon: String.fromCodePoint(0x1F69C), value: machineData.length.toString(), label: t('totalMachines'), change: t('activeFleet'), up: true },
    { icon: String.fromCodePoint(0x2705), value: machineData.filter((m) => m.status === 'Active').length.toString(), label: t('activeToday'), change: t('utilizationPct'), up: true },
    { icon: String.fromCodePoint(0x1F4B0), value: formatRsCompact(totalRevenue), label: t('revenueMonth'), change: totalRevenue > 0 ? t('liveBookings') : t('noDataYet'), up: totalRevenue > 0 },
    { icon: String.fromCodePoint(0x1F4B8), value: commission > 0 ? formatRsCompact(commission) : '₹0', label: t('myCommission'), change: dashboardKpis?.monthCommission != null ? 'This month (settled)' : 'Est. (first 250 bookings)', up: commission > 0 },
    { icon: String.fromCodePoint(0x1F465), value: activeBookings.length.toString(), label: t('activeClients'), change: t('liveBookings'), up: activeBookings.length > 0 },
    { icon: String.fromCodePoint(0x26FD), value: lowFuelMachines.length.toString(), label: t('lowFuelAlert'), change: t('actionNeeded'), up: false },
  ];

  const alerts = useMemo(() => [
    ...lowFuelMachines.map((m) => ({ icon: String.fromCodePoint(0x26FD), msg: `${m.machine_id} Fuel ${fuelDisplay(m.fuel_level, m.fuel_capacity_litres)} - Critical!`, time: 'Just now', color: '#e94560', createdAt: new Date().toISOString() })),
    ...issueData.slice(0, 30).map((issue) => ({ icon: String.fromCodePoint(0x26A0), msg: `Issue: ${issue.issue_type || 'Unknown'} - ${issue.status}`, time: 'Recent', color: '#FF9800', createdAt: issue.created_at || new Date().toISOString() })),
    { icon: String.fromCodePoint(0x2705), msg: t('systemRunningNormally'), time: 'Live', color: '#4CAF50', createdAt: new Date().toISOString() },
  ], [lowFuelMachines, issueData]);
  const groupByTemporal = (items, mode, dateSelector) => {
    const bucket = {};
    (items || []).forEach((item) => {
      const raw = dateSelector(item);
      const dt = raw ? new Date(raw) : null;
      const valid = dt && !Number.isNaN(dt.getTime());
      const key = !valid
        ? 'unknown'
        : mode === 'month'
          ? `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
          : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      if (!bucket[key]) bucket[key] = [];
      bucket[key].push(item);
    });
    return Object.entries(bucket)
      .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
      .map(([key, rows]) => ({
        key,
        rows,
        label: key === 'unknown'
          ? 'Unknown Time'
          : mode === 'month'
            ? new Date(`${key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
            : new Date(`${key}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }),
      }));
  };
  const revenueTimelineRows = useMemo(
    () => (bookingData || []).map((b) => ({ amount: Number(b.base_amount || 0), createdAt: b.created_at || b.start_date || null })),
    [bookingData],
  );
  const revenueGroups = useMemo(() => {
    const grouped = groupByTemporal(revenueTimelineRows, overviewPeriodMode, (x) => x.createdAt);
    return grouped.map((g) => ({
      ...g,
      total: g.rows.reduce((sum, x) => sum + Number(x.amount || 0), 0),
      count: g.rows.length,
    }));
  }, [revenueTimelineRows, overviewPeriodMode]);
  const alertsGroups = useMemo(() => groupByTemporal(alerts, overviewPeriodMode, (x) => x.createdAt), [alerts, overviewPeriodMode]);
  const telemetryGroups = useMemo(() => {
    const grouped = groupByTemporal(rateTelemetryHistory, overviewPeriodMode, (x) => x.ts);
    return grouped.map((g) => ({
      ...g,
      allowed: g.rows.reduce((sum, x) => sum + Number(x.allowed || 0), 0),
      blocked: g.rows.reduce((sum, x) => sum + Number(x.blocked || 0), 0),
      maxBuckets: g.rows.reduce((max, x) => Math.max(max, Number(x.activeBuckets || 0)), 0),
    }));
  }, [rateTelemetryHistory, overviewPeriodMode]);
  const visibleSecuritySignals = useMemo(
    () => securitySignals.filter((row) => {
      if (securitySeverityFilter === 'ALL') return true;
      const severity = row?.metadata?.severity || (row.action?.includes('retry_burst') ? 'HIGH' : 'MEDIUM');
      return severity === securitySeverityFilter;
    }),
    [securitySignals, securitySeverityFilter],
  );
  const highCount = visibleSecuritySignals.filter((x) => (x?.metadata?.severity || (x.action?.includes('retry_burst') ? 'HIGH' : 'MEDIUM')) === 'HIGH').length;
  const mediumCount = visibleSecuritySignals.filter((x) => (x?.metadata?.severity || (x.action?.includes('retry_burst') ? 'HIGH' : 'MEDIUM')) === 'MEDIUM').length;
  const severityMax = Math.max(1, highCount, mediumCount);
  const isSecurityStale = securityLastRefreshedAt ? (securityNow - new Date(securityLastRefreshedAt).getTime()) > 65000 : true;
  const reconciliationStats = useMemo(() => {
    const rows = transactionData || [];
    const creditCount = rows.filter((x) => String(x.type || '').toLowerCase() === 'credit').length;
    const debitCount = rows.filter((x) => String(x.type || '').toLowerCase() === 'debit').length;
    const missingRefCount = rows.filter((x) => !(x.reference || x.ref)).length;
    const totalVolume = rows.reduce((sum, x) => sum + Number(x.amount || 0), 0);
    const uniqueRefs = new Set(rows.map((x) => x.reference || x.ref).filter(Boolean));
    const duplicateRefCount = Math.max(0, rows.filter((x) => (x.reference || x.ref)).length - uniqueRefs.size);
    return { creditCount, debitCount, missingRefCount, duplicateRefCount, totalVolume };
  }, [transactionData]);
  const filteredTransactionData = useMemo(() => {
    return (transactionData || []).filter((tx) => {
      const channel = inferTxnChannel(tx);
      const status = inferTxnStatus(tx);
      const channelOk = txnChannelFilter === 'all' || channel === txnChannelFilter;
      const statusOk = txnStatusFilter === 'all' || status === txnStatusFilter;
      const dateOk = isTxnInDateRange(tx, reconFrom, reconTo);
      return channelOk && statusOk && dateOk;
    });
  }, [transactionData, txnChannelFilter, txnStatusFilter, reconFrom, reconTo, inferTxnChannel]);
  const walletMonthStats = useMemo(() => {
    const monthRange = getCurrentMonthRange();
    const monthTxns = (transactionData || []).filter((tx) => isTxnInDateRange(tx, monthRange.from, monthRange.to));
    const collected = monthTxns
      .filter((x) => String(x.type || '').toLowerCase() === 'credit')
      .reduce((sum, x) => sum + Number(x.amount || 0), 0);
    const debited = monthTxns
      .filter((x) => String(x.type || '').toLowerCase() === 'debit')
      .reduce((sum, x) => sum + Number(x.amount || 0), 0);
    const pending = Math.max(0, totalRevenue - debited);
    return { collected, debited, pending };
  }, [transactionData, totalRevenue]);
  const blockedRate = (rateTelemetry.allowed + rateTelemetry.blocked) > 0
    ? Math.round((rateTelemetry.blocked / (rateTelemetry.allowed + rateTelemetry.blocked)) * 100)
    : 0;
  const topRoute = useMemo(() => {
    const rows = rateTelemetry.byRoute || [];
    if (!rows.length) return null;
    return rows.reduce((best, current) => ((current.count || 0) > (best.count || 0) ? current : best), rows[0]);
  }, [rateTelemetry.byRoute]);
  const telemetryRiskScore = useMemo(() => {
    const blockedWeight = Math.min(100, blockedRate * 4);
    const bucketWeight = Math.min(100, (rateTelemetry.activeBuckets || 0) * 3);
    return Math.round((blockedWeight * 0.65) + (bucketWeight * 0.35));
  }, [blockedRate, rateTelemetry.activeBuckets]);
  const telemetryHealth = telemetryRiskScore >= 75 ? 'Critical' : telemetryRiskScore >= 45 ? 'Warning' : 'Healthy';
  const topFailedLogins = (failedLogins || []).slice(0, 6);
  const failedByIp = useMemo(() => {
    const bucket = {};
    (failedLogins || []).forEach((row) => {
      const ip = row?.metadata?.ip || 'unknown';
      bucket[ip] = (bucket[ip] || 0) + 1;
    });
    return Object.entries(bucket)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [failedLogins]);
  const identifierBurstMap = useMemo(() => {
    const bucket = {};
    (failedLogins || []).forEach((row) => {
      const key = `${row?.metadata?.identifier || 'unknown'}|${row?.metadata?.role || '-'}`;
      bucket[key] = (bucket[key] || 0) + 1;
    });
    return bucket;
  }, [failedLogins]);
  const apiSloSignals = useMemo(
    () => (securitySignals || []).filter((row) => row?.action === 'security.api_slo_breach').slice(0, 5),
    [securitySignals],
  );
  const securitySignalGroups = useMemo(() => {
    const bucket = {};
    (visibleSecuritySignals || []).forEach((row) => {
      const created = row?.created_at ? new Date(row.created_at) : null;
      const key = created
        ? (overviewPeriodMode === 'month'
          ? `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}`
          : `${created.getFullYear()}-${String(created.getMonth() + 1).padStart(2, '0')}-${String(created.getDate()).padStart(2, '0')}`)
        : 'unknown';
      if (!bucket[key]) bucket[key] = [];
      bucket[key].push(row);
    });
    return Object.entries(bucket)
      .sort((a, b) => String(b[0]).localeCompare(String(a[0])))
      .map(([key, rows]) => {
        const label = key === 'unknown'
          ? 'Unknown Time'
          : (overviewPeriodMode === 'month'
            ? new Date(`${key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
            : new Date(`${key}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }));
        const high = rows.filter((x) => ((x?.metadata?.severity || (x.action?.includes('retry_burst') ? 'HIGH' : 'MEDIUM')) === 'HIGH')).length;
        return { key, label, rows, high };
      });
  }, [visibleSecuritySignals, overviewPeriodMode]);
  const mergeAccordionOpenState = useCallback((prev, groups) => {
    if (!groups.length) {
      return Object.keys(prev).length === 0 ? prev : {};
    }
    const next = {};
    groups.forEach((g, idx) => {
      next[g.key] = Object.prototype.hasOwnProperty.call(prev, g.key) ? prev[g.key] : idx === 0;
    });
    const nextKeys = Object.keys(next);
    const prevKeys = Object.keys(prev);
    if (prevKeys.length === nextKeys.length && nextKeys.every((k) => prev[k] === next[k])) {
      return prev;
    }
    return next;
  }, []);

  useEffect(() => {
    setOpenSecurityGroups((prev) => mergeAccordionOpenState(prev, securitySignalGroups));
  }, [securitySignalGroups, mergeAccordionOpenState]);
  useEffect(() => {
    setOpenMachineStatusGroups((prev) => mergeAccordionOpenState(prev, machineStatusGroups));
  }, [machineStatusGroups, mergeAccordionOpenState]);
  useEffect(() => {
    setOpenTelemetryGroups((prev) => mergeAccordionOpenState(prev, telemetryGroups));
  }, [telemetryGroups, mergeAccordionOpenState]);
  useEffect(() => {
    setOpenRevenueGroups((prev) => mergeAccordionOpenState(prev, revenueGroups));
  }, [revenueGroups, mergeAccordionOpenState]);
  useEffect(() => {
    setOpenAlertsGroups((prev) => mergeAccordionOpenState(prev, alertsGroups));
  }, [alertsGroups, mergeAccordionOpenState]);
  const exportSecuritySignalsCsv = async () => {
    if (!localStorage.getItem('developmentexpress_token')) return;
    setSecurityRefreshing(true);
    try {
      const [secLogs, ackLogs] = await Promise.all([
        getAuditLogs({ action: 'security.', limit: 200 }),
        getAuditLogs({ action: 'security.signal_acknowledged', limit: 200 }),
      ]);
      const ackMap = buildAcknowledgedSignalMap(ackLogs?.items || []);
      const filtered = (secLogs?.items || []).filter((row) => {
        if (securitySeverityFilter === 'ALL') return true;
        return getSecuritySignalSeverity(row) === securitySeverityFilter;
      });
      if (filtered.length === 0) {
        setSecurityPanelMessage('No security signals to export for the current filter.');
        setTimeout(() => setSecurityPanelMessage(''), 2200);
        return;
      }
      const result = await exportSecuritySignalsReport(filtered, ackMap, {
        severityFilter: securitySeverityFilter,
      });
      if (result.ok) {
        setSecurityPanelMessage(`Report exported · ${result.rowCount} rows · ${result.filename}`);
        setTimeout(() => setSecurityPanelMessage(''), 2200);
      }
    } catch (_err) {
      setSecurityPanelMessage('Export failed');
      setTimeout(() => setSecurityPanelMessage(''), 2200);
    } finally {
      setSecurityRefreshing(false);
    }
  };
  const handleAuditCmdHover = (event) => {
    event.currentTarget.style.transform = 'translateY(-1px)';
    event.currentTarget.style.boxShadow = '0 12px 22px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.28), 0 0 18px rgba(201,168,76,0.22)';
    event.currentTarget.style.filter = 'brightness(1.08)';
  };
  const handleAuditCmdLeave = (event) => {
    event.currentTarget.style.transform = 'translateY(0)';
    event.currentTarget.style.boxShadow = '';
    event.currentTarget.style.filter = 'brightness(1)';
  };
  const handleAuditCmdDown = (event) => {
    event.currentTarget.style.transform = 'translateY(1px)';
    event.currentTarget.style.boxShadow = '0 5px 10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.12)';
  };
  const handleAuditCmdUp = (event) => {
    handleAuditCmdHover(event);
  };
  const handleAuditRowHover = (event) => {
    event.currentTarget.style.transform = 'translateY(-1px)';
    event.currentTarget.style.boxShadow = `${event.currentTarget.style.boxShadow}, inset 0 0 0 1px rgba(255,255,255,0.05)`;
    event.currentTarget.style.filter = 'brightness(1.04)';
  };
  const handleAuditRowLeave = (event) => {
    event.currentTarget.style.transform = 'translateY(0)';
    event.currentTarget.style.filter = 'brightness(1)';
  };
  const closeAuditDatePickers = () => {
    setAuditFromPickerOpen(false);
    setAuditToPickerOpen(false);
  };
  useEffect(() => {
    if (activeTab !== 'audit') {
      closeAuditDatePickers();
      setIsAuditRoleOpen(false);
    }
  }, [activeTab]);
  useEffect(() => {
    if (activeTab !== 'audit' || !isAuditRoleOpen) return undefined;
    const onPointerDown = (event) => {
      if (auditRoleWrapRef.current?.contains(event.target)) return;
      setIsAuditRoleOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [activeTab, isAuditRoleOpen]);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (activeTab !== 'audit') return;
      if (event.key !== '/') return;
      const tag = (event.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable) return;
      event.preventDefault();
      auditActionInputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeTab]);

  useEffect(() => {
    if (adminHardwareBackFromPopRef.current) {
      adminHardwareBackFromPopRef.current = false;
      return;
    }
    window.history.pushState(
      { tab: activeTab },
      '',
      `?tab=${activeTab}`,
    );
  }, [activeTab]);

  useEffect(() => {
    const restoreCurrentTabInHistory = () => {
      const tab = adminActiveTabRef.current;
      window.history.pushState({ tab }, '', `?tab=${tab}`);
    };

    const onPopState = (event) => {
      const modals = adminHardwareBackModalsRef.current;
      if (modals.selectedDlqItem) {
        setSelectedDlqItem(null);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.auditFromPickerOpen || modals.auditToPickerOpen) {
        setAuditFromPickerOpen(false);
        setAuditToPickerOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.auditExportOpen) {
        setAuditExportOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.isAuditRoleOpen) {
        setIsAuditRoleOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.isDlqQueueOpen) {
        setIsDlqQueueOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (modals.isDlqStatusOpen) {
        setIsDlqStatusOpen(false);
        restoreCurrentTabInHistory();
        return;
      }
      if (event.state && event.state.tab) {
        adminHardwareBackFromPopRef.current = true;
        setActiveTab(event.state.tab);
      } else {
        adminHardwareBackFromPopRef.current = true;
        setActiveTab(ADMIN_TAB_ORDER[0]);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const activeNavLabel = navItems.find((n) => n.id === activeTab)?.label || 'Overview';

  return (
    <AdminCommandShell
      navItems={navItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      useMobileNav={useMobileNav}
      onLogout={handleLogout}
      activeLabel={activeNavLabel}
      pendingCount={pendingUsers.length}
    >
      <style>{`
        @keyframes auditPulseRail {
          0% { opacity: 0.5; filter: blur(0px); }
          50% { opacity: 1; filter: blur(0.6px); }
          100% { opacity: 0.5; filter: blur(0px); }
        }
        @keyframes auditNoiseMove {
          0% { transform: translateX(0) translateY(0); }
          100% { transform: translateX(-24px) translateY(14px); }
        }
        @keyframes auditShimmerSweep {
          0% { background-position: -160% 0; }
          100% { background-position: 190% 0; }
        }
        @keyframes auditSoftOpen {
          0% { opacity: 0.72; transform: translateY(2px) scale(0.985); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

        {loading && (
          <div className="py-12 text-center">
            <p className="mb-3 animate-pulse text-4xl">{String.fromCodePoint(0x23F3)}</p>
            <p>{t('loadingDashboard')}</p>
          </div>
        )}

        {/* OVERVIEW TAB */}
        {!loading && activeTab === 'overview' && (
          <div className="space-y-6">
            <BentoOverview
              stats={stats}
              totalRevenue={totalRevenue}
              activeBookings={activeBookings}
              lowFuelMachines={lowFuelMachines}
              machineData={machineData}
              alerts={alerts}
              onNavigate={setActiveTab}
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
              {[
                { key: 'completed', icon: '✅', val: workStatusCounts.completed, label: 'Completed', pulse: false },
                { key: 'pending', icon: '⏳', val: workStatusCounts.pending, label: 'Pending', pulse: false },
                { key: 'active', icon: '🔄', val: workStatusCounts.active, label: 'Active', pulse: true },
                { key: 'disputed', icon: '⚠️', val: workStatusCounts.disputed, label: 'Disputed', pulse: false },
              ].map((c) => (
                <div
                  key={c.key}
                  className="de-ultra-card de-glow-gold group origin-center transition-transform duration-300 hover:scale-[1.02]"
                  style={{ borderTop: '4px solid #C9A84C' }}
                >
                  <div className="relative p-4 sm:p-5">
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="de-stat-ring text-xl shadow-[0_0_18px_rgba(201,168,76,0.35)]">{c.icon}</div>
                      {c.pulse ? (
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        </span>
                      ) : null}
                    </div>
                    <p className="de-shimmer-text m-0 text-4xl font-black tracking-tight sm:text-5xl">{c.val}</p>
                    <p className="mt-2 m-0 text-[10px] font-bold uppercase tracking-[0.2em] text-de-muted">{c.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <AdminOverviewCharts />

            <OverviewUnifiedFilters
              temporalMode={overviewTemporalMode}
              onTemporalModeChange={setOverviewTemporalMode}
              securitySeverityFilter={securitySeverityFilter}
              onSecuritySeverityChange={setSecuritySeverityFilter}
              onSecurityRefresh={refreshSecurityPanel}
              onSecurityExport={exportSecuritySignalsCsv}
              securityRefreshing={securityRefreshing}
              refreshLabel={t('refreshText')}
              refreshingLabel={t('refreshingText')}
              exportLabel={t('exportCsv')}
            />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 xl:gap-5">
            <OverviewRollPanel
              title={t('liveMachineStatus')}
              isEmpty={machineStatusGroups.length === 0}
              emptyMessage={t('noMachineTelemetry')}
              headerRight={(
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  Live
                </span>
              )}
            >
              {machineStatusGroups.map((group) => {
                const isOpen = Boolean(openMachineStatusGroups[group.key]);
                return (
                  <div key={group.key} style={{ border: '1px solid rgba(201,168,76,0.22)', borderRadius: '10px', marginBottom: '8px', background: 'rgba(0,0,0,0.2)' }}>
                    <button
                      type="button"
                      onClick={() => setOpenMachineStatusGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                      style={{ width: '100%', background: 'linear-gradient(165deg, rgba(16,30,48,0.9), rgba(9,16,28,0.92))', border: 'none', borderBottom: isOpen ? '1px solid rgba(201,168,76,0.2)' : 'none', color: '#e8e0d0', borderRadius: isOpen ? '10px 10px 0 0' : '10px', padding: '9px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#f3db99', fontWeight: 700, fontSize: '12px' }}>{group.label}</span>
                        <span style={{ color: '#8ea2b8', fontSize: '10px' }}>{group.rows.length} machines</span>
                        <span style={{ color: '#9fe3be', fontSize: '10px', border: '1px solid rgba(76,175,80,0.4)', borderRadius: '999px', padding: '1px 6px', background: 'rgba(76,175,80,0.12)' }}>Active {group.activeCount}</span>
                      </span>
                      <span className="text-xs font-bold text-de-gold">{isOpen ? '\u25BC' : '\u25B6'}</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: '8px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                        {group.rows.map((m, i) => (
                          <div key={`${m.machine_id || 'machine'}-${i}`} style={{ border: '1px solid rgba(201,168,76,0.18)', borderRadius: '10px', padding: '10px', background: 'linear-gradient(160deg, rgba(14,25,40,0.88), rgba(8,14,24,0.92))' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                              <span className={ui.machineTag}>{m.machine_id}</span>
                              <span style={{ background: m.status === 'Active' ? 'rgba(76,175,80,0.15)' : 'rgba(233,69,96,0.15)', color: m.status === 'Active' ? '#4CAF50' : '#e94560', padding: '3px 8px', borderRadius: '20px', fontSize: '11px' }}>{m.status}</span>
                            </div>
                            <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 6px' }}>{m.type || 'N/A'} · 📍 {m.location || 'N/A'}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <div className={ui.fuelBar}>
                                  <div className={cn(ui.fuelFill, (m.fuel_level || 0) > 30 ? 'bg-emerald-500' : 'bg-de-accent')} style={{ width: `${m.fuel_level || 0}%` }} />
                                </div>
                                <span style={{ color: (m.fuel_level || 0) > 30 ? '#4CAF50' : '#e94560', fontSize: '12px' }}>{fuelDisplay(m.fuel_level || 0, m.fuel_capacity_litres || 0)} fuel</span>
                              </div>
                              <span style={{ color: '#c9a84c', fontSize: '12px', fontWeight: 700 }}>₹{(m.rate_per_day || 0).toLocaleString('en-IN')}</span>
                            </div>
                            {(() => {
                              const hrs = lookupMachineHours(m);
                              const util = Math.round((hrs.month / 240) * 100);
                              const utilWidth = Math.min(100, Math.max(0, util));
                              return (
                                <div style={{ marginTop: 8 }}>
                                  <p style={{ color: '#C9A84C', fontSize: 11, fontWeight: 700, margin: '0 0 6px' }}>
                                    HMR Today: {formatHoursHm(hrs.today)}
                                  </p>
                                  <p style={{ color: '#8896a8', fontSize: 10, margin: '0 0 4px' }}>Utilization {util}%</p>
                                  <div className="h-1.5 overflow-hidden rounded-full bg-[#030810]">
                                    <div
                                      className="h-full rounded-full bg-gradient-to-r from-amber-700 to-de-gold transition-all duration-500"
                                      style={{ width: `${utilWidth}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </OverviewRollPanel>

            <OverviewRollPanel
              title={t('revenueTimeline')}
              isEmpty={revenueGroups.length === 0}
              emptyMessage={t('noRevenueData')}
            >
              {revenueGroups.map((g) => {
                  const isOpen = Boolean(openRevenueGroups[g.key]);
                  return (
                    <div key={g.key} style={{ border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', marginBottom: '8px', overflow: 'hidden' }}>
                      <button type="button" onClick={() => setOpenRevenueGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))} style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: 'none', color: '#e8e0d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', cursor: 'pointer' }}>
                        <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ color: '#f2d78b', fontSize: '11px', fontWeight: 700 }}>{g.label}</span>
                          <span style={{ color: '#8ea2b8', fontSize: '10px' }}>{g.count} bookings</span>
                        </span>
                        <span className="text-xs font-bold text-de-gold">₹{g.total.toLocaleString('en-IN')} {isOpen ? '\u25BC' : '\u25B6'}</span>
                      </button>
                      {isOpen && (
                        <div style={{ padding: '8px 10px', background: 'rgba(0,0,0,0.2)' }}>
                          <div className={cn(ui.revenueBarBg, 'mb-1.5')}>
                            <div className={ui.revenueBarFill} style={{ width: `${Math.min(100, Math.round((g.total / Math.max(1, totalRevenue || 1)) * 100))}%` }} />
                          </div>
                          <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{t('shareOfRevenue')}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
            </OverviewRollPanel>

            <OverviewRollPanel
              title={t('alertsNotifications')}
              isEmpty={alertsGroups.length === 0}
              emptyMessage={t('noAlertsInPeriod')}
              footer={issuesHasMore ? (
                <div className="mt-2 shrink-0 border-t border-de-gold/15 pt-2">
                  <LoadMoreButton onClick={loadMoreIssues} disabled={issuesLoadingMore} loading={issuesLoadingMore} label={t('loadMoreAlerts')} loadingLabel={t('loading')} />
                </div>
              ) : null}
            >
              <PushNotificationToggle />
              {alertsGroups.map((g) => {
                  const isOpen = Boolean(openAlertsGroups[g.key]);
                  return (
                    <div key={g.key} style={{ border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', marginBottom: '8px', overflow: 'hidden' }}>
                      <button type="button" onClick={() => setOpenAlertsGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))} style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: 'none', color: '#e8e0d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', cursor: 'pointer' }}>
                        <span style={{ color: '#f2d78b', fontSize: '11px', fontWeight: 700 }}>{g.label}</span>
                        <span className="text-[10px] text-de-muted">{g.rows.length} alerts {isOpen ? '\u25BC' : '\u25B6'}</span>
                      </button>
                      {isOpen && (
                        <div style={{ padding: '8px' }}>
                          {g.rows.map((a, i) => (
                            <div key={`${g.key}-${i}`} className={ui.alertRow} style={{ borderLeft: `3px solid ${a.color}` }}>
                              <p className={ui.alertMsg}>{a.icon} {a.msg}</p>
                              <p className={ui.alertTime}>{a.time}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </OverviewRollPanel>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
              <OverviewRollPanel
                title={`🛡 ${t('securitySignalsTitle')}`}
                scrollMaxHeightClass="max-h-[min(260px,34vh)]"
                isEmpty={visibleSecuritySignals.length === 0}
                emptyMessage={t('noSecuritySignalsFound')}
                showScrollHint={visibleSecuritySignals.length > 0}
              >
                <p className="m-0 mb-2 text-[10px] text-de-muted">
                  {t('lastRefreshed')}: {securityLastRefreshedAt ? new Date(securityLastRefreshedAt).toLocaleTimeString('en-IN') : t('neverText')} {isSecurityStale ? `(${t('staleText')})` : `(${t('liveText')})`}
                </p>
                {securityPanelMessage ? (
                  <p className="m-0 mb-2 text-[10px] text-de-accent">{securityPanelMessage}</p>
                ) : null}
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-de-gold/15 bg-black/25 p-2">
                    <p className="m-0 text-[10px] text-de-muted">{t('highTrend')}</p>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-de-accent" style={{ width: `${Math.round((highCount / severityMax) * 100)}%` }} />
                    </div>
                    <p className="m-0 mt-1 text-[10px] font-bold text-de-accent">{highCount}</p>
                  </div>
                  <div className="rounded-lg border border-de-gold/15 bg-black/25 p-2">
                    <p className="m-0 text-[10px] text-de-muted">{t('mediumTrend')}</p>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-amber-400" style={{ width: `${Math.round((mediumCount / severityMax) * 100)}%` }} />
                    </div>
                    <p className="m-0 mt-1 text-[10px] font-bold text-amber-400">{mediumCount}</p>
                  </div>
                </div>
                {securitySignalGroups.map((group) => {
                    const isOpen = Boolean(openSecurityGroups[group.key]);
                    return (
                      <div key={group.key} style={{ border: '1px solid rgba(201,168,76,0.22)', borderRadius: '10px', marginBottom: '8px', background: 'rgba(0,0,0,0.2)' }}>
                        <button
                          type="button"
                          onClick={() => setOpenSecurityGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                          style={{ width: '100%', background: 'linear-gradient(165deg, rgba(16,30,48,0.9), rgba(9,16,28,0.92))', border: 'none', borderBottom: isOpen ? '1px solid rgba(201,168,76,0.2)' : 'none', color: '#e8e0d0', borderRadius: isOpen ? '10px 10px 0 0' : '10px', padding: '9px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: '#f3db99', fontWeight: 700, fontSize: '12px' }}>{group.label}</span>
                            <span style={{ color: '#8ea2b8', fontSize: '10px' }}>{group.rows.length} signals</span>
                            {group.high > 0 && <span style={{ color: '#ff9aa8', fontSize: '10px', border: '1px solid rgba(233,69,96,0.4)', borderRadius: '999px', padding: '1px 6px', background: 'rgba(233,69,96,0.12)' }}>High {group.high}</span>}
                          </span>
                          <span className="text-xs font-bold text-de-gold">{isOpen ? '\u25BC' : '\u25B6'}</span>
                        </button>
                        {isOpen && (
                          <div style={{ padding: '8px' }}>
                            {group.rows.map((row) => {
                              const severity = row?.metadata?.severity || (row.action?.includes('retry_burst') ? 'HIGH' : 'MEDIUM');
                              const signalKey = getSecuritySignalKey(row);
                              const ackInfo = acknowledgedSignalIds[signalKey];
                              const isAcked = Boolean(ackInfo?.acknowledged);
                              return (
                                <div key={row.id} className={ui.alertRow}>
                                  <p className={ui.alertMsg}>
                                    {row.action}
                                    <span style={{
                                      marginLeft: '8px',
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      borderRadius: '10px',
                                      border: severity === 'HIGH' ? '1px solid rgba(233,69,96,0.45)' : '1px solid rgba(255,152,0,0.45)',
                                      color: severity === 'HIGH' ? '#ff9aa8' : '#FFB74D',
                                      background: severity === 'HIGH' ? 'rgba(233,69,96,0.14)' : 'rgba(255,152,0,0.14)',
                                    }}>
                                      {severity}
                                    </span>
                                  </p>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                    <p className={ui.alertTime}>
                                      {row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}
                                      {isAcked ? ` | Ack: ${ackInfo?.actorId || '-'} @ ${ackInfo?.acknowledgedAt ? new Date(ackInfo.acknowledgedAt).toLocaleString('en-IN') : '-'}` : ''}
                                    </p>
                                    <button
                                      style={{ background: isAcked ? 'rgba(76,175,80,0.18)' : 'rgba(201,168,76,0.12)', border: isAcked ? '1px solid rgba(76,175,80,0.45)' : '1px solid rgba(201,168,76,0.35)', color: isAcked ? '#4CAF50' : '#c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '10px', cursor: isAcked ? 'default' : 'pointer' }}
                                      disabled={isAcked}
                                      onClick={async () => {
                                        const ackResult = await acknowledgeSecuritySignal(row.id);
                                        if (ackResult.ok) {
                                          setAcknowledgedSignalIds((prev) => ({
                                            ...prev,
                                            [signalKey]: {
                                              acknowledged: true,
                                              actorId: ackResult.acknowledgedBy || getCurrentAdminId(),
                                              acknowledgedAt: ackResult.acknowledgedAt || new Date().toISOString(),
                                            },
                                          }));
                                          refreshSecurityPanel();
                                        } else {
                                          alert('Failed to acknowledge signal. Please try again.');
                                        }
                                      }}
                                    >
                                      {isAcked ? t('acknowledgedText') : t('acknowledgeText')}
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                    <h4 style={{ color: '#c9a84c', fontSize: '13px', margin: '0 0 8px' }}>{t('apiSloAlertsTitle')}</h4>
                    {apiSloSignals.length === 0 ? (
                      <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{t('noRecentApiSloBreach')}</p>
                    ) : apiSloSignals.map((row) => (
                      <div key={row.id} className={ui.alertRow}>
                        <p className={cn(ui.alertMsg, 'text-de-accent')}>
                          p95 {row?.metadata?.p95Ms || 0}ms / {row?.metadata?.thresholds?.p95MsThreshold || '-'}ms · error {row?.metadata?.errorRatePct || 0}% / {row?.metadata?.thresholds?.errorRatePctThreshold || '-'}%
                        </p>
                        <p className={ui.alertTime}>
                          {row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}
                        </p>
                      </div>
                    ))}
                  </div>
              </OverviewRollPanel>

              <OverviewRollPanel
                title={`📊 ${t('rateLimitTelemetryTitle')}`}
                scrollMaxHeightClass="max-h-[min(240px,32vh)]"
                summary={(
                  <>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px', textAlign: 'center', border: '1px solid rgba(76,175,80,0.28)' }}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 3px' }}>{t('allowedText')}</p>
                    <p style={{ color: '#4CAF50', margin: 0, fontWeight: '700' }}>{rateTelemetry.allowed || 0}</p>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px', textAlign: 'center', border: '1px solid rgba(233,69,96,0.28)' }}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 3px' }}>{t('blockedText')}</p>
                    <p style={{ color: '#e94560', margin: 0, fontWeight: '700' }}>{rateTelemetry.blocked || 0}</p>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px', textAlign: 'center', border: '1px solid rgba(201,168,76,0.28)' }}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 3px' }}>{t('activeBucketsText')}</p>
                    <p style={{ color: '#c9a84c', margin: 0, fontWeight: '700' }}>{rateTelemetry.activeBuckets || 0}</p>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '8px', textAlign: 'center', border: telemetryHealth === 'Critical' ? '1px solid rgba(233,69,96,0.38)' : telemetryHealth === 'Warning' ? '1px solid rgba(255,152,0,0.38)' : '1px solid rgba(76,175,80,0.38)' }}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 3px' }}>Risk Score</p>
                    <p style={{ color: telemetryHealth === 'Critical' ? '#ff9aa8' : telemetryHealth === 'Warning' ? '#FFB74D' : '#4CAF50', margin: 0, fontWeight: '700' }}>{telemetryRiskScore}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{t('blockedRateText')}: {blockedRate}%</p>
                  <span style={{
                    border: blockedRate >= 15 ? '1px solid rgba(233,69,96,0.45)' : '1px solid rgba(76,175,80,0.45)',
                    color: blockedRate >= 15 ? '#ff9aa8' : '#4CAF50',
                    background: blockedRate >= 15 ? 'rgba(233,69,96,0.14)' : 'rgba(76,175,80,0.14)',
                    borderRadius: '10px',
                    padding: '2px 8px',
                    fontSize: '10px',
                  }}>
                    {blockedRate >= 15 ? t('alertText') : t('normalText')}
                  </span>
                  <span style={{
                    border: telemetryHealth === 'Critical' ? '1px solid rgba(233,69,96,0.45)' : telemetryHealth === 'Warning' ? '1px solid rgba(255,152,0,0.45)' : '1px solid rgba(76,175,80,0.45)',
                    color: telemetryHealth === 'Critical' ? '#ff9aa8' : telemetryHealth === 'Warning' ? '#FFB74D' : '#4CAF50',
                    background: telemetryHealth === 'Critical' ? 'rgba(233,69,96,0.14)' : telemetryHealth === 'Warning' ? 'rgba(255,152,0,0.14)' : 'rgba(76,175,80,0.14)',
                    borderRadius: '10px',
                    padding: '2px 8px',
                    fontSize: '10px',
                  }}>
                    {telemetryHealth}
                  </span>
                </div>
                {topRoute && (
                  <div style={{ marginBottom: '10px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px', border: '1px solid rgba(201,168,76,0.18)' }}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>Top Route Pressure</p>
                    <p style={{ color: '#e8e0d0', fontSize: '11px', margin: 0 }}>{topRoute.route} · hits: {topRoute.count || 0}</p>
                  </div>
                )}
                <div style={{ marginBottom: '10px', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '8px' }}>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{t('apiHealthTitle')} ({Math.round((apiHealthTelemetry.windowMs || 0) / 60000)}m)</p>
                  <p style={{ color: '#e8e0d0', fontSize: '11px', margin: '0 0 2px' }}>
                    p95: {apiHealthTelemetry.p95Ms || 0}ms / target {apiHealthTelemetry.slo?.p95MsThreshold || 0}ms · error: {apiHealthTelemetry.errorRatePct || 0}% / target {apiHealthTelemetry.slo?.errorRatePctThreshold || 0}%
                  </p>
                  <p style={{ color: (apiHealthTelemetry.slo?.p95Healthy && apiHealthTelemetry.slo?.errorRateHealthy) ? '#4CAF50' : '#ff9aa8', fontSize: '10px', margin: 0 }}>
                    {(apiHealthTelemetry.slo?.p95Healthy && apiHealthTelemetry.slo?.errorRateHealthy) ? t('sloHealthy') : t('sloBreachRisk')}
                  </p>
                </div>
                  </>
                )}
              >
                {telemetryGroups.map((g) => {
                  const isOpen = Boolean(openTelemetryGroups[g.key]);
                  const total = g.allowed + g.blocked;
                  const localBlockedRate = total > 0 ? Math.round((g.blocked / total) * 100) : 0;
                  return (
                    <div key={g.key} style={{ border: '1px solid rgba(201,168,76,0.2)', borderRadius: '10px', marginBottom: '8px', overflow: 'hidden' }}>
                      <button type="button" onClick={() => setOpenTelemetryGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))} style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: 'none', color: '#e8e0d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', cursor: 'pointer' }}>
                        <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ color: '#f2d78b', fontSize: '11px', fontWeight: 700 }}>{g.label}</span>
                          <span style={{ color: '#8ea2b8', fontSize: '10px' }}>snapshots: {g.rows.length}</span>
                        </span>
                        <span className={cn('text-[10px] font-bold', localBlockedRate >= 15 ? 'text-de-accent' : 'text-emerald-400')}>blocked {localBlockedRate}% {isOpen ? '\u25BC' : '\u25B6'}</span>
                      </button>
                      {isOpen && (
                        <div style={{ padding: '8px', background: 'rgba(0,0,0,0.18)' }}>
                          <p style={{ color: '#d2d8e2', fontSize: '11px', margin: '0 0 4px' }}>Allowed: {g.allowed} · Blocked: {g.blocked} · Peak buckets: {g.maxBuckets}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
                {(rateTelemetry.byRoute || []).slice(0, 5).map((x) => (
                  <div key={x.route} className={ui.alertRow}>
                    <p className={ui.alertMsg}>{x.route}</p>
                    <p className={ui.alertTime}>{t('hitsText')}: {x.count}</p>
                  </div>
                ))}
                <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px solid rgba(201,168,76,0.15)' }}>
                  <h4 style={{ color: '#c9a84c', fontSize: '13px', margin: '0 0 8px' }}>{t('recentFailedLoginsTitle')}</h4>
                  {failedByIp.length > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '6px', marginBottom: '8px' }}>
                      {failedByIp.map(([ip, count]) => (
                        <div key={ip} style={{ background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '8px', padding: '6px 8px' }}>
                          <p style={{ color: '#8896a8', fontSize: '10px', margin: 0 }}>{t('topSourceIp')}</p>
                          <p style={{ color: '#e8e0d0', fontSize: '11px', margin: '3px 0 0' }}>{ip} · {t('attemptsLabel')}: {count}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {topFailedLogins.length === 0 ? (
                    <p style={{ color: '#8896a8', fontSize: '11px', margin: 0 }}>{t('noRecentFailedLogins')}</p>
                  ) : topFailedLogins.map((row) => (
                    <div key={row.id} className={ui.alertRow}>
                      <p className={cn(ui.alertMsg, (identifierBurstMap[`${row.metadata?.identifier || 'unknown'}|${row.metadata?.role || '-'}`] || 0) >= 3 ? 'text-de-accent' : 'text-slate-200')}>
                        {(row.metadata?.identifier || 'unknown')} | {(row.metadata?.role || '-')}
                        {(identifierBurstMap[`${row.metadata?.identifier || 'unknown'}|${row.metadata?.role || '-'}`] || 0) >= 3 && (
                          <span style={{ marginLeft: '6px', fontSize: '10px', padding: '2px 6px', borderRadius: '10px', border: '1px solid rgba(233,69,96,0.45)', color: '#ff9aa8', background: 'rgba(233,69,96,0.14)' }}>
                            {t('burstText')}
                          </span>
                        )}
                      </p>
                      <p className={ui.alertTime}>
                        {row.metadata?.reason || t('failedText')} · IP: {row.metadata?.ip || t('unknownText')} · {row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}
                      </p>
                    </div>
                  ))}
                </div>
              </OverviewRollPanel>
            </div>
          </div>
        )}

        {/* MACHINES TAB */}
        {!loading && activeTab === 'machines' && (
          <div>
            <MachineCompliancePanel
              authToken={localStorage.getItem('developmentexpress_token') || ''}
            />
            <MachineServicePanel
              authToken={localStorage.getItem('developmentexpress_token') || ''}
            />
            <div className={cn(ui.cardRow, 'sm:grid-cols-2')}>
              {[
                { icon: String.fromCodePoint(0x1F69C), val: machineData.length.toString(), label: t('totalMachines') },
                { icon: String.fromCodePoint(0x2705), val: machineData.filter((m) => m.status === 'Active').length.toString(), label: t('activeToday') },
                { icon: String.fromCodePoint(0x26FD), val: lowFuelMachines.length.toString(), label: t('lowFuelAlert') },
                { icon: String.fromCodePoint(0x1F4CD), val: machineData.filter((m) => m.status === 'Deployed').length.toString(), label: t('deployedLabel') },
              ].map((c, i) => (
                <PremiumStatCard key={i} icon={c.icon} value={c.val} label={c.label} />
              ))}
            </div>
            <TablePanel
              title={`${String.fromCodePoint(0x1F69C)} All Machines`}
              preferCards={preferCompactTables}
              footer={`${t('auditFooterLoaded')} ${machineData.length} ${t('adminFooterMachines')}${machinesHasMore ? ` ${t('auditFooterMoreAvailable')}` : ` ${t('auditFooterAllLoaded')}`}`}
              desktop={
                <Table>
                  <TableHeader>
                    <TableRow>{[t('machineIdLabel'), t('surveyColName'), t('type'), t('location'), t('fuel'), t('adminMachinesColMinHrs'), t('adminMachinesColRateDay'), t('status'), t('surveyQrFreelancerActions')].map((h) => <TableHead key={h} className="text-de-gold/70">{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {machineData.map((m, i) => (
                      <React.Fragment key={m.id || i}>
                        <TableRow>
                          <TableCell><Badge>{m.machine_id}</Badge></TableCell>
                          <TableCell>{m.name}</TableCell>
                          <TableCell>{m.type || 'N/A'}</TableCell>
                          <TableCell>{m.location}</TableCell>
                          <TableCell className={(m.fuel_level || 0) > 30 ? 'text-emerald-400' : 'text-de-accent'}>{fuelDisplay(m.fuel_level || 0, m.fuel_capacity_litres || 0)}</TableCell>
                          <TableCell>{m.minimum_hours || 3}h</TableCell>
                          <TableCell>₹{(m.rate_per_day || 0).toLocaleString('en-IN')}</TableCell>
                          <TableCell><Badge variant={m.status === 'Active' ? 'success' : 'warning'}>{m.status}</Badge></TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              <button
                                type="button"
                                onClick={() => toggleMachineRateHistory(m.id)}
                                style={{
                                  background: rateHistoryMachineId === m.id
                                    ? 'rgba(201,168,76,0.25)'
                                    : 'rgba(201,168,76,0.1)',
                                  border: '1px solid rgba(201,168,76,0.4)',
                                  color: '#C9A84C',
                                  borderRadius: 6,
                                  padding: '4px 10px',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                }}
                              >
                                {rateHistoryMachineId === m.id
                                  ? 'Hide history'
                                  : 'Rate history'}
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  const newStatus = m.status === 'Inactive'
                                    ? 'Active'
                                    : 'Inactive';
                                  try {
                                    await secureFetch(
                                      `/api/admin/machines/${m.id}/status`,
                                      {
                                        method: 'PATCH',
                                        body: JSON.stringify({ status: newStatus }),
                                      },
                                    );
                                    loadMachines();
                                  } catch (err) {
                                    if ((err?.message || '') === 'auth_required') return;
                                  }
                                }}
                                style={{
                                  background: m.status === 'Inactive'
                                    ? 'rgba(76,175,80,0.15)'
                                    : 'rgba(233,69,96,0.15)',
                                  border: m.status === 'Inactive'
                                    ? '1px solid rgba(76,175,80,0.4)'
                                    : '1px solid rgba(233,69,96,0.4)',
                                  color: m.status === 'Inactive'
                                    ? '#4CAF50'
                                    : '#e94560',
                                  borderRadius: 6,
                                  padding: '4px 10px',
                                  fontSize: 11,
                                  cursor: 'pointer',
                                }}
                              >
                                {m.status === 'Inactive'
                                  ? t('activateBtn')
                                  : t('deactivateBtn')}
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {rateHistoryMachineId === m.id && (
                          <TableRow>
                            <TableCell colSpan={9} className="bg-black/20">
                              <div style={{ padding: '8px 4px' }}>
                                <p style={{
                                  color: '#C9A84C',
                                  fontWeight: 700,
                                  fontSize: 12,
                                  margin: '0 0 8px',
                                }}>
                                  Rate Change History
                                </p>
                                {rateHistoryLoading && (
                                  <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                                    {t('loading')}
                                  </p>
                                )}
                                {!rateHistoryLoading && rateHistoryError && (
                                  <p style={{ color: '#e94560', fontSize: 11, margin: 0 }}>
                                    {rateHistoryError}
                                  </p>
                                )}
                                {!rateHistoryLoading && !rateHistoryError
                                  && rateHistoryItems.length === 0 && (
                                  <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                                    No rate changes recorded for this machine yet.
                                  </p>
                                )}
                                {!rateHistoryLoading && rateHistoryItems.length > 0 && (
                                  <div style={{ overflowX: 'auto' }}>
                                    <table style={{
                                      width: '100%',
                                      borderCollapse: 'collapse',
                                      fontSize: 11,
                                    }}>
                                      <thead>
                                        <tr style={{ color: '#8896a8', textAlign: 'left' }}>
                                          <th style={{ padding: '4px 6px' }}>When</th>
                                          <th style={{ padding: '4px 6px' }}>Field</th>
                                          <th style={{ padding: '4px 6px' }}>Old</th>
                                          <th style={{ padding: '4px 6px' }}>New</th>
                                          <th style={{ padding: '4px 6px' }}>Owner</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rateHistoryItems.map((row) => (
                                          <tr
                                            key={row.id}
                                            style={{
                                              borderTop: '1px solid rgba(255,255,255,0.06)',
                                              color: '#e8e0d0',
                                            }}
                                          >
                                            <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                              {row.changed_at
                                                ? new Date(row.changed_at).toLocaleString('en-IN')
                                                : '—'}
                                            </td>
                                            <td style={{ padding: '4px 6px', color: '#C9A84C' }}>
                                              {row.field}
                                            </td>
                                            <td style={{
                                              padding: '4px 6px',
                                              fontFamily: 'monospace',
                                              maxWidth: 220,
                                              wordBreak: 'break-word',
                                            }}>
                                              {formatRateHistoryValue(row.old_value)}
                                            </td>
                                            <td style={{
                                              padding: '4px 6px',
                                              fontFamily: 'monospace',
                                              maxWidth: 220,
                                              wordBreak: 'break-word',
                                            }}>
                                              {formatRateHistoryValue(row.new_value)}
                                            </td>
                                            <td style={{
                                              padding: '4px 6px',
                                              fontFamily: 'monospace',
                                              fontSize: 10,
                                            }}>
                                              {row.owner_id
                                                ? `${String(row.owner_id).slice(0, 8)}…`
                                                : '—'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              }
              mobile={machineData.map((m, i) => (
                <Card key={i} className={ui.mobileCard}>
                  <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                    <CardTitle className="text-sm text-de-gold">{m.machine_id}</CardTitle>
                    <Badge variant={m.status === 'Active' ? 'success' : 'warning'}>{m.status}</Badge>
                  </CardHeader>
                  <CardContent className={ui.mobileGrid2}>
                    <MobileField label={t('surveyColName')} value={m.name} />
                    <MobileField label={t('type')} value={m.type || t('naLabel')} />
                    <MobileField label={t('location')} value={m.location} />
                    <MobileField label={t('fuel')} value={fuelDisplay(m.fuel_level || 0, m.fuel_capacity_litres || 0)} />
                    <MobileField label={t('adminMachinesColMinHrs')} value={`${m.minimum_hours || 3}h`} />
                    <MobileField label={t('adminMachinesColRateDay')} value={`₹${(m.rate_per_day || 0).toLocaleString('en-IN')}`} className="col-span-2" />
                    <div className="col-span-2 pt-1" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => toggleMachineRateHistory(m.id)}
                        style={{
                          background: rateHistoryMachineId === m.id
                            ? 'rgba(201,168,76,0.25)'
                            : 'rgba(201,168,76,0.1)',
                          border: '1px solid rgba(201,168,76,0.4)',
                          color: '#C9A84C',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 11,
                          cursor: 'pointer',
                        }}
                      >
                        {rateHistoryMachineId === m.id
                          ? 'Hide history'
                          : 'Rate history'}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const newStatus = m.status === 'Inactive'
                            ? 'Active'
                            : 'Inactive';
                          try {
                            await secureFetch(
                              `/api/admin/machines/${m.id}/status`,
                              {
                                method: 'PATCH',
                                body: JSON.stringify({ status: newStatus }),
                              },
                            );
                            loadMachines();
                          } catch (err) {
                            if ((err?.message || '') === 'auth_required') return;
                          }
                        }}
                        style={{
                          background: m.status === 'Inactive'
                            ? 'rgba(76,175,80,0.15)'
                            : 'rgba(233,69,96,0.15)',
                          border: m.status === 'Inactive'
                            ? '1px solid rgba(76,175,80,0.4)'
                            : '1px solid rgba(233,69,96,0.4)',
                          color: m.status === 'Inactive'
                            ? '#4CAF50'
                            : '#e94560',
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 11,
                          cursor: 'pointer',
                        }}
                      >
                        {m.status === 'Inactive'
                          ? t('activateBtn')
                          : t('deactivateBtn')}
                      </button>
                    </div>
                    {rateHistoryMachineId === m.id && (
                      <div className="col-span-2 pt-2" style={{ borderTop: '1px solid rgba(201,168,76,0.2)' }}>
                        <p style={{ color: '#C9A84C', fontWeight: 700, fontSize: 12, margin: '0 0 6px' }}>
                          Rate Change History
                        </p>
                        {rateHistoryLoading && (
                          <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>{t('loading')}</p>
                        )}
                        {!rateHistoryLoading && rateHistoryError && (
                          <p style={{ color: '#e94560', fontSize: 11, margin: 0 }}>{rateHistoryError}</p>
                        )}
                        {!rateHistoryLoading && !rateHistoryError && rateHistoryItems.length === 0 && (
                          <p style={{ color: '#8896a8', fontSize: 11, margin: 0 }}>
                            No rate changes recorded for this machine yet.
                          </p>
                        )}
                        {!rateHistoryLoading && rateHistoryItems.map((row) => (
                          <div
                            key={row.id}
                            style={{
                              fontSize: 10,
                              color: '#e8e0d0',
                              marginBottom: 6,
                              paddingBottom: 6,
                              borderBottom: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            <div style={{ color: '#C9A84C' }}>{row.field}</div>
                            <div>
                              {row.changed_at
                                ? new Date(row.changed_at).toLocaleString('en-IN')
                                : '—'}
                            </div>
                            <div style={{ fontFamily: 'monospace' }}>
                              {formatRateHistoryValue(row.old_value)}
                              {' → '}
                              {formatRateHistoryValue(row.new_value)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            />
            {machinesHasMore && (
              <LoadMoreButton onClick={loadMoreMachines} disabled={machinesLoadingMore} loading={machinesLoadingMore} label={t('loadMoreMachines')} loadingLabel={t('loading')} />
            )}
          </div>
        )}

                {/* BOOKINGS TAB */}
        {!loading && activeTab === 'bookings' && (
          <div>
            <AdminSiteWorkSection onRefresh={refreshWalletPanels} />
            {bookingData.filter((b) => b.status === 'Disputed').map((b) => (
              <AdminDisputePanel key={b.id} booking={b} onDone={refreshWalletPanels} />
            ))}
            {bookingData
              .filter((b) => (
                b.status === 'Completing'
                || b.status === 'Completing_PendingPayment'
              ))
              .map((b) => {
                const client = userData.find((u) => u.id === b.client_id);
                return (
                  <AdminForceSettlePanel
                    key={`force-settle-${b.id}`}
                    booking={b}
                    clientWalletBalance={client?.wallet_balance}
                    onDone={refreshWalletPanels}
                  />
                );
              })}
            {!isManager && bookingData
              .filter((b) => b.status === 'Active')
              .map((b) => (
                <AdminForceCancelPanel
                  key={`force-cancel-${b.id}`}
                  booking={b}
                  onDone={refreshWalletPanels}
                />
              ))}
            {/* Stats */}
            <div className={cn(ui.cardRow, 'mb-5 sm:grid-cols-2')}>
              {[
                { icon: String.fromCodePoint(0x1F4CB), val: bookingData.length.toString(), label: t('totalBookings') },
                { icon: String.fromCodePoint(0x2705), val: activeBookings.length.toString(), label: t('activeNow') },
                { icon: String.fromCodePoint(0x1F4B0), val: '₹' + totalRevenue.toLocaleString('en-IN'), label: t('totalRevenue') },
                { icon: String.fromCodePoint(0x1F4C5), val: todayBookingsCount.toString(), label: t('todayLabel') },
              ].map((c, i) => (
                <PremiumStatCard key={i} icon={c.icon} value={c.val} label={c.label} />
              ))}
            </div>
            {/* Group by Month */}
            {(() => {
              const grouped = {};
              bookingData.forEach(b => {
                const d = parseBookingDate(b);
                if (!d) return;
                const key = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                const day = d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short' });
                if (!grouped[key]) grouped[key] = {};
                if (!grouped[key][day]) grouped[key][day] = [];
                grouped[key][day].push(b);
              });
              return Object.entries(grouped).map(([month, days]) => (
                <MonthAccordion
                  key={month}
                  month={month}
                  days={days}
                  userData={userData}
                  isMobile={isMobile}
                  machineLookup={machineLookup}
                  onBookingRefresh={refreshWalletPanels}
                  onApproveLoadingMilestone={handleApproveLoadingMilestone}
                  approveLoadingBusyId={approveLoadingBusyId}
                  loadingReleaseFlashById={loadingReleaseFlashById}
                />
              ));
            })()}
            {bookingsHasMore && <LoadMoreButton onClick={loadMoreBookings} disabled={bookingsLoadingMore} loading={bookingsLoadingMore} label={t('loadMoreBookings')} loadingLabel={t('loading')} />}
            <p className={ui.hint}>
              {`${t('loadedBookings').replace('{{count}}', bookingData.length)}${bookingsHasMore ? ' ' + t('moreAvailable') : ' ' + t('allLoaded')}`}
            </p>
          </div>
        )}
        {/* CLIENTS TAB */}
        {!loading && activeTab === 'clients' && (
          <div>
            <AdminClientKycPanel
              authToken={localStorage.getItem('developmentexpress_token') || ''}
              onQueueChange={refreshPendingKycCounts}
            />
            <TablePanel
              title={`${String.fromCodePoint(0x1F477)} All Clients (${clientUsers.length})`}
              preferCards={preferCompactTables}
              footer={`${t('auditFooterLoaded')} ${clientUsers.length} ${t('adminFooterClients')}${usersHasMore ? ` ${t('auditFooterMoreAvailable')}` : ` ${t('auditFooterAllLoaded')}`}`}
              desktop={
                <Table>
                  <TableHeader>
                    <TableRow>{[t('adminClientName'), t('emailLabel'), t('surveyQrMobile'), t('gstin'), 'Client Type', t('bookingsTab'), t('status'), 'Actions'].map((h) => <TableHead key={h} className="text-de-gold/70">{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientUsers.length > 0 ? clientUsers.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-semibold text-de-gold">{c.name}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <PhoneTableCell user={c} />
                        <TableCell>{c.gstin || 'N/A'}</TableCell>
                        <TableCell>
                          <span
                            title={clientTypeTier(c.client_type)}
                            className="inline-block whitespace-nowrap rounded-lg border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C9A84C]/80"
                          >
                            {formatClientType(c.client_type)}
                          </span>
                        </TableCell>
                        <TableCell>{bookingData.filter((b) => b.client_id === c.id).length}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1">
                            {userStatusBadge(c.status, t)}
                            {c.is_blacklisted ? (
                              <Badge variant="danger">{String.fromCodePoint(0x1F6AB)} {t('clientBlacklisted')}</Badge>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          {c.is_blacklisted ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-[10px] border-green-500/50 text-green-400 hover:bg-green-500/10"
                              onClick={() => handleUnblacklistClient(c.id)}
                            >
                              {String.fromCodePoint(0x2705)} {t('clientUnblacklist')}
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-[10px] border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleBlacklistClient(c)}
                            >
                              {String.fromCodePoint(0x1F6AB)} Blacklist
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={8} className="text-center text-de-muted">No clients found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              }
              mobile={clientUsers.length > 0 ? clientUsers.map((c, i) => (
                <Card key={i} className={ui.mobileCard}>
                  <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                    <CardTitle className="text-sm text-de-gold">{c.name}</CardTitle>
                    <div className="flex flex-wrap items-center gap-1 justify-end">
                      {userStatusBadge(c.status, t)}
                      {c.is_blacklisted ? (
                        <Badge variant="danger">{String.fromCodePoint(0x1F6AB)} {t('clientBlacklisted')}</Badge>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent className={ui.mobileGrid2}>
                    <MobileField label="Email" value={c.email} className="col-span-2" />
                    <MobilePhoneField user={c} className="col-span-2" />
                    <MobileField label="GSTIN" value={c.gstin || 'N/A'} />
                    <MobileField
                      label="Client Type"
                      value={`${formatClientType(c.client_type)} · ${clientTypeTier(c.client_type)}`}
                    />
                    <MobileField label="Bookings" value={bookingData.filter((b) => b.client_id === c.id).length} />
                    <div className="col-span-2 pt-1">
                      {c.is_blacklisted ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 w-full text-[10px] border-green-500/50 text-green-400"
                          onClick={() => handleUnblacklistClient(c.id)}
                        >
                          {String.fromCodePoint(0x2705)} {t('clientUnblacklist')}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 w-full text-[10px] border-red-500/50 text-red-400"
                          onClick={() => handleBlacklistClient(c)}
                        >
                          {String.fromCodePoint(0x1F6AB)} Blacklist
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )) : <p className={ui.empty}>No clients found</p>}
            />
            {usersHasMore && <LoadMoreButton onClick={loadMoreUsers} disabled={usersLoadingMore} loading={usersLoadingMore} label={t('loadMoreUsers')} loadingLabel={t('loading')} />}
          </div>
        )}

        {/* OWNERS TAB */}
        {!loading && activeTab === 'owners' && (
          <div>
            <AdminOwnerKycPanel
              authToken={localStorage.getItem('developmentexpress_token') || ''}
              onQueueChange={refreshPendingKycCounts}
            />
            <TablePanel
              title={`${String.fromCodePoint(0x1F3D7)} Machine Owners (${ownerUsers.length})`}
              footer={`${t('auditFooterLoaded')} ${ownerUsers.length} ${t('adminFooterOwners')}${usersHasMore ? ` ${t('auditFooterMoreAvailable')}` : ` ${t('auditFooterAllLoaded')}`}`}
              desktop={
                <Table>
                  <TableHeader>
                    <TableRow>{[t('adminOwnerName'), t('emailLabel'), t('surveyQrMobile'), 'Entity', t('adminPayoutWallet'), t('status')].map((h) => <TableHead key={h} className="text-de-gold/70">{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {ownerUsers.length > 0 ? ownerUsers.map((o, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-semibold text-de-gold">{o.name}</TableCell>
                        <TableCell>{o.email}</TableCell>
                        <PhoneTableCell user={o} />
                        <TableCell>
                          <span className="inline-block whitespace-nowrap rounded-lg border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C9A84C]/80">
                            {formatOwnerEntityType(o.owner_entity_type)}
                          </span>
                        </TableCell>
                        <TableCell className="font-bold tabular-nums text-emerald-400">₹{(o.wallet_balance || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell>{userStatusBadge(o.status, t)}</TableCell>
                      </TableRow>
                    )) : (
                      <TableRow><TableCell colSpan={6} className="text-center text-de-muted">No owners found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              }
              preferCards={preferCompactTables}
              mobile={ownerUsers.length > 0 ? ownerUsers.map((o, i) => (
                <Card key={i} className={ui.mobileCard}>
                  <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                    <CardTitle className="break-words text-sm text-de-gold">{o.name}</CardTitle>
                    {userStatusBadge(o.status, t)}
                  </CardHeader>
                  <CardContent className={ui.mobileGrid2}>
                    <MobileField label="Email" value={o.email} className="col-span-2" />
                    <MobilePhoneField user={o} className="col-span-2" />
                    <MobileField label="Entity" value={formatOwnerEntityType(o.owner_entity_type)} />
                    <MobileField label="Payout Wallet" value={`₹${(o.wallet_balance || 0).toLocaleString('en-IN')}`} className="col-span-2" />
                  </CardContent>
                </Card>
              )) : <p className={ui.empty}>No owners found</p>}
            />
            {usersHasMore && <LoadMoreButton onClick={loadMoreUsers} disabled={usersLoadingMore} loading={usersLoadingMore} label={t('loadMoreUsers')} loadingLabel={t('loading')} />}
          </div>
        )}

        {/* OPERATORS TAB */}
        {!loading && activeTab === 'operators' && (
          <div>
            {!isManager && (
              <div className="mb-6 space-y-4">
                <div className={cn(ui.tableCard, 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between')}>
                  <p className={ui.tableTitle}>{t('managerSection')}</p>
                  <Button
                    type="button"
                    onClick={() => setManagerFormOpen((open) => !open)}
                    className="h-10 rounded-xl border border-de-gold/35 bg-de-gold/15 px-4 text-sm font-bold text-de-gold hover:bg-de-gold/25"
                  >
                    {t('managerAdd')}
                  </Button>
                </div>

                {managerFormOpen && (
                  <Card className="border-de-gold/25 bg-de-panel">
                    <CardHeader className="p-4 pb-2 sm:p-5">
                      <CardTitle className="text-base text-de-gold">{t('managerAdd')}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
                      <form onSubmit={handleCreateManager} className="grid gap-3 sm:grid-cols-2">
                        <label className="flex flex-col gap-1 text-xs text-de-muted">
                          {t('managerName')}
                          <input
                            required
                            value={managerForm.name}
                            onChange={(e) => setManagerForm((prev) => ({ ...prev, name: e.target.value }))}
                            className="min-h-10 rounded-lg border border-de-gold/25 bg-black/30 px-3 text-sm text-slate-100"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-de-muted">
                          {t('managerEmail')}
                          <input
                            required
                            type="email"
                            value={managerForm.email}
                            onChange={(e) => setManagerForm((prev) => ({ ...prev, email: e.target.value }))}
                            className="min-h-10 rounded-lg border border-de-gold/25 bg-black/30 px-3 text-sm text-slate-100"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-de-muted">
                          {t('managerPassword')}
                          <input
                            required
                            type="password"
                            minLength={8}
                            value={managerForm.password}
                            onChange={(e) => setManagerForm((prev) => ({ ...prev, password: e.target.value }))}
                            className="min-h-10 rounded-lg border border-de-gold/25 bg-black/30 px-3 text-sm text-slate-100"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs text-de-muted">
                          {t('operatorsColPhone')}
                          <input
                            value={managerForm.phone}
                            onChange={(e) => setManagerForm((prev) => ({ ...prev, phone: e.target.value }))}
                            className="min-h-10 rounded-lg border border-de-gold/25 bg-black/30 px-3 text-sm text-slate-100"
                          />
                        </label>
                        <div className="sm:col-span-2">
                          <Button
                            type="submit"
                            disabled={managerSaving}
                            className="h-10 rounded-xl border border-de-gold/35 bg-de-gold/15 px-4 text-sm font-bold text-de-gold hover:bg-de-gold/25 disabled:opacity-60"
                          >
                            {managerSaving ? t('loading') : t('managerAdd')}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                )}

                <TablePanel
                  title={`${t('managerSection')} (${managers.length})`}
                  preferCards={preferCompactTables}
                  desktop={
                    managersLoading ? (
                      <p className={ui.empty}>{t('loading')}</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {[t('managerName'), t('managerEmail'), t('managerLoginUrl'), t('operatorsColPhone'), t('operatorsColStatus'), t('deactivateBtn')].map((h) => (
                              <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {managers.length > 0 ? managers.map((m) => (
                            <TableRow key={m.id}>
                              <TableCell className="font-semibold text-de-gold">{m.name}</TableCell>
                              <TableCell>{m.email}</TableCell>
                              <TableCell>
                                <a
                                  href="https://app.developmentexpress.in/login/admin"
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-de-gold underline-offset-2 hover:underline"
                                >
                                  {t('managerLoginUrl')}
                                </a>
                              </TableCell>
                              <PhoneTableCell user={m} />
                              <TableCell>{userStatusBadge(m.status, t)}</TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {m.status === 'active' && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 px-2 text-[10px] border-red-500/50 text-red-400 hover:bg-red-500/10"
                                      onClick={() => handleDeactivateManager(m.id)}
                                    >
                                      {t('deactivateBtn')}
                                    </Button>
                                  )}
                                  {m.status === 'inactive' && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 px-2 text-[10px] border-green-500/50 text-green-400 hover:bg-green-500/10"
                                      onClick={() => handleReactivateManager(m.id)}
                                    >
                                      {t('reactivateBtn')}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )) : (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center text-de-muted">{t('operatorsNoResults')}</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    )
                  }
                  mobile={managersLoading ? (
                    <p className={ui.empty}>{t('loading')}</p>
                  ) : managers.length > 0 ? managers.map((m) => (
                    <Card key={m.id} className={ui.mobileCard}>
                      <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                        <CardTitle className="text-sm text-de-gold">{m.name}</CardTitle>
                        {userStatusBadge(m.status, t)}
                      </CardHeader>
                      <CardContent className={ui.mobileGrid2}>
                        <MobileField label={t('managerEmail')} value={m.email} className="col-span-2" />
                        <a
                          href="https://app.developmentexpress.in/login/admin"
                          target="_blank"
                          rel="noreferrer"
                          className="col-span-2 text-sm font-semibold text-de-gold underline-offset-2 hover:underline"
                        >
                          {t('managerLoginUrl')}
                        </a>
                        <MobilePhoneField user={m} className="col-span-2" />
                        <div className="col-span-2 flex flex-wrap items-center gap-2 pt-1">
                          {m.status === 'active' && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDeactivateManager(m.id)}
                            >
                              {t('deactivateBtn')}
                            </Button>
                          )}
                          {m.status === 'inactive' && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-3 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                              onClick={() => handleReactivateManager(m.id)}
                            >
                              {t('reactivateBtn')}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )) : <p className={ui.empty}>{t('operatorsNoResults')}</p>}
                />

                <div className="mt-6 border-b border-de-gold/25 pb-4">
                  <p className="mb-3 font-semibold text-de-gold">{t('managerActivity')}</p>
                  {managers.map((m) => {
                    const isOpen = Boolean(managerActivityOpen[m.id]);
                    const items = managerActivityData[m.id] || [];
                    const isLoading = Boolean(managerActivityLoading[m.id]);
                    return (
                      <div
                        key={m.id}
                        className="mb-3 overflow-hidden rounded-xl border border-de-gold/25 bg-[#0a1628]"
                      >
                        <button
                          type="button"
                          className="flex w-full items-center justify-between px-4 py-3 text-left"
                          onClick={() => {
                            const nextOpen = !isOpen;
                            setManagerActivityOpen((prev) => ({ ...prev, [m.id]: nextOpen }));
                            if (nextOpen) loadManagerActivity(m.id);
                          }}
                        >
                          <span className="min-w-0">
                            <span className="block font-medium text-white">{m.name || '—'}</span>
                            <span className="block text-sm text-de-muted">{m.email || '—'}</span>
                          </span>
                          <span className="shrink-0 text-xs font-bold text-de-gold">
                            {isOpen ? '\u25BC' : '\u25B6'}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="border-t border-de-gold/15">
                            {isLoading ? (
                              <div className="flex justify-center py-6">
                                <span
                                  className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-de-gold/30 border-t-de-gold"
                                  aria-label={t('loading')}
                                />
                              </div>
                            ) : items.length === 0 ? (
                              <p className="py-4 text-center text-sm text-de-muted">{t('managerActivityEmpty')}</p>
                            ) : (
                              <>
                                {items.map((log) => {
                                  const rawAction = String(log.action || '').replace(/_/g, ' ');
                                  const actionLabel = rawAction
                                    ? rawAction.charAt(0).toUpperCase() + rawAction.slice(1)
                                    : '—';
                                  const entityId = String(log.entity_id || '');
                                  const ts = log.created_at
                                    ? new Date(log.created_at).toLocaleString('en-IN', {
                                      timeZone: 'Asia/Kolkata',
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false,
                                    })
                                    : '—';
                                  return (
                                    <div
                                      key={log.id || `${log.action}-${log.created_at}`}
                                      className="flex items-start justify-between border-b border-de-gold/10 px-4 py-2"
                                    >
                                      <div className="min-w-0 pr-3">
                                        <p className="text-sm text-white">
                                          {actionLabel}
                                          {log.metadata?.admin_scope === 'manager' && (
                                            <span className="ml-1 inline-block rounded-full border border-de-gold/40 bg-de-gold/15 px-2 py-0.5 text-[10px] font-semibold text-de-gold">
                                              Manager
                                            </span>
                                          )}
                                        </p>
                                        <p className="text-xs text-de-muted">
                                          {log.entity_type || '—'}
                                          {' · '}
                                          {entityId ? entityId.slice(0, 8) : '—'}
                                        </p>
                                      </div>
                                      <span className="shrink-0 text-xs text-de-muted">{ts}</span>
                                    </div>
                                  );
                                })}
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="mx-4 mb-3 mt-2 h-8 rounded-lg border border-de-gold/50 bg-[rgba(201,168,76,0.12)] px-3 text-sm font-semibold text-de-gold hover:bg-de-gold/20"
                                  onClick={() => {
                                    setAuditActorFilter(m.id);
                                    setActiveTab('audit');
                                  }}
                                >
                                  {t('managerActivityViewAll')}
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Dialog open={managerCredentials !== null} onOpenChange={() => {}}>
                  <DialogContent className="max-w-md border-de-gold/30 bg-de-panel text-slate-200">
                    <DialogHeader>
                      <DialogTitle className="text-de-gold">{t('managerCredentialsTitle')}</DialogTitle>
                      <DialogDescription className="text-xs text-de-muted">
                        {t('managerCredentialsSubtitle')}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-3 py-2">
                      {[
                        { label: t('managerLoginUrl'), value: managerCredentials?.loginUrl },
                        { label: t('managerEmail'), value: managerCredentials?.email },
                        { label: t('managerPassword'), value: managerCredentials?.password },
                      ].map((row) => (
                        <div key={row.label} className="flex flex-col gap-1">
                          <span className="text-xs text-de-muted">{row.label}</span>
                          <div className="flex items-center gap-2">
                            <span className="min-w-0 flex-1 break-all font-mono text-sm text-slate-100">{row.value}</span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 shrink-0 rounded-lg border border-de-gold/50 bg-[rgba(201,168,76,0.12)] px-3 text-[13px] font-semibold text-de-gold hover:bg-de-gold/20"
                              onClick={() => copyManagerCredential(row.value)}
                            >
                              Copy
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        onClick={closeManagerCredentials}
                        className="h-10 w-full rounded-xl border border-de-gold/35 bg-gradient-to-br from-[#c9a84c] to-[#8b6914] px-4 text-sm font-bold text-[#0a1628] hover:opacity-95"
                      >
                        {t('managerCredentialsSaved')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
            <AdminOperatorKycPanel
              authToken={localStorage.getItem('developmentexpress_token') || ''}
              onQueueChange={refreshPendingKycCounts}
            />
            <TablePanel
              title={`${String.fromCodePoint(0x1F527)} ${t('operatorsAllTitle').replace('{{count}}', operatorUsers.length)}`}
              preferCards={preferCompactTables}
              desktop={
                <Table>
                  <TableHeader>
                    <TableRow>{['operatorsColOperator', 'operatorsColEmail', 'operatorsColPhone', 'operatorsColDocuments', 'operatorsColStatus'].map((key) => <TableHead key={key} className="text-de-gold/70">{t(key)}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {operatorUsers.length > 0 ? operatorUsers.map((o, i) => {
                      const operatorDocs = getOperatorKycDocs(o, t);
                      return (
                      <TableRow key={i}>
                        <TableCell className="font-semibold text-de-gold">{o.name}</TableCell>
                        <TableCell>{formatOperatorEmailDisplay(o.email)}</TableCell>
                        <PhoneTableCell user={o} />
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {operatorDocs.length > 0 ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-[10px]"
                                onClick={() => setOperatorDocsTarget({ id: o.id, name: o.name, docs: operatorDocs })}
                              >
                                {t('operatorsDocumentsBtn').replace('{{count}}', operatorDocs.length)}
                              </Button>
                            ) : '-'}
                            {o.status === 'active' && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-[10px] border-red-500/50 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeactivateUser(o.id)}
                              >
                                {t('deactivateBtn')}
                              </Button>
                            )}
                            {o.status === 'inactive' && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-[10px] border-green-500/50 text-green-400 hover:bg-green-500/10"
                                onClick={() => handleReactivateUser(o.id)}
                              >
                                {t('reactivateBtn')}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{userStatusBadge(o.status, t)}</TableCell>
                      </TableRow>
                      );
                    }) : (
                      <TableRow><TableCell colSpan={5} className="text-center text-de-muted">{t('operatorsNoResults')}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              }
              mobile={operatorUsers.length > 0 ? operatorUsers.map((o, i) => {
                const operatorDocs = getOperatorKycDocs(o, t);
                return (
                <Card key={i} className={ui.mobileCard}>
                  <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                    <CardTitle className="text-sm text-de-gold">{o.name}</CardTitle>
                    {userStatusBadge(o.status, t)}
                  </CardHeader>
                  <CardContent className={ui.mobileGrid2}>
                    <MobileField label={t('operatorsColEmail')} value={formatOperatorEmailDisplay(o.email)} className="col-span-2" />
                    <MobilePhoneField user={o} className="col-span-2" />
                    <div className="col-span-2 flex flex-wrap items-center gap-2 pt-1">
                      {operatorDocs.length > 0 ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 text-xs"
                          onClick={() => setOperatorDocsTarget({ id: o.id, name: o.name, docs: operatorDocs })}
                        >
                          {t('operatorsDocumentsBtn').replace('{{count}}', operatorDocs.length)}
                        </Button>
                      ) : null}
                      {o.status === 'active' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleDeactivateUser(o.id)}
                        >
                          {t('deactivateBtn')}
                        </Button>
                      )}
                      {o.status === 'inactive' && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 px-3 text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                          onClick={() => handleReactivateUser(o.id)}
                        >
                          {t('reactivateBtn')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              }) : <p className={ui.empty}>{t('operatorsNoResults')}</p>}
            />
            <Dialog open={Boolean(operatorDocsTarget)} onOpenChange={(open) => { if (!open) setOperatorDocsTarget(null); }}>
              <DialogContent className="max-w-sm border-de-gold/30 bg-de-panel text-slate-200">
                <DialogHeader>
                  <DialogTitle className="text-de-gold">{operatorDocsTarget?.name}</DialogTitle>
                  <DialogDescription>{t('operatorsKycDialogDesc')}</DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-2 py-2">
                  {(operatorDocsTarget?.docs || []).map((doc) => (
                    <Button
                      key={doc.type}
                      type="button"
                      variant="outline"
                      className="h-9 justify-start border-de-gold/30 text-de-gold hover:bg-de-gold/10"
                      onClick={() => {
                        handleViewKyc(operatorDocsTarget.id, doc.type);
                        setOperatorDocsTarget(null);
                      }}
                    >
                      {doc.label}
                    </Button>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
            {usersHasMore && <LoadMoreButton onClick={loadMoreUsers} disabled={usersLoadingMore} loading={usersLoadingMore} label={t('loadMoreUsers')} loadingLabel={t('loading')} />}
          </div>
        )}

        {/* WALLET TAB */}
        {!loading && activeTab === 'wallet' && !isManager && (
          <div className="de-wallet-ultra-scroll space-y-0 pb-2">
            <TreasuryWalletPanel key={walletPanelKey} />
            <PendingSettlements onRetryDone={refreshWalletPanels} />

            <div className={cn('grid items-start gap-4', pick('grid-cols-1', 'grid-cols-1', 'grid-cols-2'))}>
              <ClientRefundForm bookings={refundableBookings} onRefunded={refreshWalletPanels} />
              <OwnerPayoutForm owners={ownerUsers} onPaid={refreshWalletPanels} />
            </div>

            <div className={cn(ui.cardRow, 'sm:grid-cols-2')}>
              {[
                { icon: String.fromCodePoint(0x1F4B0), val: formatRsCompact(totalRevenue), label: t('totalBilledMonth') },
                { icon: String.fromCodePoint(0x2705), val: formatRsCompact(walletMonthStats.collected), label: t('totalCollected') },
                { icon: String.fromCodePoint(0x23F3), val: formatRsCompact(walletMonthStats.pending), label: t('pendingLabel') },
                { icon: String.fromCodePoint(0x1F4B8), val: commission > 0 ? `₹${commission.toLocaleString('en-IN')}` : '₹0', label: t('commissionEarned') },
              ].map((c, i) => (
                <PremiumStatCard key={i} icon={c.icon} value={c.val} label={c.label} />
              ))}
            </div>
            <Card className="mb-4 border-de-gold/25 bg-de-panel">
              <CardHeader className="p-4 pb-2 sm:p-5">
                <CardTitle className="text-base text-de-gold">{t('reconciliationSnapshot')}</CardTitle>
              </CardHeader>
              <CardContent className={cn('grid gap-2.5 p-4 pt-0 sm:p-5 sm:pt-0', pick('grid-cols-2', 'grid-cols-3', 'grid-cols-5'))}>
                {[
                  { label: t('creditsLabel'), value: reconciliationStats.creditCount },
                  { label: t('debitsLabel'), value: reconciliationStats.debitCount },
                  { label: t('missingRefLabel'), value: reconciliationStats.missingRefCount },
                  { label: t('duplicateRefLabel'), value: reconciliationStats.duplicateRefCount },
                  { label: t('txnVolumeLabel'), value: `₹${reconciliationStats.totalVolume.toLocaleString('en-IN')}` },
                ].map((item) => (
                  <StatMini key={item.label} label={item.label} value={item.value} />
                ))}
              </CardContent>
            </Card>
            <TablePanel
              title={`${String.fromCodePoint(0x1F4B3)} Client Wallet Status`}
              preferCards={preferCompactTables}
              desktop={
                <Table>
                  <TableHeader>
                    <TableRow>{['Client', 'Email', 'Mobile', 'Bookings', 'Wallet', 'Status'].map((h) => <TableHead key={h} className="text-de-gold/70">{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientUsers.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-semibold text-de-gold">{c.name}</TableCell>
                        <TableCell>{c.email}</TableCell>
                        <PhoneTableCell user={c} />
                        <TableCell>{bookingData.filter((b) => b.client_id === c.id).length}</TableCell>
                        <TableCell className="font-bold tabular-nums text-emerald-400">₹{(c.wallet_balance || 0).toLocaleString('en-IN')}</TableCell>
                        <TableCell>{userStatusBadge(c.status, t)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              }
              mobile={clientUsers.map((c, i) => (
                <Card key={i} className={ui.mobileCard}>
                  <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                    <CardTitle className="text-sm text-de-gold">{c.name}</CardTitle>
                    {userStatusBadge(c.status, t)}
                  </CardHeader>
                  <CardContent className={ui.mobileGrid2}>
                    <MobileField label="Email" value={c.email} className="col-span-2" />
                    <MobilePhoneField user={c} className="col-span-2" />
                    <MobileField label="Bookings" value={bookingData.filter((b) => b.client_id === c.id).length} />
                    <MobileField label="Wallet" value={`₹${(c.wallet_balance || 0).toLocaleString('en-IN')}`} />
                  </CardContent>
                </Card>
              ))}
            />
          </div>
        )}

                {/* APPROVALS TAB */}
        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between')}>
            <p className={ui.tableTitle}>Client invite codes</p>
            <Button
              type="button"
              onClick={handleGenerateInviteCode}
              disabled={inviteGenerating}
              className="h-10 rounded-xl border border-de-gold/35 bg-de-gold/15 px-4 text-sm font-bold text-de-gold hover:bg-de-gold/25 disabled:opacity-60"
            >
              {inviteGenerating ? 'Generating...' : 'Generate Invite Code'}
            </Button>
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <>
            <h3 style={{
              color: '#C9A84C',
              fontSize: 16,
              fontWeight: 700,
              margin: '0 0 16px',
            }}
            >
              Pending Approvals
            </h3>
            <TablePanel
            title={`${String.fromCodePoint(0x23F3)} Pending Client Approvals (${pendingUsers.length})`}
            preferCards={preferCompactTables}
            desktop={
              pendingUsers.length === 0 ? (
                <p className={ui.empty}>No pending approvals!</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>{['Name', 'Role', 'Company', 'Email', 'Mobile', 'GSTIN', 'PAN', 'Date', 'Action'].map((h) => <TableHead key={h} className="text-de-gold/70">{h}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map((u, i) => (
                      <TableRow key={i}>
                        <TableCell>{u.name}</TableCell>
                        <TableCell>
                          <span style={{
                            fontSize: 10,
                            padding: '2px 8px',
                            borderRadius: 20,
                            background: u.role === 'owner'
                              ? 'rgba(201,168,76,0.15)'
                              : u.role === 'operator'
                                ? 'rgba(76,175,80,0.15)'
                                : 'rgba(100,149,237,0.15)',
                            color: u.role === 'owner'
                              ? '#C9A84C'
                              : u.role === 'operator'
                                ? '#4CAF50'
                                : '#6495ED',
                          }}
                          >
                            {u.role || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{u.company || '-'}</TableCell>
                        <TableCell>{formatSyntheticEmailDisplay(u.email)}</TableCell>
                        <PhoneTableCell user={u} />
                        <TableCell>
                          <div>
                            <p style={{
                              margin: 0,
                              fontSize: 11,
                              color: '#8896a8',
                            }}
                            >
                              {u.gstin || 'No GSTIN'}
                              {u.gst_verified && (
                                <span style={{
                                  color: '#4CAF50',
                                  marginLeft: 4,
                                  fontSize: 10,
                                }}
                                >
                                  ✅ Verified
                                </span>
                              )}
                              {u.gstin && !u.gst_verified && (
                                <span style={{
                                  color: '#FF9800',
                                  marginLeft: 4,
                                  fontSize: 10,
                                }}
                                >
                                  ⏳ Pending
                                </span>
                              )}
                            </p>
                            {u.gst_legal_name && (
                              <p style={{
                                margin: '2px 0 0',
                                fontSize: 10,
                                color: '#4CAF50',
                                fontStyle: 'italic',
                              }}
                              >
                                {u.gst_legal_name}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {u.pan_document_path ? (
                              <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-[10px]" onClick={() => handleViewKyc(u.id, 'pan')}>View</Button>
                            ) : null}
                            {(() => {
                              const panStatus = formatPendingUserPanStatus(u);
                              if (!panStatus) return null;
                              return (
                                <span style={{
                                  fontSize: 10,
                                  color: panStatus.color,
                                  display: 'block',
                                  marginTop: 2,
                                }}
                                >
                                  {panStatus.label}
                                </span>
                              );
                            })()}
                            {u.license_document_path ? (
                              <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-[10px]" onClick={() => handleViewKyc(u.id, 'license')}>License</Button>
                            ) : null}
                            {u.aadhaar_document_path ? (
                              <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-[10px]" onClick={() => handleViewKyc(u.id, 'aadhaar')}>Aadhaar</Button>
                            ) : null}
                            {u.aadhaar_front_path ? (
                              <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-[10px]" onClick={() => handleViewKyc(u.id, 'aadhaar_front')}>Aadhaar Front</Button>
                            ) : null}
                            {u.aadhaar_back_path ? (
                              <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-[10px]" onClick={() => handleViewKyc(u.id, 'aadhaar_back')}>Aadhaar Back</Button>
                            ) : null}
                            {u.gst_certificate_path ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-[10px]"
                                onClick={() => handleViewKyc(
                                  u.id, 'gst_certificate',
                                )}
                              >
                                GST Cert
                              </Button>
                            ) : null}
                            {!u.pan_document_path && !u.license_document_path && !u.aadhaar_document_path ? '-' : null}
                          </div>
                        </TableCell>
                        <TableCell>{new Date(u.created_at).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-[10px]"
                              onClick={() => generateServiceAgreement(u)}
                            >
                              📄 Agreement
                            </Button>
                            <Button type="button" size="sm" variant="outline" className={ui.btnApprove} onClick={() => handleApproveUser(u.id)}>Approve</Button>
                            <Button type="button" size="sm" variant="outline" className={ui.btnReject} onClick={() => handleRejectUser(u.id)}>Reject</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            }
            mobile={
              pendingUsers.length === 0 ? (
                <p className={ui.empty}>No pending approvals!</p>
              ) : (
                pendingUsers.map((u, i) => (
                  <Card key={i} className={ui.mobileCard}>
                    <CardHeader className="p-4 pb-2">
                      <CardTitle className="text-sm text-de-gold">{u.name}</CardTitle>
                      <CardDescription>{u.company || '—'}</CardDescription>
                      <span style={{
                        fontSize: 10,
                        padding: '2px 8px',
                        borderRadius: 20,
                        background: u.role === 'owner'
                          ? 'rgba(201,168,76,0.15)'
                          : u.role === 'operator'
                            ? 'rgba(76,175,80,0.15)'
                            : 'rgba(100,149,237,0.15)',
                        color: u.role === 'owner'
                          ? '#C9A84C'
                          : u.role === 'operator'
                            ? '#4CAF50'
                            : '#6495ED',
                        display: 'inline-block',
                        marginTop: 6,
                      }}
                      >
                        {u.role || '-'}
                      </span>
                    </CardHeader>
                    <CardContent className={cn(ui.mobileGrid2, 'gap-3')}>
                      <MobileField label="Email" value={formatSyntheticEmailDisplay(u.email)} className="col-span-2" />
                      <MobilePhoneField user={u} className="col-span-2" />
                      <div className="col-span-2">
                        <p style={{ margin: 0, fontSize: 11, color: '#8896a8' }}>GSTIN</p>
                        <p style={{ margin: '2px 0 0', fontSize: 13, color: '#e8e0d0' }}>
                          {u.gstin || '-'}
                          {u.gst_verified && (
                            <span style={{
                              color: '#4CAF50',
                              marginLeft: 4,
                              fontSize: 10,
                            }}
                            >
                              ✅ Verified
                            </span>
                          )}
                          {u.gstin && !u.gst_verified && (
                            <span style={{
                              color: '#FF9800',
                              marginLeft: 4,
                              fontSize: 10,
                            }}
                            >
                              ⏳ Pending
                            </span>
                          )}
                        </p>
                        {u.gst_legal_name && (
                          <p style={{
                            margin: '2px 0 0',
                            fontSize: 10,
                            color: '#4CAF50',
                            fontStyle: 'italic',
                          }}
                          >
                            {u.gst_legal_name}
                          </p>
                        )}
                      </div>
                      <MobileField label="Date" value={new Date(u.created_at).toLocaleDateString('en-IN')} />
                      {u.pan_document_path && (
                        <div className="col-span-2">
                          <Button type="button" size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={() => handleViewKyc(u.id, 'pan')}>View PAN</Button>
                        </div>
                      )}
                      {(() => {
                        const panStatus = formatPendingUserPanStatus(u);
                        if (!panStatus) return null;
                        return (
                          <p style={{
                            fontSize: 10,
                            color: panStatus.color,
                            margin: '2px 0 0',
                          }}
                          >
                            {panStatus.label}
                          </p>
                        );
                      })()}
                      {u.license_document_path && (
                        <div className="col-span-2">
                          <Button type="button" size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={() => handleViewKyc(u.id, 'license')}>License</Button>
                        </div>
                      )}
                      {u.aadhaar_document_path && (
                        <div className="col-span-2">
                          <Button type="button" size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={() => handleViewKyc(u.id, 'aadhaar')}>Aadhaar</Button>
                        </div>
                      )}
                      {u.aadhaar_front_path && (
                        <div className="col-span-2">
                          <Button type="button" size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={() => handleViewKyc(u.id, 'aadhaar_front')}>Aadhaar Front</Button>
                        </div>
                      )}
                      {u.aadhaar_back_path && (
                        <div className="col-span-2">
                          <Button type="button" size="sm" variant="outline" className="h-8 px-3 text-xs" onClick={() => handleViewKyc(u.id, 'aadhaar_back')}>Aadhaar Back</Button>
                        </div>
                      )}
                      {u.gst_certificate_path && (
                        <div className="col-span-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 px-3 text-xs"
                            onClick={() => handleViewKyc(
                              u.id, 'gst_certificate',
                            )}
                          >
                            GST Certificate
                          </Button>
                        </div>
                      )}
                      <div className="col-span-2 flex gap-2 pt-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => generateServiceAgreement(u)}
                        >
                          📄 Agreement
                        </Button>
                        <Button type="button" size="sm" variant="outline" className={cn(ui.btnApprove, 'flex-1')} onClick={() => handleApproveUser(u.id)}>Approve</Button>
                        <Button type="button" size="sm" variant="outline" className={cn(ui.btnReject, 'flex-1')} onClick={() => handleRejectUser(u.id)}>Reject</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )
            }
          />
          </>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <TablePanel
              title={`${String.fromCodePoint(0x274C)} Rejected Registrations (${rejectedUsers.length})`}
              preferCards={preferCompactTables}
              desktop={
                rejectedUsers.length === 0 ? (
                  <p className={ui.empty}>No rejected registrations.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {['Name', 'Role', 'Phone', 'Email', 'Reason', 'Action'].map((h) => (
                          <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rejectedUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>{u.name}</TableCell>
                          <TableCell>{u.role || '—'}</TableCell>
                          <TableCell>{u.phone || '—'}</TableCell>
                          <TableCell>{formatSyntheticEmailDisplay(u.email)}</TableCell>
                          <TableCell className="max-w-[220px] text-xs text-red-300">
                            {u.registration_rejection_reason || '—'}
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 px-2 text-[10px]"
                              onClick={() => handleAllowResubmission(u)}
                            >
                              Allow resubmission
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )
              }
              mobile={
                rejectedUsers.length === 0 ? (
                  <p className={ui.empty}>No rejected registrations.</p>
                ) : (
                  rejectedUsers.map((u) => (
                    <Card key={u.id} className={ui.mobileCard}>
                      <CardHeader className="p-4 pb-2">
                        <CardTitle className="text-sm text-de-gold">{u.name}</CardTitle>
                        <CardDescription>{u.role || '—'} · {u.phone || '—'}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2 p-4 pt-0">
                        <MobileField label="Email" value={formatSyntheticEmailDisplay(u.email)} />
                        <p className="text-xs text-red-300">
                          Reason: {u.registration_rejection_reason || '—'}
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="w-full text-xs"
                          onClick={() => handleAllowResubmission(u)}
                        >
                          Allow resubmission
                        </Button>
                      </CardContent>
                    </Card>
                  ))
                )
              }
            />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>{String.fromCodePoint(0x1F69C)} Pending Machine Registrations</p>
            <MachineRegistrationApprovals
              onQueueChange={setPendingMachineRegistrations}
              onApproved={handleMachineRegistrationApproved}
            />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>{String.fromCodePoint(0x1F4DD)} Quotation Exceptions</p>
            <QuotationExceptionsApprovals onQueueChange={setPendingQuotationExceptions} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>Contract Terminations</p>
            <ContractTerminationApprovals onQueueChange={setPendingContractTerminations} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>Legal Notices</p>
            <LegalNoticeApprovals onQueueChange={setPendingLegalNotices} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>Owner Defaults</p>
            <OwnerDefaultApprovals onQueueChange={setPendingOwnerDefaults} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && !isManager && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>{String.fromCodePoint(0x1F3E6)} Pending NEFT/IMPS Wallet Top-ups</p>
            <BankTopupApprovals onQueueChange={setPendingBankTopups} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && !isManager && (
          <div className="mt-4 space-y-4">
            <DualApprovals onQueueChange={setPendingDualApprovals} />
            <WithdrawApprovals onQueueChange={setPendingWithdraws} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>{String.fromCodePoint(0x1F4F1)} Pending Phone Change Requests</p>
            <PhoneChangeApprovalPanel
              authToken={localStorage.getItem('developmentexpress_token') || ''}
              onQueueChange={refreshPendingPhoneChangeCount}
            />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>{String.fromCodePoint(0x1F512)} Active User Sessions</p>
            <AdminSessionPanel authToken={localStorage.getItem('developmentexpress_token') || ''} />
          </div>
        )}

        {!loading && activeTab === 'approvals' && (
          <div className={cn(ui.tableCard, 'mt-4')}>
            <p className={cn(ui.tableTitle, 'mb-3')}>{String.fromCodePoint(0x1F510)} Security settings — Setup 2FA</p>
            <AdminTotpSetupPanel authToken={localStorage.getItem('developmentexpress_token') || ''} />
          </div>
        )}

        {!loading && activeTab === 'dlq' && (
          <div className={ui.tableCard}>
            <p className={cn(ui.tableTitle, 'mb-0')}>Queue items · {dlqData.length}</p>
            {dlqMessage && (
              <div style={{ background: dlqMessage.toLowerCase().includes('error') ? 'rgba(233,69,96,0.14)' : 'rgba(76,175,80,0.12)', border: dlqMessage.toLowerCase().includes('error') ? '1px solid rgba(233,69,96,0.45)' : '1px solid rgba(76,175,80,0.4)', color: dlqMessage.toLowerCase().includes('error') ? '#ff9aa8' : '#4CAF50', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', fontSize: '12px' }}>
                {dlqMessage}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 3, 4), gap: '10px', marginBottom: '14px' }}>
              {[
                { label: '24h Total', val: dlqStats.totalLast24h || 0 },
                { label: '24h Failed', val: dlqStats.failedLast24h || 0 },
                { label: '24h Retried', val: dlqStats.retriedLast24h || 0 },
                { label: '24h Peak/Hour', val: Math.max(0, ...(dlqStats.hourly || []).map((x) => x.count || 0)) },
              ].map((item) => (
                <div key={item.label} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{item.label}</p>
                  <p style={{ color: '#c9a84c', fontWeight: '700', margin: 0 }}>{item.val}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 4, 6), gap: '10px', marginBottom: '14px' }}>
              {[
                { label: t('dlqTotal'), val: dlqCounters.total || 0 },
                { label: t('dlqFailed'), val: dlqCounters.failed || 0 },
                { label: t('dlqRetried'), val: dlqCounters.retried || 0 },
                { label: t('dlqWhatsapp'), val: dlqCounters.whatsapp || 0 },
                { label: t('dlqEmail'), val: dlqCounters.email || 0 },
                { label: t('dlqAlert'), val: dlqCounters.alert || 0 },
              ].map((item) => (
                <div key={item.label} style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                  <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 4px' }}>{item.label}</p>
                  <p style={{ color: '#c9a84c', fontWeight: '700', margin: 0 }}>{item.val}</p>
                </div>
              ))}
            </div>
            <div style={{ position: 'relative', display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', padding: '12px', border: '1px solid rgba(245,216,138,0.28)', borderRadius: '12px', background: 'radial-gradient(circle at 8% -45%, rgba(245,216,138,0.2), transparent 34%), linear-gradient(168deg, rgba(20,36,58,0.8), rgba(7,14,25,0.9))' }}>
              <div style={{ position: 'relative', minWidth: '170px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsDlqQueueOpen((v) => !v);
                    setIsDlqStatusOpen(false);
                  }}
                  style={{ ...auditCmdInputStyle, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                >
                  <span>{dlqQueueFilter || 'All Queues'}</span>
                  <span style={{ color: '#f5d88a', fontSize: '11px', transform: isDlqQueueOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }} aria-hidden>{String.fromCodePoint(0x25BC)}</span>
                </button>
                {isDlqQueueOpen && (
                  <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 40, borderRadius: '10px', border: '1px solid rgba(201,168,76,0.42)', background: 'linear-gradient(165deg, rgba(12,24,40,0.98), rgba(6,12,22,0.99))', boxShadow: '0 14px 30px rgba(0,0,0,0.42)' }}>
                    {['', 'whatsapp', 'email', 'alert', 'pdf'].map((queue) => (
                      <button
                        key={queue || 'all'}
                        type="button"
                        onClick={() => {
                          setDlqQueueFilter(queue);
                          setIsDlqQueueOpen(false);
                        }}
                        style={{ width: '100%', textAlign: 'left', border: 'none', background: dlqQueueFilter === queue ? 'rgba(201,168,76,0.2)' : 'transparent', color: dlqQueueFilter === queue ? '#f7df9b' : '#d2d8e2', padding: '8px 10px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        {queue || 'All Queues'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ position: 'relative', minWidth: '160px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsDlqStatusOpen((v) => !v);
                    setIsDlqQueueOpen(false);
                  }}
                  style={{ ...auditCmdInputStyle, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                >
                  <span>{dlqStatusFilter || 'All Status'}</span>
                  <span style={{ color: '#f5d88a', fontSize: '11px', transform: isDlqStatusOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }} aria-hidden>{String.fromCodePoint(0x25BC)}</span>
                </button>
                {isDlqStatusOpen && (
                  <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 40, borderRadius: '10px', border: '1px solid rgba(201,168,76,0.42)', background: 'linear-gradient(165deg, rgba(12,24,40,0.98), rgba(6,12,22,0.99))', boxShadow: '0 14px 30px rgba(0,0,0,0.42)' }}>
                    {['', 'failed', 'retried'].map((status) => (
                      <button
                        key={status || 'all'}
                        type="button"
                        onClick={() => {
                          setDlqStatusFilter(status);
                          setIsDlqStatusOpen(false);
                        }}
                        style={{ width: '100%', textAlign: 'left', border: 'none', background: dlqStatusFilter === status ? 'rgba(201,168,76,0.2)' : 'transparent', color: dlqStatusFilter === status ? '#f7df9b' : '#d2d8e2', padding: '8px 10px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        {status || 'All Status'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 6px' }}>Last 24h Queue Volume</p>
              <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 4, 5), gap: '8px' }}>
                {Object.entries(dlqStats.queueCounts || {}).map(([queueName, count]) => (
                  <div key={queueName} style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                    <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 2px' }}>{queueName}</p>
                    <p style={{ color: '#c9a84c', fontWeight: '700', margin: 0 }}>{count}</p>
                  </div>
                ))}
              </div>
            </div>
            {dlqData.length === 0 ? (
              <p style={{ color: '#8896a8', textAlign: 'center', padding: '20px' }}>{t('noDlqItems')}</p>
            ) : (
              <div>
                {isMobile ? (
                  <div className="flex w-full min-w-0 flex-col gap-3">
                    {dlqData.map((item) => (
                      <Card key={item.id} className={ui.mobileCard}>
                        <CardHeader className="p-4 pb-2">
                          <CardTitle className="break-words text-sm text-de-gold">{item.queue_name}</CardTitle>
                          <CardDescription className="break-words">{item.job_name}</CardDescription>
                        </CardHeader>
                        <CardContent className={ui.mobileGrid2}>
                          <MobileField label="Error" value={item.error || '—'} className="col-span-2" />
                          <MobileField label="Created" value={item.created_at ? new Date(item.created_at).toLocaleString('en-IN') : '—'} className="col-span-2" />
                          <MobileField label="Retried By" value={item.retried_by || '—'} />
                          <MobileField label="Retried At" value={item.retried_at ? new Date(item.retried_at).toLocaleString('en-IN') : '—'} />
                          <div className="col-span-2 pt-1">
                            <button
                              type="button"
                              className="w-full min-h-10 rounded-md border border-emerald-500/50 bg-emerald-500/10 px-3 text-xs font-semibold text-emerald-400"
                              onClick={() => {
                                setSelectedDlqItem(item);
                                setDlqRetryReason('');
                              }}
                            >
                              {t('retryText')}
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                <table className={ui.table}>
                  <thead><tr>{['Queue', 'Job', 'Error', 'Created', 'Retried By', 'Retried At', 'Action'].map(h => <th key={h} className={ui.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {dlqData.map((item) => (
                      <tr key={item.id} className={ui.tr}>
                        <td className={ui.td}>{item.queue_name}</td>
                        <td className={ui.td}>{item.job_name}</td>
                        <td className={cn(ui.td, 'max-w-[280px] break-words')}>{item.error || '-'}</td>
                        <td className={ui.td}>{item.created_at ? new Date(item.created_at).toLocaleString('en-IN') : '-'}</td>
                        <td className={ui.td}>{item.retried_by || '-'}</td>
                        <td className={ui.td}>{item.retried_at ? new Date(item.retried_at).toLocaleString('en-IN') : '-'}</td>
                        <td className={ui.td}>
                          <button
                            style={{ background: 'rgba(76,175,80,0.1)', border: '1px solid #4CAF50', color: '#4CAF50', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
                            onClick={async () => {
                              setSelectedDlqItem(item);
                              setDlqRetryReason('');
                            }}
                          >
                            {t('retryText')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
                {dlqHasMore && <LoadMoreButton onClick={loadMoreDlq} disabled={dlqLoadingMore} loading={dlqLoadingMore} label={t('loadMoreDlq')} loadingLabel={t('loading')} />}
                <p className={ui.hint}>
                  {t('loadedDlqItems')}: {dlqData.length}{dlqHasMore ? ` (${t('moreAvailable')})` : ` (${t('allLoaded')})`}
                </p>
              </div>
            )}
          </div>
        )}

        <Dialog open={Boolean(selectedDlqItem)} onOpenChange={(open) => { if (!open) setSelectedDlqItem(null); }}>
          <DialogContent className="max-w-md border-de-gold/30 bg-de-panel text-slate-200">
            {selectedDlqItem && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-de-gold">{t('retryDlqItemTitle')} #{selectedDlqItem.id}</DialogTitle>
                  <DialogDescription>Queue: {selectedDlqItem.queue_name} | Job: {selectedDlqItem.job_name}</DialogDescription>
                </DialogHeader>
                <textarea
                  value={dlqRetryReason}
                  onChange={(e) => setDlqRetryReason(e.target.value)}
                  placeholder={t('retryReasonAuditPlaceholder')}
                  className="min-h-[88px] w-full rounded-lg border border-de-gold/25 bg-black/30 p-2 text-sm text-slate-200"
                />
                <p className="text-[11px] text-de-muted">Minimum 8 characters required.</p>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setSelectedDlqItem(null)}>Cancel</Button>
                  <Button
                    type="button"
                    className={cn('flex-1', ui.btnApprove)}
                    disabled={dlqRetryReason.trim().length < 8}
                    onClick={async () => {
                      const previous = [...dlqData];
                      const itemId = selectedDlqItem.id;
                      setDlqData((prev) => prev.filter((x) => x.id !== itemId));
                      setSelectedDlqItem(null);
                      const ok = await retryDlqItem(itemId, dlqRetryReason);
                      if (ok) {
                        setDlqMessage(`DLQ item ${itemId} retried successfully.`);
                      } else {
                        setDlqData(previous);
                        setDlqMessage(`${t('retryFailedForDlq')} ${itemId}.`);
                      }
                      setTimeout(() => setDlqMessage(''), 3000);
                    }}
                  >
                    {t('confirmRetry')}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {!loading && activeTab === 'audit' && (
          <div className={ui.tableCard}>
            <div style={{ border: '1px solid rgba(201,168,76,0.28)', borderRadius: '14px', padding: '12px', marginBottom: '12px', background: 'radial-gradient(circle at 12% -20%, rgba(201,168,76,0.2), transparent 38%), linear-gradient(160deg, rgba(12,24,40,0.95), rgba(5,10,20,0.96))' }}>
              <p style={{ color: '#8fa1b7', margin: 0, fontSize: '12px' }}>
                {t('auditIntroBanner')}
              </p>
              <p style={{ color: '#c9a84c', margin: '8px 0 0', fontSize: '11px', fontWeight: 600 }}>
                {`${visibleAuditLogs.length} ${t('auditLoaded')}`}
                {auditHasMore ? ` · ${t('auditMoreAvailable')}` : ` · ${t('auditEndOfList')}`}
                {auditActiveFilterCount > 0 ? ` · ${auditActiveFilterCount} ${auditActiveFilterCount > 1 ? t('auditFiltersActive') : t('auditFilterActive')}` : ''}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 3, 5), gap: '8px', marginBottom: '12px' }}>
              {[
                { label: t('auditStatTotal'), value: visibleAuditLogs.length, color: '#f7df9b' },
                { label: t('auditStatSecurity'), value: securityAuditCount, color: '#ff9aa8' },
                { label: t('auditStatActors'), value: uniqueAuditActors, color: '#8dd3ff' },
                { label: t('auditStatAdmin'), value: auditRoleCounts.admin, color: '#f6d98a' },
                { label: t('auditStatNonAdmin'), value: auditRoleCounts.owner + auditRoleCounts.client + auditRoleCounts.operator + auditRoleCounts.unknown, color: '#9fe3be' },
              ].map((item) => (
                <div key={item.label} style={{ background: 'linear-gradient(165deg, rgba(20,34,54,0.92), rgba(8,14,24,0.96))', border: '1px solid rgba(201,168,76,0.26)', borderRadius: '12px', padding: '10px 11px', boxShadow: '0 10px 26px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.05)' }}>
                  <p style={{ color: '#8896a8', margin: 0, fontSize: '10px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>{item.label}</p>
                  <p style={{ color: item.color, margin: '4px 0 0', fontSize: '20px', fontWeight: 800 }}>{item.value}</p>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: '12px', border: '1px solid rgba(201,168,76,0.24)', borderRadius: '12px', padding: '10px 12px', background: 'linear-gradient(168deg, rgba(18,30,48,0.84), rgba(7,14,24,0.9))' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: '#f1d793', fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px' }}>{t('auditSeverityBreakdown')}</span>
                <span style={{ color: '#8ea2b8', fontSize: '11px' }}>{t('auditPressSlash')}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: gridCols(2, 3, 4), gap: '8px' }}>
                {[
                  { key: 'Critical', color: '#e94560' },
                  { key: 'High', color: '#ff9800' },
                  { key: 'Medium', color: '#f2c94c' },
                  { key: 'Normal', color: '#4CAF50' },
                ].map((x) => {
                  const total = visibleAuditLogs.length;
                  const count = auditSeverityCounts[x.key] || 0;
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={x.key} style={{ border: '1px solid rgba(148,163,184,0.22)', borderRadius: '10px', padding: '7px 8px', background: 'rgba(8,15,26,0.55)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: x.color, fontSize: '11px', fontWeight: 700 }}>{({ Critical: t('auditSeverityCritical'), High: t('auditSeverityHigh'), Medium: t('auditSeverityMedium'), Normal: t('auditSeverityNormal') })[x.key] || x.key}</span>
                        <span style={{ color: '#d2d8e2', fontSize: '11px' }}>{auditSeverityCounts[x.key] || 0}</span>
                      </div>
                      <div style={{ height: '5px', borderRadius: '999px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: '999px', background: `linear-gradient(90deg, ${x.color}, rgba(255,255,255,0.75))` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {auditMessage && (
              <div style={{ background: auditMessage.toLowerCase().includes('error') ? 'rgba(233,69,96,0.14)' : 'rgba(76,175,80,0.12)', border: auditMessage.toLowerCase().includes('error') ? '1px solid rgba(233,69,96,0.45)' : '1px solid rgba(76,175,80,0.4)', color: auditMessage.toLowerCase().includes('error') ? '#ff9aa8' : '#4CAF50', borderRadius: '8px', padding: '8px 10px', marginBottom: '10px', fontSize: '12px' }}>
                {auditMessage}
              </div>
            )}
            <p style={{ color: '#f1d793', margin: '0 0 8px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px' }}>{t('auditFilters')}</p>
            <div style={{ position: 'relative', display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', padding: '14px', border: '1px solid rgba(245,216,138,0.34)', borderRadius: '14px', background: 'radial-gradient(circle at 12% -35%, rgba(245,216,138,0.26), transparent 36%), linear-gradient(168deg, rgba(20,36,58,0.86), rgba(7,14,25,0.94))', boxShadow: '0 16px 34px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -22px 26px rgba(0,0,0,0.28)' }}>
              <span aria-hidden style={{ position: 'absolute', top: 0, left: '4%', right: '4%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,230,160,0.9), transparent)', opacity: 0.85 }} />
              <span aria-hidden style={{ position: 'absolute', bottom: 0, left: '8%', right: '8%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.55), transparent)', opacity: 0.6 }} />
              <input
                ref={auditActionInputRef}
                value={auditActionFilter}
                onChange={(e) => setAuditActionFilter(e.target.value)}
                placeholder={t('auditFilterActionPlaceholder')}
                style={auditCmdInputStyle}
              />
              <input
                value={auditActorFilter}
                onChange={(e) => setAuditActorFilter(e.target.value)}
                placeholder={t('auditFilterActorPlaceholder')}
                style={auditCmdInputStyle}
              />
              <div ref={auditRoleWrapRef} style={{ position: 'relative', minWidth: isMobile ? 0 : '150px', width: isMobile ? '100%' : undefined, flex: isMobile ? '1 1 100%' : undefined }}>
                <button
                  type="button"
                  style={{ ...auditCmdInputStyle, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 0 0 1px rgba(0,0,0,0.25), 0 10px 20px rgba(0,0,0,0.28), 0 0 12px rgba(201,168,76,0.12)' }}
                  onClick={() => {
                    closeAuditDatePickers();
                    setIsAuditRoleOpen((v) => !v);
                  }}
                >
                  <span>{auditRoleLabel(auditRoleFilter)}</span>
                  <span style={{ color: '#f5d88a', fontSize: '11px' }}>{isAuditRoleOpen ? String.fromCodePoint(0x25B2) : String.fromCodePoint(0x25BC)}</span>
                </button>
                {isAuditRoleOpen && (
                  <div style={{ position: 'absolute', top: '110%', left: 0, right: 0, zIndex: 40, borderRadius: '10px', border: '1px solid rgba(201,168,76,0.4)', background: 'linear-gradient(165deg, rgba(12,24,40,0.98), rgba(6,12,22,0.99))', boxShadow: '0 14px 30px rgba(0,0,0,0.42)' }}>
                    {['', 'admin', 'owner', 'client', 'operator', 'manager'].map((role) => (
                      <button
                        key={role || 'all'}
                        type="button"
                        onClick={() => {
                          setAuditRoleFilter(role);
                          setAuditManagerFilter('');
                          setAuditActorFilter('');
                          if (role === 'manager' && !managers.length && !managersLoading) loadManagers();
                          setIsAuditRoleOpen(false);
                        }}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: auditRoleFilter === role ? 'rgba(201,168,76,0.2)' : 'transparent',
                          color: auditRoleFilter === role ? '#f7df9b' : '#d2d8e2',
                          padding: '8px 10px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        {auditRoleLabel(role)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {auditRoleFilter === 'manager' && (
                managersLoading ? (
                  <span
                    className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-de-gold/30 border-t-de-gold"
                    style={{ alignSelf: 'center' }}
                    aria-label={t('loading')}
                  />
                ) : (
                  <select
                    value={auditManagerFilter}
                    onChange={(e) => {
                      const next = e.target.value;
                      setAuditManagerFilter(next);
                      setAuditActorFilter(next);
                    }}
                    onFocus={() => {
                      if (!managers.length && !managersLoading) loadManagers();
                    }}
                    style={{
                      ...auditCmdInputStyle,
                      background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                      border: '1px solid rgba(201,168,76,0.3)',
                      borderRadius: '8px',
                      color: '#e8e0d0',
                      fontSize: '13px',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      outline: 'none',
                      boxSizing: 'border-box',
                      minWidth: isMobile ? '100%' : '200px',
                    }}
                  >
                    <option value="">All managers</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {`${m.name || '—'} · ${m.email || '—'}`}
                      </option>
                    ))}
                  </select>
                )
              )}
              <input
                value={auditEntityFilter}
                onChange={(e) => setAuditEntityFilter(e.target.value)}
                placeholder={t('auditFilterEntityPlaceholder')}
                style={auditCmdInputStyle}
              />
              <input
                value={auditMetaFilter}
                onChange={(e) => setAuditMetaFilter(e.target.value)}
                placeholder={t('auditFilterMetaPlaceholder')}
                style={auditCmdInputStyle}
              />
              <AuditDatetimePicker
                fullWidth={isMobile}
                value={auditFrom}
                onChange={(v) => { setAuditFrom(v); setAuditPresetActive(''); }}
                placeholder={t('auditFilterFromPlaceholder')}
                open={auditFromPickerOpen}
                onOpenChange={(next) => {
                  if (next) {
                    setAuditToPickerOpen(false);
                    setIsAuditRoleOpen(false);
                  }
                  setAuditFromPickerOpen(next);
                }}
              />
              <AuditDatetimePicker
                fullWidth={isMobile}
                value={auditTo}
                onChange={(v) => { setAuditTo(v); setAuditPresetActive(''); }}
                placeholder={t('auditFilterToPlaceholder')}
                open={auditToPickerOpen}
                onOpenChange={(next) => {
                  if (next) {
                    setAuditFromPickerOpen(false);
                    setIsAuditRoleOpen(false);
                  }
                  setAuditToPickerOpen(next);
                }}
              />
              <button style={{ ...auditCmdButtonStyle, ...(auditPresetActive === 'today' ? { border: '1px solid rgba(245,216,138,0.9)', boxShadow: '0 10px 20px rgba(0,0,0,0.35), 0 0 14px rgba(201,168,76,0.4)' } : {}) }} onClick={() => setAuditPreset('today')} onMouseEnter={handleAuditCmdHover} onMouseLeave={handleAuditCmdLeave} onMouseDown={handleAuditCmdDown} onMouseUp={handleAuditCmdUp}>{t('todayText')}</button>
              <button style={{ ...auditCmdButtonStyle, ...(auditPresetActive === '24h' ? { border: '1px solid rgba(245,216,138,0.9)', boxShadow: '0 10px 20px rgba(0,0,0,0.35), 0 0 14px rgba(201,168,76,0.4)' } : {}) }} onClick={() => setAuditPreset('24h')} onMouseEnter={handleAuditCmdHover} onMouseLeave={handleAuditCmdLeave} onMouseDown={handleAuditCmdDown} onMouseUp={handleAuditCmdUp}>{t('audit24h')}</button>
              <button style={{ ...auditCmdButtonStyle, ...(auditPresetActive === '7d' ? { border: '1px solid rgba(245,216,138,0.9)', boxShadow: '0 10px 20px rgba(0,0,0,0.35), 0 0 14px rgba(201,168,76,0.4)' } : {}) }} onClick={() => setAuditPreset('7d')} onMouseEnter={handleAuditCmdHover} onMouseLeave={handleAuditCmdLeave} onMouseDown={handleAuditCmdDown} onMouseUp={handleAuditCmdUp}>{t('audit7d')}</button>
              <button style={{ ...auditCmdButtonStyle, opacity: auditPresetActive ? 1 : 0.85 }} onClick={() => setAuditPreset('')} onMouseEnter={handleAuditCmdHover} onMouseLeave={handleAuditCmdLeave} onMouseDown={handleAuditCmdDown} onMouseUp={handleAuditCmdUp}>{t('resetText')}</button>
              <button style={{ ...auditCmdButtonStyle, color: '#9fe3be', border: '1px solid rgba(76,175,80,0.55)' }} onClick={exportAuditCsv} onMouseEnter={handleAuditCmdHover} onMouseLeave={handleAuditCmdLeave} onMouseDown={handleAuditCmdDown} onMouseUp={handleAuditCmdUp}>{t('exportCsv')}</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ color: '#8896a8', fontSize: '11px' }}>{`${t('auditTimezonePrefix')} ${Intl.DateTimeFormat().resolvedOptions().timeZone} ${t('auditTimezoneSuffix')}`}</span>
              <button style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={clearAllAuditFilters}>{t('clearAll')}</button>
              {auditActionFilter && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('action')}>{t('chipAction')} {'\u00D7'}</button>}
              {auditActorFilter && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('actor')}>{t('chipActor')} {'\u00D7'}</button>}
              {auditRoleFilter && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('role')}>{t('chipRole')} {'\u00D7'}</button>}
              {auditEntityFilter && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('entity')}>{t('chipEntity')} {'\u00D7'}</button>}
              {auditMetaFilter && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('meta')}>{t('chipMeta')} {'\u00D7'}</button>}
              {auditFrom && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('from')} title={auditFrom}>{t('chipFrom')}: {formatAuditDatetimeLabel(auditFrom)} {'\u00D7'}</button>}
              {auditTo && <button style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c', borderRadius: '16px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }} onClick={() => clearAuditField('to')} title={auditTo}>{t('chipTo')}: {formatAuditDatetimeLabel(auditTo)} {'\u00D7'}</button>}
            </div>
            <button
              type="button"
              onClick={() => setAuditExportOpen((v) => !v)}
              style={{ background: 'transparent', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', marginBottom: auditExportOpen ? '8px' : '10px' }}
            >
              {auditExportOpen ? t('auditHideCsvOptions') : t('auditShowCsvOptions')}
            </button>
            {auditExportOpen && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
              {Object.keys(csvColumns).map((key) => (
                <label key={key} style={{ color: '#8896a8', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input
                    type="checkbox"
                    checked={csvColumns[key]}
                    onChange={(e) => setCsvColumns((prev) => ({ ...prev, [key]: e.target.checked }))}
                  />
                  {auditCsvColLabel(key)}
                </label>
              ))}
            </div>
            )}
            <p style={{ color: '#f1d793', margin: '0 0 8px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px' }}>{t('auditEventLog')}</p>
            {visibleAuditLogs.length === 0 ? (
              <p style={{ color: '#8896a8', textAlign: 'center', padding: '20px' }}>{t('auditNoLogs')}</p>
            ) : (
              <div>
                <div
                  style={{
                    maxHeight: '430px',
                    overflowY: 'auto',
                    border: '1px solid rgba(201,168,76,0.24)',
                    borderRadius: '12px',
                    padding: '10px',
                    background: 'linear-gradient(180deg, rgba(9,17,30,0.92), rgba(5,10,18,0.96))',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 16px 30px rgba(0,0,0,0.3)',
                  }}
                >
                  {auditVirtualWindow.items.map((row) => (
                    (() => {
                      const severity = getAuditSeverityMeta(row.action);
                      return (
                    <div
                      key={row.id}
                      style={{
                        position: 'relative',
                        overflow: 'hidden',
                        border: `1px solid ${severity.glow}`,
                        borderLeft: `4px solid ${severity.rail}`,
                        borderRadius: '14px',
                        padding: '12px 13px',
                        marginBottom: '10px',
                        background: 'linear-gradient(155deg, rgba(18,32,50,0.92), rgba(8,15,26,0.98))',
                        boxShadow: `0 10px 24px rgba(0,0,0,0.28), 0 0 22px ${severity.glow}`,
                        transition: 'transform 0.18s ease, filter 0.18s ease, box-shadow 0.18s ease',
                      }}
                      onMouseEnter={handleAuditRowHover}
                      onMouseLeave={handleAuditRowLeave}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '3px',
                          background: `linear-gradient(180deg, ${severity.rail}, transparent 60%, ${severity.rail})`,
                          boxShadow: `0 0 14px ${severity.glow}`,
                          animation: 'auditPulseRail 1.8s ease-in-out infinite',
                        }}
                      />
                      <span
                        aria-hidden
                        style={{
                          position: 'absolute',
                          inset: 0,
                          backgroundImage: 'repeating-linear-gradient(120deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 7px)',
                          opacity: 0.35,
                          pointerEvents: 'none',
                          animation: 'auditNoiseMove 6s linear infinite',
                        }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ color: '#9aa8bb', fontSize: '11px', background: 'rgba(9,17,31,0.65)', border: '1px solid rgba(148,163,184,0.22)', borderRadius: '999px', padding: '2px 8px' }}>
                            {row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-'}
                          </span>
                          <span style={{ ...getActionBadgeStyle(row.action), borderRadius: '999px', padding: '2px 9px', fontSize: '11px', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <span aria-hidden>{getActionSeverityIcon(row.action)}</span>
                            {row.action}
                          </span>
                          <span style={{ color: severity.rail, border: `1px solid ${severity.glow}`, background: 'rgba(6,12,22,0.7)', borderRadius: '999px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
                            {({ Critical: t('auditSeverityCritical'), High: t('auditSeverityHigh'), Medium: t('auditSeverityMedium'), Normal: t('auditSeverityNormal') })[severity.level] || severity.level}
                          </span>
                          <span style={{ ...getRoleBadgeStyle(row.actor_role), borderRadius: '999px', padding: '2px 9px', fontSize: '11px', fontWeight: 600, textTransform: 'lowercase' }}>
                            {row.actor_role || '-'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            style={{ background: 'rgba(109,190,255,0.14)', border: '1px solid rgba(109,190,255,0.4)', color: '#b8e6ff', padding: '4px 10px', borderRadius: '7px', cursor: 'pointer', fontSize: '11px' }}
                            onClick={() => copyAuditEvent(row)}
                          >
                            {t('auditCopyEvent')}
                          </button>
                          <button
                            style={{ background: 'rgba(201,168,76,0.14)', border: '1px solid rgba(201,168,76,0.35)', color: '#f6d98a', padding: '4px 10px', borderRadius: '7px', cursor: 'pointer', fontSize: '11px' }}
                            onClick={() => setExpandedAuditRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                          >
                            {expandedAuditRows[row.id] ? t('auditHideDetails') : t('auditViewDetails')}
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', fontSize: '11px' }}>
                        <span style={{ color: '#a7b4c6', background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.24)', borderRadius: '999px', padding: '2px 8px' }}>{t('auditActor')}: {row.actor_id || '-'}</span>
                        <span style={{ color: '#a7b4c6', background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.24)', borderRadius: '999px', padding: '2px 8px' }}>{t('auditEntity')}: {row.entity_type || '-'}</span>
                        <span style={{ color: '#a7b4c6', background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.24)', borderRadius: '999px', padding: '2px 8px' }}>ID: {row.entity_id || '-'}</span>
                      </div>
                      {expandedAuditRows[row.id] && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                            <button
                              type="button"
                              onClick={() => copyAuditMetadata(row)}
                              style={{ background: 'rgba(201,168,76,0.14)', border: '1px solid rgba(201,168,76,0.35)', color: '#f7df9b', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px' }}
                            >
                              {t('auditCopyJson')}
                            </button>
                          </div>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#d2d8e2', fontSize: '11px', background: 'linear-gradient(170deg, rgba(10,19,31,0.9), rgba(5,10,18,0.95))', border: '1px solid rgba(201,168,76,0.2)', borderRadius: '8px', padding: '10px' }}>
                            {JSON.stringify(row.metadata || {}, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                      );
                    })()
                  ))}
                </div>
                {auditHasMore && <LoadMoreButton onClick={loadMoreAuditLogs} disabled={auditLoadingMore} loading={auditLoadingMore} label={t('loadMoreLogs')} loadingLabel={t('loading')} />}
                <p className={ui.hint}>
                  {`${t('auditFooterLoaded')} ${visibleAuditLogs.length} ${t('auditFooterLogs')}${auditHasMore ? ` ${t('auditFooterMoreAvailable')}` : ` ${t('auditFooterAllLoaded')}`}`}
                </p>
              </div>
            )}
          </div>
        )}

{/* REPORTS TAB */}
        {!loading && activeTab === 'reports' && !isManager && (
          <div>
            <div className={cn(ui.cardRow, 'sm:grid-cols-2')}>
              {[
                { icon: String.fromCodePoint(0x1F4B0), val: formatRsCompact(totalRevenue), label: t('revenueMonth') },
                { icon: String.fromCodePoint(0x1F4B8), val: commission > 0 ? formatRsCompact(commission) : '₹0', label: t('commissionLabel') },
                { icon: String.fromCodePoint(0x1F4CB), val: bookingData.length.toString(), label: t('totalBookings') },
                { icon: String.fromCodePoint(0x1F3D7), val: ownerUsers.length.toString(), label: t('activeOwners') },
              ].map((c, i) => (
                <PremiumStatCard key={i} icon={c.icon} value={c.val} label={c.label} />
              ))}
            </div>
            <div style={{
              border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 24,
              background: 'rgba(201,168,76,0.03)',
            }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
                flexWrap: 'wrap',
                gap: 12,
              }}
              >
                <h3 style={{
                  color: '#C9A84C',
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                }}
                >
                  {t('gstSummaryReport')}
                </h3>
                <input
                  type="month"
                  value={gstMonth}
                  onChange={(e) => setGstMonth(e.target.value)}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 8,
                    color: '#e8e0d0',
                    padding: '6px 12px',
                    fontSize: 13,
                  }}
                />
              </div>

              {gstLoading ? (
                <p style={{ color: '#8896a8' }}>{t('loadingLabel')}</p>
              ) : gstReport ? (
                <div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 12,
                    marginBottom: 16,
                  }}
                  >
                    {[
                      { label: t('totalBookings'), val: gstReport.totals.count },
                      { label: t('taxableAmount'), val: `₹${gstReport.totals.taxable.toLocaleString('en-IN')}` },
                      { label: t('cgst9pct'), val: `₹${gstReport.totals.cgst.toLocaleString('en-IN')}` },
                      { label: t('sgst9pct'), val: `₹${gstReport.totals.sgst.toLocaleString('en-IN')}` },
                      { label: t('totalGst'), val: `₹${gstReport.totals.output_gst.toLocaleString('en-IN')}` },
                      { label: t('grossTotal'), val: `₹${gstReport.totals.gross.toLocaleString('en-IN')}` },
                    ].map((item) => (
                      <div key={item.label} style={{
                        background: 'rgba(255,255,255,0.03)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                      >
                        <p style={{ color: '#8896a8', fontSize: 11, margin: '0 0 4px' }}>
                          {item.label}
                        </p>
                        <p style={{ color: '#e8e0d0', fontWeight: 700, margin: 0, fontSize: 14 }}>
                          {item.val}
                        </p>
                      </div>
                    ))}
                  </div>

                  {gstReport.bookings.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                            {['Booking', 'Date', 'Client', 'GSTIN', 'Taxable', 'CGST 9%', 'SGST 9%', 'Total'].map((h) => (
                              <th key={h} style={{
                                color: '#8896a8',
                                padding: '6px 8px',
                                textAlign: 'left',
                                fontWeight: 600,
                              }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {gstReport.bookings.map((b) => (
                            <tr key={b.booking_ref} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              <td style={{ padding: '6px 8px', color: '#c9a84c' }}>
                                {b.booking_ref}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#8896a8' }}>
                                {new Date(b.completed_at).toLocaleDateString('en-IN')}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#e8e0d0' }}>
                                {b.client_name}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#8896a8' }}>
                                {b.client_gstin}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#e8e0d0' }}>
                                ₹{b.taxable_amount.toLocaleString('en-IN')}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#4CAF50' }}>
                                ₹{b.cgst.toLocaleString('en-IN')}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#4CAF50' }}>
                                ₹{b.sgst.toLocaleString('en-IN')}
                              </td>
                              <td style={{ padding: '6px 8px', color: '#e8e0d0', fontWeight: 700 }}>
                                ₹{b.gross_amount.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ color: '#8896a8' }}>
                  No completed bookings this month.
                </p>
              )}
            </div>
            <TransactionLedger
              embedded
              transactions={transactionData}
              filteredTransactions={filteredTransactionData}
              reconFrom={reconFrom}
              reconTo={reconTo}
              onReconFromChange={setReconFrom}
              onReconToChange={setReconTo}
              channelFilter={txnChannelFilter}
              onChannelFilterChange={setTxnChannelFilter}
              statusFilter={txnStatusFilter}
              onStatusFilterChange={setTxnStatusFilter}
              ledgerClientLookup={ledgerClientLookup}
              ledgerMachineLookup={machineLookup}
              ledgerBookingByRef={ledgerBookingByRef}
              onLast7Days={() => {
                const r = getLastDaysRange(7);
                setReconFrom(r.from);
                setReconTo(r.to);
              }}
              onLast30Days={() => {
                const r = getLastDaysRange(30);
                setReconFrom(r.from);
                setReconTo(r.to);
              }}
              onThisMonth={() => {
                const r = getCurrentMonthRange();
                setReconFrom(r.from);
                setReconTo(r.to);
              }}
              hasMore={transactionsHasMore}
              loadingMore={transactionsLoadingMore}
              onLoadMore={loadMoreTransactions}
              loadMoreLabel={t('loadMoreTransactions')}
              loadingLabel={t('loading')}
              inferTxnStatus={inferTxnStatus}
              inferTxnChannel={inferTxnChannel}
            />

          </div>
        )}

        {!loading && activeTab === 'analytics' && (
          <div className="space-y-8">
            {!isManager && (
              <FillRateDashboard authToken={localStorage.getItem('developmentexpress_token') || ''} />
            )}
            {!isManager && (
              <AdminFleetAnalytics authToken={localStorage.getItem('developmentexpress_token') || ''} />
            )}
            {!isManager && (
              <AdminMachineFinancialPanel authToken={localStorage.getItem('developmentexpress_token') || ''} />
            )}
            {!isManager && (
              <AdminMarketRatePanel authToken={localStorage.getItem('developmentexpress_token') || ''} />
            )}
            {!isManager && (
              <AdminPayrollOverview authToken={localStorage.getItem('developmentexpress_token') || ''} />
            )}
            {!isManager && (
              <AdminNotificationLog authToken={localStorage.getItem('developmentexpress_token') || ''} />
            )}
          </div>
        )}

        {!loading && activeTab === 'surveys' && surveyTabError && (
          <div className="mx-2 mb-3 rounded-xl border border-de-accent/40 bg-de-accent/10 p-4 text-de-accent sm:mx-4">
            {surveyTabError}
          </div>
        )}

        {!loading && activeTab === 'surveys' && (
          <SurveyResponsesTab />
        )}

        {!loading && activeTab === 'surveyReview' && (
          <SurveyReviewTab onPendingCountChange={setPendingSurveyReviews} />
        )}

        {!loading && activeTab === 'surveyPayout' && !isManager && (
          <SurveyPayoutTab />
        )}

        {!loading && activeTab === 'surveyQr' && (
          <SurveyQrGeneratorTab onPendingCountChange={setPendingFreelancers} />
        )}

        {/* IoT TAB */}
        {!loading && activeTab === 'iot' && (
          <div>
            <div className={cn(ui.cardRow, 'sm:grid-cols-2')}>
              {[
                { icon: String.fromCodePoint(0x1F4CD), val: String(iotGpsDevicesActiveCount), labelKey: 'iotGpsDevicesActive' },
                { icon: String.fromCodePoint(0x1F4F9), val: '0', labelKey: 'iotDashcamsOnline' },
                { icon: String.fromCodePoint(0x26FD), val: String(iotFuelSensorsActiveCount), labelKey: 'iotFuelSensorsActive' },
                { icon: String.fromCodePoint(0x1F512), val: '0', labelKey: 'iotMachinesLocked' },
              ].map((c, i) => (
                <PremiumStatCard key={i} icon={c.icon} value={c.val} label={t(c.labelKey)} />
              ))}
            </div>
            <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm font-semibold text-amber-400">
              {t('iotNotConnectedBanner')}
            </div>
            {iotLoading && (
              <p className="mb-3 text-[12px] text-slate-400">Loading IoT devices…</p>
            )}
            <TablePanel
              title={`${String.fromCodePoint(0x1F4E1)} ${t('iotDeviceStatus')}`}
              desktop={
                <Table>
                  <TableHeader>
                    <TableRow>{['iotColMachine', 'iotColGps', 'iotColLocation', 'iotColFuel', 'iotColDashcam', 'iotColIgnition', 'iotColAction'].map((key) => <TableHead key={key} className="text-de-gold/70">{t(key)}</TableHead>)}</TableRow>
                  </TableHeader>
                  <TableBody>
                    {machineData.map((m, i) => {
                      const iotRow = getIotRowForMachine(m);
                      const gps = iotGpsStatusLabel(iotRow?.status);
                      const fuelPct = iotFuelPctForMachine(m);
                      return (
                        <TableRow key={i}>
                          <TableCell><span className={ui.machineTag}>{m.machine_id}</span></TableCell>
                          <TableCell className={gps.className}>{gps.text}</TableCell>
                          <TableCell>{String.fromCodePoint(0x1F4CD)} {m.location}</TableCell>
                          <TableCell className={fuelPct > 30 ? 'text-emerald-400' : 'text-de-accent'}>{fuelDisplay(fuelPct, m.fuel_capacity_litres || 0)}</TableCell>
                          <TableCell className="text-amber-400">{String.fromCodePoint(0x1F4F9)} {t('iotStatusNotConnected')}</TableCell>
                          <TableCell className={m.status === 'Active' ? 'text-emerald-400' : 'text-amber-400'}>{m.status === 'Active' ? `${String.fromCodePoint(0x1F7E2)} ${t('iotStatusOn')}` : `${String.fromCodePoint(0x26AB)} ${t('iotStatusOff')}`}</TableCell>
                          <TableCell>
                            <Button type="button" size="sm" variant="outline" className={ui.btnReject} disabled title={t('iotNotConnectedBanner')}>{String.fromCodePoint(0x1F512)} {t('iotLockBtn')}</Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              }
              mobile={machineData.map((m, i) => {
                const iotRow = getIotRowForMachine(m);
                const gps = iotGpsStatusLabel(iotRow?.status);
                const fuelPct = iotFuelPctForMachine(m);
                return (
                  <Card key={i} className={ui.mobileCard}>
                    <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
                      <CardTitle className="text-sm"><span className={ui.machineTag}>{m.machine_id}</span></CardTitle>
                      <Badge variant={m.status === 'Active' ? 'success' : 'warning'}>{m.status}</Badge>
                    </CardHeader>
                    <CardContent className={ui.mobileGrid2}>
                      <MobileField label={t('iotColGps')} value={gps.text} />
                      <MobileField label={t('iotColLocation')} value={m.location} />
                      <MobileField label={t('iotColFuel')} value={fuelDisplay(fuelPct, m.fuel_capacity_litres || 0)} />
                      <MobileField label={t('iotColDashcam')} value={`${String.fromCodePoint(0x1F4F9)} ${t('iotStatusNotConnected')}`} />
                      <div className="col-span-2 pt-1">
                        <Button type="button" size="sm" variant="outline" className={cn(ui.btnReject, 'w-full')} disabled title={t('iotNotConnectedBanner')}>{String.fromCodePoint(0x1F512)} {t('iotLockBtn')}</Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            />
          </div>
        )}
        {!loading && activeTab === 'grievances' && (
          <div className={ui.tableCard}>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className={cn(ui.tableTitle, 'mb-0')}>Grievances</p>
              <select
                value={grievanceFilter}
                onChange={(e) => setGrievanceFilter(e.target.value)}
                style={{
                  background: 'linear-gradient(135deg, #0f2040, #0a1628)',
                  border: '1px solid rgba(201,168,76,0.3)',
                  borderRadius: '8px',
                  color: '#e8e0d0',
                  fontSize: '13px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box',
                  minHeight: '42px',
                  minWidth: '180px',
                }}
              >
                <option value="" style={{ background: '#0a1628', color: '#e8e0d0' }}>All</option>
                <option value="submitted" style={{ background: '#0a1628', color: '#e8e0d0' }}>Submitted</option>
                <option value="acknowledged" style={{ background: '#0a1628', color: '#e8e0d0' }}>Acknowledged</option>
                <option value="resolved" style={{ background: '#0a1628', color: '#e8e0d0' }}>Resolved</option>
                <option value="escalated" style={{ background: '#0a1628', color: '#e8e0d0' }}>Escalated</option>
              </select>
            </div>
            {grievancesLoading && <p className="m-0 mb-3 text-[12px] text-slate-400">Loading grievances…</p>}
            {!grievancesLoading && grievances.length === 0 && (
              <p className={ui.empty}>No grievances</p>
            )}
            {grievances.length > 0 && (
              <>
                <div className={ui.tableWrap}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {['Name', 'Email', 'Subject', 'Status', 'Created', 'Due Ack', 'Due Resolve', 'Actions'].map((h) => (
                          <TableHead key={h} className="text-de-gold/70">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {grievances.map((g) => {
                        const isAcked = Boolean(g.acknowledged_at);
                        const isResolved = Boolean(g.resolved_at);
                        const goldBtn = {
                          background: 'rgba(201,168,76,0.12)',
                          border: '1px solid rgba(201,168,76,0.5)',
                          borderRadius: '8px',
                          color: '#C9A84C',
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '10px 16px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        };
                        return (
                          <TableRow key={g.id}>
                            <TableCell>{g.name || '—'}</TableCell>
                            <TableCell className="whitespace-normal break-words">{g.email || '—'}</TableCell>
                            <TableCell className="whitespace-normal break-words">{g.subject || '—'}</TableCell>
                            <TableCell>
                              <span className={ui.statusBadge} style={{ border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.12)', color: '#C9A84C' }}>
                                {g.status || '—'}
                              </span>
                            </TableCell>
                            <TableCell>{formatAuditDatetimeLabel(g.created_at) || '—'}</TableCell>
                            <TableCell>{getSLABadge(g.due_ack_by, g.status === 'submitted')}</TableCell>
                            <TableCell>{getSLABadge(g.due_resolve_by, g.status === 'submitted' || g.status === 'acknowledged')}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  disabled={isAcked || grievancesLoading}
                                  onClick={() => handleAckGrievance(g.id)}
                                  style={{ ...goldBtn, opacity: isAcked ? 0.45 : 1, cursor: isAcked ? 'not-allowed' : 'pointer' }}
                                >
                                  Acknowledge
                                </button>
                                <button
                                  type="button"
                                  disabled={isResolved || grievancesLoading}
                                  onClick={() => handleResolveGrievance(g.id)}
                                  style={{ ...goldBtn, opacity: isResolved ? 0.45 : 1, cursor: isResolved ? 'not-allowed' : 'pointer' }}
                                >
                                  Resolve
                                </button>
                                {g.status === 'acknowledged' && (
                                  <button
                                    type="button"
                                    disabled={g.status === 'escalated' || grievancesLoading}
                                    onClick={() => handleEscalateGrievance(g.id)}
                                    style={{
                                      ...goldBtn,
                                      opacity: g.status === 'escalated' ? 0.45 : 1,
                                      cursor: g.status === 'escalated' ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    Escalate
                                  </button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className={ui.tableMobile}>
                  {grievances.map((g) => {
                    const isAcked = Boolean(g.acknowledged_at);
                    const isResolved = Boolean(g.resolved_at);
                    const goldBtn = {
                      background: 'rgba(201,168,76,0.12)',
                      border: '1px solid rgba(201,168,76,0.5)',
                      borderRadius: '8px',
                      color: '#C9A84C',
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    };
                    return (
                      <Card key={g.id} className={ui.mobileCard}>
                        <CardHeader className="flex-row items-start justify-between space-y-0 p-4 pb-2">
                          <CardTitle className="text-sm text-[#C9A84C]">{g.subject || '—'}</CardTitle>
                          <Badge variant="outline">{g.status || '—'}</Badge>
                        </CardHeader>
                        <CardContent className={ui.mobileGrid2}>
                          <MobileField label="Name" value={g.name || '—'} />
                          <MobileField label="Email" value={g.email || '—'} />
                          <MobileField label="Created" value={formatAuditDatetimeLabel(g.created_at) || '—'} />
                          <MobileField label="Due Ack" value={getSLABadge(g.due_ack_by, g.status === 'submitted')} />
                          <MobileField label="Due Resolve" value={getSLABadge(g.due_resolve_by, g.status === 'submitted' || g.status === 'acknowledged')} className="col-span-2" />
                        </CardContent>
                        <div className="flex flex-col gap-2 px-4 pb-4">
                          <button
                            type="button"
                            disabled={isAcked || grievancesLoading}
                            onClick={() => handleAckGrievance(g.id)}
                            style={{ ...goldBtn, opacity: isAcked ? 0.45 : 1, cursor: isAcked ? 'not-allowed' : 'pointer' }}
                          >
                            Acknowledge
                          </button>
                          <button
                            type="button"
                            disabled={isResolved || grievancesLoading}
                            onClick={() => handleResolveGrievance(g.id)}
                            style={{ ...goldBtn, opacity: isResolved ? 0.45 : 1, cursor: isResolved ? 'not-allowed' : 'pointer' }}
                          >
                            Resolve
                          </button>
                          {g.status === 'acknowledged' && (
                            <button
                              type="button"
                              disabled={g.status === 'escalated' || grievancesLoading}
                              onClick={() => handleEscalateGrievance(g.id)}
                              style={{
                                ...goldBtn,
                                opacity: g.status === 'escalated' ? 0.45 : 1,
                                cursor: g.status === 'escalated' ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Escalate
                            </button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
    </AdminCommandShell>
  );
};

export default AdminDashboard;
